// src/data/pianiDiZona.ts
// Piano di Zona 2025–2027 - Ambito di Vimercate

export type TipoObiettivo = 'promozionale' | 'preventivo' | 'riparativo' | 'misto';
export type StatoServizio = 'nuovo' | 'continuita' | 'potenziamento' | 'rivisto';
export const CLUSTER_TARGET_VALUES = ['Anziani', 'Minori', 'Adulti'] as const;
export type ClusterTarget = typeof CLUSTER_TARGET_VALUES[number];
export type ObiettivoCluster = 'Inclusione' | 'Sistema' | 'Benessere sociale' | 'Interambito';

export interface SchedaObiettivo {
  id: string;
  titolo: string;
  obiettivo_cluster: ObiettivoCluster;
  sottotitolo?: string;
  obiettivo: string;
  target: string;
  target_cluster: ClusterTarget[];
  focus_disabilita: boolean;
  azioni: string[];
  risorse: string[];
  partners?: string[];
  obiettiviDaRaggiungere?: string[];
  bisogniPdf?: string[];
  inputPdf?: string[];
  modalitaOperativePdf?: string[];
  contenutoIntegralePdf?: string;
  cooperazioneAltriAmbiti?: string;
  continuita2021?: boolean;
  nuovoServizio?: boolean;
  statoServizio?: StatoServizio;
  coinvolgimentoASST: boolean;
  indicatoriOutput?: string[];
  indicatoriOutcome?: string[];
  areePolicy?: string[];
}

export interface GruppoPianoZona {
  id: string;
  titolo: string;
  colore: string;
  icona: string;
  schede: SchedaObiettivo[];
}

export interface PianoZona {
  ambito: string;
  triennio: string;
  descrizione?: string;
  ultimoAggiornamento: string;
  gruppi: GruppoPianoZona[];
}

// ─── DATI VIMERCATE ────────────────────────────────────────────────────────────

export const pianoZonaVimercate: PianoZona = {
  ambito: 'Vimercate',
  triennio: '2025–2027',
  descrizione: 'Piano di Zona dell\'Ambito Territoriale Sociale di Vimercate / Offertasociale',
  ultimoAggiornamento: '2024-12',
  gruppi: [
    // ─── 6.1 NON AUTOSUFFICIENZA ─────────────────────────────────────────────
    {
      id: 'non-autosufficienza',
      titolo: 'Non Autosufficienza',
      colore: '#f59e0b',
      icona: '🏥',
      schede: [
        {
          id: 'invecchiamento-attivo',
          titolo: 'Invecchiamento Attivo',
          obiettivo_cluster: 'Inclusione',
          sottotitolo: 'Progetto "Generazione Senior"',
          obiettivo:
            'Creare un sistema integrato di interventi per l\'invecchiamento attivo con il coinvolgimento d i tutti gli stakeholder territoriali (ambiti, ASST, ATS, ETS) per valorizzare il ruolo degli anziani, contrastare l\'isolamento sociale e promuovere la salute.',
          target: 'Popolazione over 65, in particolare anziani autosufficienti, vulnerabili o a rischio di vulnerabilità (isolamento, emarginazione sociale, transizione dalla vita attiva alla fragilità).',
          target_cluster: ['Anziani'],
          focus_disabilita: false,
          azioni: [
            'Creazione di una vetrina di luoghi e iniziative di socialità per over 65 sulla piattaforma Isidora',
            'Redazione di un catalogo di proposte nelle aree: movimento consapevole, area cognitiva, area espressiva, area salute',
            'Azioni di contrasto alla solitudine e servizi domiciliari; alternanza scuola-volontariato (PCTO)',
            'Promozione del progetto a scala provinciale',
            'Governance attraverso cabina di regia allargata, tavolo operativo e tavolo di ambito',
          ],
          risorse: [
            'Progetto Generazione Senior (coprogettazione ATS Brianza)',
            'Cofinanziamento ETS',
            'Fondo Nazionale Politiche Sociali',
          ],
          partners: [
            'CSV Monza-Lecco-Sondrio (capofila)',
            'Sociosfera Onlus',
            'Cooperativa Aeris (coordinamento vimercatese)',
            'Cooperativa La Meridiana',
            'Spazio Giovani Impresa Sociale',
            'Azienda Speciale Consortile Codebri',
            'AUSER e ANTEAS (reti provinciali)',
            'Le Comunità della Salute',
            'ATS Brianza',
            'ASST Brianza',
          ],
          cooperazioneAltriAmbiti: 'Ambiti di Carate Brianza, Desio e Seregno (sostenitori)',
          continuita2021: true,
          nuovoServizio: false,
          coinvolgimentoASST: true,
          areePolicy: [
            'A) Contrasto povertà e emarginazione sociale',
            'D) Domiciliarità',
            'E) Anziani',
            'F) Digitalizzazione dei servizi',
          ],
          indicatoriOutput: [
            'Aumento del 50% dei partecipanti alla rete di sostegno',
            'Aumento del 10% dei luoghi presidiati',
            'Almeno 40 colloqui di orientamento al volontariato (CSV MLS)',
            'Almeno 200 ore di attività nel catalogo',
            'Almeno 5.000 anziani coinvolti nelle attività',
            'Almeno il 5% della popolazione anziana MB raggiunta dalla promozione',
            'Almeno 2 eventi sul territorio',
          ],
          indicatoriOutcome: [
            'Aumento della conoscenza dei luoghi e iniziative di socializzazione over 65',
            'Aumento della partecipazione agli eventi',
            'Maggior coinvolgimento in attività di volontariato (empowerment over 65)',
            'Miglioramento degli stili di vita sani',
          ],
        },

        {
          id: 'welfare-prossimita',
          titolo: 'Welfare di Prossimità',
          obiettivo_cluster: 'Inclusione',
          sottotitolo: 'Punto Unico di Accesso (PUA) – Dimissioni Protette',
          obiettivo:
            'Potenziare il sistema integrato di interventi e servizi territoriali per la salute, favorendo la domiciliarità delle cure, riducendo le ospedalizzazioni improprie, e realizzando il LEPS Dimissioni Protette e il Punto Unico di Accesso.',
          target: 'Persone non autosufficienti, con limitazioni funzionali, disabilità, vulnerabilità sociale o necessità assistenziali.',
          target_cluster: ['Anziani'],
          focus_disabilita: true,
          azioni: [
            'Potenziamento del segretariato sociale e del PUA (formazione, materiale informativo)',
            'Realizzazione del progetto "badante di quartiere"',
            'Coprogettazione "reti di prossimità" con ETS e ASST',
            'Revisione linee guida ATS Brianza su dimissioni/ammissioni protette',
            'Protocolli con ASST per gestione integrata PUA e servizi domiciliari',
            'Gruppo di lavoro PNRR 1.1.3 con Ufficio di Piano, comuni e ASST',
            'Formazione operatori in tema sociosanitario',
            'Sportello SI Informatico presso Casa di Comunità di Vimercate',
          ],
          risorse: [
            'PNRR M5C2 1.1.3 Ambito Vimercate',
            'PNRR 1.3.1 Housing First (Vimercate e Trezzo sull\'Adda)',
            'FNA 2023-2024',
            'Fondo Nazionale Politiche Sociali 2022-2023',
          ],
          partners: [
            'Ufficio di Piano',
            'Assistente sociale di ambito (nuova assunzione)',
            'Operatore di rete di ambito (nuova assunzione)',
            'Responsabili servizi sociali comunali',
            'Medici di medicina generale',
            'ATS Brianza e ASST Brianza',
            'Tecnici Melc (Mediazione lavori di cura)',
            'ETS',
          ],
          continuita2021: true,
          nuovoServizio: true,
          statoServizio: 'nuovo',
          coinvolgimentoASST: true,
          areePolicy: [
            'A) Contrasto povertà e emarginazione',
            'B) Politiche abitative',
            'D) Domiciliarità',
            'E) Anziani',
            'F) Digitalizzazione dei servizi',
            'J) Disabilità',
          ],
          indicatoriOutput: [
            '125 persone assistite con servizi domiciliari post-dimissioni (target PNRR 1.1.3, entro 31/3/2026)',
            'Stipula protocolli dimissioni protette (inclusi senza dimora) tra ambito e ASST',
            'Assunzione 1 assistente sociale (t. pieno, indeterminato)',
            'Assunzione 1 operatore di rete (sperimentale)',
            'Produzione di almeno 10 schede informative per PUA',
            'Definizione modello PAI integrato ASST-ambito',
          ],
          indicatoriOutcome: [
            'Aumento della percezione di supporto da parte dei servizi sociali',
            'Riduzione dei ricoveri (campione 20% assistiti)',
            'Maggiore integrazione sociosanitaria percepita dagli operatori',
            'Almeno 4 ETS coinvolti nell\'erogazione dei servizi domiciliari',
          ],
        },

        {
          id: 'vita-autonoma-indipendente',
          titolo: 'Vita Autonoma Indipendente',
          obiettivo_cluster: 'Inclusione',
          obiettivo:
            'Favorire percorsi di autonomia per persone con disabilità attraverso la creazione di una rete di appartamenti, un centro di vita indipendente e interventi di inclusione sociale.',
          target: 'Persone con disabilità e loro famiglie.',
          target_cluster: ['Adulti'],
          focus_disabilita: true,
          azioni: [
            'Realizzazione di 1 Centro di Vita Indipendente (CVI) in accordo con ambiti, ETS e associazioni',
            'Potenziamento dell\'equipe di valutazione multidimensionale qualificata',
            'Riqualificazione di un laboratorio informatico per inclusione socio-relazionale',
            'Redazione di linee guida per la vita indipendente condivise con ETS e famiglie',
            'Implementazione rete di appartamenti per vita indipendente (DDN, PRO.Vi, PNRR 1.2)',
            'Attività di sensibilizzazione sulle tematiche della disabilità e formazione caregiver',
          ],
          risorse: [
            'PNRR M5C2 1.2 Percorsi di autonomia per persone con disabilità',
            'Fondo regionale per centri di vita indipendente',
            'Fondo Non Autosufficienza',
            'Fondi locali',
          ],
          partners: [
            'Ufficio di Piano',
            'Ambito di Monza (co-realizzazione CVI)',
            'Coordinatore CVI',
            'Responsabili servizi sociali comunali',
            'ATS Brianza, ASST Brianza',
            'ETS',
            'Associazioni area disabilità',
          ],
          cooperazioneAltriAmbiti: 'Ambito Territoriale Sociale di Monza (CVI)',
          continuita2021: false,
          nuovoServizio: true,
          coinvolgimentoASST: true,
          areePolicy: [
            'J) Interventi a favore di persone con disabilità',
            'D) Domiciliarità',
            'A) Contrasto povertà e inclusione attiva',
            'H) Politiche del lavoro',
            'K) Potenziamento Ufficio di Piano',
          ],
          indicatoriOutput: [
            'Realizzazione linee guida vita autonoma per persone con disabilità',
            'Almeno 40 accessi annui al CVI',
            'Redazione di almeno 10 progetti di vita',
            'Avviamento di almeno 12 percorsi di vita indipendente',
            'Almeno 4 eventi di sensibilizzazione (canali convenzionali e digitali)',
            'Almeno 3 appartamenti per vita indipendente sul territorio',
            'Laboratorio informatico per formazione e tirocini inclusivi',
            'Almeno 3 corsi di formazione digitale per persone con disabilità',
          ],
          indicatoriOutcome: [
            'Rafforzamento delle buone prassi di presa in carico',
            'Aumento utilizzo risorse pubbliche (DDN, PRO.Vi)',
            'Riduzione residui finanziari misura Dopo di Noi',
            'Aumento strutture autorizzate DDN e fornitori accreditati PRO.Vi',
          ],
        },
      ],
    },

    // ─── 6.2 INCLUSIONE ──────────────────────────────────────────────────────
    {
      id: 'inclusione',
      titolo: 'Inclusione',
      colore: '#10b981',
      icona: '🏠',
      schede: [
        {
          id: 'housing-temporaneo',
          titolo: 'Housing Temporaneo',
          obiettivo_cluster: 'Inclusione',
          obiettivo:
            'Sistematizzare la filiera di housing temporaneo sul territorio per garantire risposte concrete alle situazioni di vulnerabilità abitativa prima che sfocino nella marginalità.',
          target: 'Singoli o nuclei familiari in situazione di vulnerabilità abitativa residenti nell\'ambito.',
          target_cluster: ['Adulti'],
          focus_disabilita: false,
          azioni: [
            'Sistema di accoglienza residenziale con supporto educativo verso l\'autonomia abitativa, economica e sociale',
            'Coprogettazione con ETS per housing sociale differenziato',
            'Approfondimento analisi del bisogno per profilare meglio gli utenti in carico',
            'Consolidamento del sistema abitare (Agenzia Sociale per l\'Affitto, Stazione di posta) in continuità con PNRR',
            'Consolidamento équipe di valutazione multidisciplinare a geometria variabile',
            'Messa in rete del terzo settore dell\'abitare con altre realtà territoriali',
            'Creazione e aggiornamento banca dati posti disponibili di accoglienza abitativa',
          ],
          risorse: [
            'Fondo Nazionale Politiche Sociali',
            'Fondo Povertà',
            'PNRR 1.3.1 Housing First (Vimercate e Trezzo sull\'Adda)',
          ],
          partners: [
            'Ufficio di Piano (coordinamento)',
            'Servizi sociali comunali (case manager)',
            'Operatori della sanità (MMG, Casa di Comunità)',
          ],
          cooperazioneAltriAmbiti: 'Ambito di Trezzo sull\'Adda',
          continuita2021: true,
          nuovoServizio: false,
          statoServizio: 'continuita',
          coinvolgimentoASST: false,
          areePolicy: [
            'A) Contrasto povertà e inclusione attiva',
            'B) Politiche abitative',
            'H) Politiche del lavoro',
          ],
          indicatoriOutput: [
            'Aumento del numero di alloggi per accoglienza residenziale',
            'Numero di segnalazioni con focus sulla vulnerabilità (multi-dimensionale)',
            'Numero di progetti di autonomia abitativa avviati',
            'Numero di progetti di autonomia abitativa conclusi positivamente',
          ],
          indicatoriOutcome: [
            'Potenziamento delle reti di housing temporaneo',
            'Diversificazione delle professionalità in base ai bisogni',
            'Maggiore competenza degli operatori sui processi di inclusione sociale',
          ],
        },
        {
          id: 'vulnerabilita-sociale',
          titolo: 'Intercettare e Lavorare con la Vulnerabilità Sociale',
          obiettivo_cluster: 'Inclusione',
          obiettivo:
            'Creare e sistematizzare un sistema per intercettare e accompagnare persone e nuclei familiari in situazione di vulnerabilità (abitativa, lavorativa, economica, familiare, digitale, sanitaria, relazionale) al fine di prevenire situazioni di grave marginalità.',
          target: 'Persone singole o nuclei familiari in situazione di vulnerabilità, a prescindere dall\'ISEE, residenti nell\'ambito. Include anche il "ceto medio impoverito" non coperto dalle misure standard.',
          target_cluster: ['Adulti'],
          focus_disabilita: false,
          azioni: [
            'Mappatura dei "luoghi" che intercettano la vulnerabilità e delle professionalità coinvolte',
            'Istituzione cabina di regia sul tema vulnerabilità e inclusione sociale',
            'Équipe di valutazione multidisciplinare a geometria variabile (figure sociali, educative, psicologiche, finanziarie, SIL)',
            'Attività di formazione, informazione e prevenzione al territorio (incluse scuole primarie)',
            'Sistematizzazione delle risposte: educazione finanziaria, supporto educativo/psicologico/sanitario/familiare',
            'Percorsi di integrazione lavorativa e misura ADI',
            'Riattivazione Progetti Utili alla Collettività (PUC)',
            'Nuovo sito d\'ambito per cittadini e operatori dei comuni',
          ],
          risorse: [
            'Fondo Nazionale Politiche Sociali',
            'Fondo Povertà',
            'Fondi di ambito',
          ],
          partners: [
            'Ufficio di Piano (coordinamento)',
            'Servizi sociali dei comuni',
            'Tecnici misura ADI',
            'ETS',
            'Operatori della sanità (MMG, Casa di Comunità)',
            'CFP e Scuole',
            'Delegati sindacali',
            'Volontari',
          ],
          cooperazioneAltriAmbiti: 'Ambito di Trezzo sull\'Adda',
          continuita2021: false,
          nuovoServizio: false,
          statoServizio: 'rivisto',
          coinvolgimentoASST: true,
          areePolicy: [
            'A) Contrasto povertà e inclusione attiva',
            'B) Politiche abitative',
            'H) Politiche del lavoro',
            'K) Potenziamento Ufficio di Piano',
            'I) Politiche giovanili e minori',
            'J) Disabilità',
          ],
          indicatoriOutput: [
            'Numero segnalazioni con focus sulla vulnerabilità (multi-dimensionale)',
            'Numero équipe di valutazione multidisciplinare attivate',
            'Numero équipe integrate con figure sanitarie',
            'Numero progetti di fuoriuscita dalla vulnerabilità avviati e conclusi positivamente',
            'Numero accordi formali con luoghi che intercettano la vulnerabilità',
            'Protocolli con Centri per l\'Impiego e SIL per percettori ADI',
            'Aumento postazioni PUC sul territorio',
            'Il risultato si considera raggiunto con l\'80% dei soggetti intercettati',
          ],
          indicatoriOutcome: [
            'Maggiore consapevolezza della cittadinanza e degli operatori sulla vulnerabilità',
            'Potenziamento della rete territoriale che intercetta la vulnerabilità',
            'Intervento precoce favorendo atteggiamento proattivo individuale e comunitario',
          ],
        },
      ],
    },

    // ─── 6.3 GIOVANI, MINORI, FAMIGLIE ───────────────────────────────────────
    {
      id: 'giovani-minori-famiglie',
      titolo: 'Giovani, Minori, Famiglie',
      colore: '#6366f1',
      icona: '👨‍👩‍👧‍👦',
      schede: [
        {
          id: 'disagio-giovanile',
          titolo: 'Contrastare il Disagio Giovanile',
          obiettivo_cluster: 'Benessere sociale',
          obiettivo:
            'Garantire a minori e famiglie vulnerabili un intervento integrato e personalizzato che lavori sulle competenze e risorse, con una logica di processo (non di progetto singolo), per avviare percorsi di sostegno stabili nel tempo. Valorizzare il terzo settore come mediatore tra famiglie e servizi sociali. Proseguire il percorso verso l\'integrazione sociosanitaria, integrando il lavoro in corso con nuove progettualità quali Centro Famiglia e PIPPI.',
          target: 'Minori di età compresa tra i 4-18 anni e le loro famiglie, con particolare attenzione a coloro che vivono esperienze di negligenza, incuria, ritiro sociale.',
          target_cluster: ['Minori'],
          focus_disabilita: false,
          azioni: [
            'Azione 1 – Sviluppare modalità di lavoro che favoriscano la collaborazione con cittadini e amministrazioni: sensibilizzazione sul ruolo del servizio sociale, processi metodologici condivisi tra servizi/scuole/ETS, strategie innovative di prevenzione al disagio giovanile e scolastico',
            'Azione 2 – Implementazione del programma PIPPI: strutturazione equipe multidisciplinare, formazione operatori e ETS, creazione rete di vicinato solidale, diffusione metodologie PIPPI',
            'Azione 3 – Contrasto alla dispersione scolastica: tavolo permanente con servizi, scuola, ASST, psicopedagogia, ETS e famiglie per affrontare vulnerabilità, dispersione, Decreto Caivano e giovani NEET',
          ],
          risorse: [
            'Fondo Nazionale Politiche Sociali',
            'PNRR',
          ],
          partners: [
            'Tecnici Ufficio di Piano',
            'Tecnici dei servizi sociali e dell\'istruzione dei comuni',
            'Tecnici di Offertasociale',
            'Tecnici degli istituti scolastici',
            'Tecnici del tavolo degli psicopedagogisti',
            'Tecnici ASST',
            'Tecnici Enti del Terzo Settore',
            'Consulente formazione/corsi',
            'Educatori e insegnanti degli istituti comprensivi del territorio',
            'Arteterapisti e mediatori familiari',
          ],
          cooperazioneAltriAmbiti: 'Ambito di Trezzo sull\'Adda',
          continuita2021: true,
          nuovoServizio: false,
          statoServizio: 'rivisto',
          coinvolgimentoASST: true,
          areePolicy: [
            'G) Politiche giovanili e per i minori',
            'I) Interventi per la famiglia',
          ],
          indicatoriOutput: [
            'Numero di segnalazioni relative ad interventi di prevenzione',
            'Numero di segnalazioni precoci di giovani in situazione di dispersione scolastica',
            'Numero equipe di valutazione multidisciplinare attivate',
            'Numero equipe di valutazione multidisciplinare integrate con figure sanitarie',
            'Numero di progetti attivati (PIPPI, fuoriclasse ecc.)',
            'Aumento del numero di attori che intercettano il disagio giovanile/familiare',
            'Numero di protocolli con attori territoriali per l\'integrazione tra servizi',
            'Numero di interventi educativi e formativi di gruppo',
            'Numero di interventi psicologici attivati',
            'Il risultato si considera raggiunto con l\'80% dei soggetti intercettati',
          ],
          indicatoriOutcome: [
            'Potenziamento delle reti territoriali che si occupano di minori',
            'Diversificazione delle professionalità coinvolte in base ai bisogni rilevati',
            'Maggiore consapevolezza e competenza circa le vulnerabilità giovanili da parte degli operatori',
            'Aumento del numero di segnalazioni dalla scuola/rete delle psicopedagogiste in ottica preventiva',
            'Diffusione del programma PIPPI come modalità operativa comune',
            'Aumento del numero delle famiglie che partecipano a programmi preventivi',
          ],
        },

        {
          id: 'presa-in-carico-integrata',
          titolo: 'Sviluppare Processi di Presa in Carico Integrata',
          obiettivo_cluster: 'Benessere sociale',
          obiettivo:
            'Offrire agli operatori dei servizi territoriali la possibilità di contare su un gruppo di valutazione multidisciplinare interno all\'Ufficio di Piano per una lettura condivisa delle situazioni complesse di minori e famiglie, con interventi multifattoriali e multidisciplinari. Predisporre un progetto di intervento personalizzato che integri risorse aziendali e territoriali, con un unico case manager come riferimento, superando la logica della frammentarietà degli interventi.',
          target: 'Operatori dei servizi sociali del territorio e/o servizi del terzo settore e/o tutele che gestiscono situazioni complesse e necessitano di interconnessione di più progettualità, servizi e programmi in atto presso l\'Ufficio di Piano.',
          target_cluster: ['Adulti', 'Minori'],
          focus_disabilita: true,
          azioni: [
            'Azione 1 – Costituzione di un gruppo di valutazione multidisciplinare per l\'area Minori e Famiglia: individuazione figure tecniche, costituzione gruppo, stesura procedure, individuazione modalità di segnalazione, attivazione processo sperimentale',
            'Azione 2 – Sensibilizzazione nei servizi sociali e sul territorio: presentazione del nuovo processo di lavoro, organizzazione momenti di supervisione dedicati',
            'Azione 3 – Conoscenza del territorio in materia di minori: mappatura progetti Offertasociale, mappatura progetti territoriali, aggiornamento mappatura associazioni locali, interconnessioni con piattaforme già in uso',
            'Azione 4 – Realizzazione di prassi operative comuni per la presa in carico delle situazioni complesse: definizione buone prassi, individuazione e sperimentazione nuove prassi per famiglie multiproblematiche',
          ],
          risorse: [
            'Fondo Nazionale Politiche Sociali',
            'PNRR',
          ],
          partners: [
            'Tecnici Ufficio di Piano',
            'Tecnici dei servizi sociali e dell\'istruzione dei comuni',
            'Tecnici del tavolo degli psicopedagogisti',
            'Tecnici Enti del Terzo Settore',
            'Tecnici degli istituti scolastici',
            'Tecnici ANCI',
            'Tecnici Regione Lombardia',
            'Tecnici altri ambiti',
            'Offertascolastica',
            'Consulente formazione / corsi',
            'Educatori e insegnanti della scuola',
          ],
          continuita2021: true,
          nuovoServizio: false,
          statoServizio: 'rivisto',
          coinvolgimentoASST: true,
          areePolicy: [
            'G) Politiche giovanili e per i minori',
            'I) Interventi per la famiglia',
          ],
          indicatoriOutput: [
            'Numero di segnalazioni al gruppo di valutazione multidisciplinare',
            'Numero di progetti attivati',
            'Numero di gruppi di valutazione multidisciplinari integrati con figure sanitarie',
            'Diversificazione delle professionalità coinvolte',
            'Numero di accordi o prassi costruite con i luoghi che intercettano il disagio giovanile',
            'Numero di protocolli con attori territoriali per l\'integrazione tra servizi di disagio minorile e familiare',
            'Numero di interventi educativi e formativi di gruppo',
            'Numero di interventi psicologici attivati',
            'Il risultato si considera raggiunto con l\'80% dei soggetti intercettati',
          ],
          indicatoriOutcome: [
            'Potenziamento dei progetti attivati',
            'Diversificazione delle professionalità coinvolte in base ai bisogni rilevati',
            'Maggiore consapevolezza e competenza circa le vulnerabilità giovanili da parte degli operatori',
            'Migliore coordinamento delle risorse territoriali e aziendali',
            'Migliore raccordo tra servizi e territorio',
            'Aumento del numero di segnalazioni dalla scuola/rete delle psicopedagogiste in ottica preventiva',
            'Numero di progetti personalizzati avviati',
          ],
        },
      ],
    },

    // ─── 6.5 OBIETTIVI DI SISTEMA ────────────────────────────────────────────
    {
      id: 'obiettivi-sistema',
      titolo: 'Sistema',
      colore: '#06b6d4',
      icona: '⚙️',
      schede: [
        {
          id: 'rafforzamento-ufficio-piano',
          titolo: 'Azione di Sistema: Rafforzamento Attività Ufficio di Piano',
          obiettivo_cluster: 'Sistema',
          obiettivo:
            'Sviluppare un approccio metodologico integrato al sistema dei servizi con finalità multifattoriali e multiprofessionali. Garantire una lettura territoriale costante e analisi del bisogno per orientare le politiche future. Costituire equipe di valutazione multidisciplinari per rispondere efficacemente sia alla dimensione micro (singola situazione) che macro (bisogno del territorio). Sviluppare forme di comunicazione e rilettura dei fenomeni costanti ed efficaci.',
          target: 'Operatori dei servizi territoriali, staff Ufficio di Piano dei servizi di Offertasociale, coordinatori commissioni tecniche, coordinatori di progetto, responsabili dei comuni.',
          target_cluster: ['Adulti', 'Anziani', 'Minori'],
          focus_disabilita: false,
          azioni: [
            'Azione 1 – Circolarità delle informazioni: costituzione di fonti informative attraverso il sito per tecnici dei servizi (leggi, strumenti metodologici, analisi del bisogno), amministratori (iniziative, calendario politico) e famiglie/cittadini (pratiche, servizi, sensibilizzazione)',
            'Azione 2 – Capacity building: pianificazione strategica con Ufficio di Piano, coordinamento commissioni, equipe di valutazione multidisciplinare, intercettazione bandi, condivisione metodologia, formazione/supervisione LEPS, potenziamento offerta, digitalizzazione (cartella sociale informatizzata)',
            'Azione 3 – Attività di supervisione e formazione degli operatori del territorio: formazione agli attori sociali, supervisione a livello micro e macro sistema',
            'Azione 4 – Equipe di valutazione multidisciplinare: costituzione equipe per le diverse aree interne all\'Ufficio di Piano, creazione modalità di funzionamento, costruzione strumenti e procedure di raccordo con i servizi sociali territoriali',
          ],
          risorse: [
            'Fondo Nazionale Politiche Sociali',
            'Fondo Povertà',
            'Fondi derivanti da singole progettualità',
          ],
          partners: [
            'Ufficio di Piano con funzioni di coordinamento e raccordo',
            'Servizi sociali dei comuni',
            'Enti del Terzo Settore',
            'Enti/istituti (CFP, scuole, …)',
            'Consulente formazione/eventi di sensibilizzazione',
          ],
          cooperazioneAltriAmbiti: 'Ambito di Trezzo sull\'Adda (medesime funzioni)',
          continuita2021: false,
          nuovoServizio: false,
          statoServizio: 'rivisto',
          coinvolgimentoASST: true,
          areePolicy: [
            'K) Interventi di sistema per il potenziamento dell\'Ufficio di Piano e della gestione associata',
          ],
          indicatoriOutput: [
            'Numero di incontri effettuati con il territorio (servizi sociali, amministratori, responsabili)',
            'Numero di protocolli, linee guida, strumentazione condivisa con la rete dei servizi',
            'Numero delle equipe di valutazione multidimensionali costituite per area presso Ufficio di Piano',
            'Numero di progetti realizzati e condivisi con il territorio',
            'Aumento del numero di attori che costituiscono la rete dei servizi e condividono le azioni di sistema',
            'Numero di accordi formali o di prassi costruite',
            'Numero di protocolli sottoscritti con attori territoriali per l\'integrazione tra servizi',
            'Il risultato si considera raggiunto con l\'80% degli operatori/amministratori',
          ],
          indicatoriOutcome: [
            'Aumentata consapevolezza e competenza degli attori del territorio nella costruzione di processi di empowerment e capacity building',
            'Potenziamento della rete sul territorio',
            'Procedure comunicative in uso agli operatori e alla cittadinanza efficaci e rispondenti al bisogno',
            'Maggiore consapevolezza della complessità del sistema di welfare e della necessità di pratiche innovative',
          ],
        },

        {
          id: 'sviluppo-pnrr',
          titolo: 'Sviluppo Progetti PNRR – Tavoli di Lavoro',
          obiettivo_cluster: 'Sistema',
          obiettivo:
            'Sostenere lo sviluppo e la sostenibilità dei 5 progetti PNRR approvati per l\'Ambito di Vimercate (avviati tra 2022-2023, con termine al 31 marzo 2026), garantendo una governance partecipativa e risposte strutturate ai bisogni di inclusione sociale, domiciliarità, disabilità, housing e genitorialità. Promuovere la transizione ecologica e digitale e una maggiore inclusione sociale.',
          target: 'Cittadini e cittadine dell\'Ambito di Vimercate che vivono condizioni di fragilità temporanea o definitiva, relativamente a: genitorialità, dimissioni protette, vulnerabilità e inclusione sociale, senza dimora, fragilità per condizioni economiche e abitative.',
          target_cluster: ['Adulti', 'Minori', 'Anziani'],
          focus_disabilita: false,
          azioni: [
            'Progetto PNRR 1.1.1 – "Sostegno alle capacità genitoriali e prevenzione della vulnerabilità delle famiglie e dei bambini"',
            'Progetto PNRR 1.1.3 – "Rafforzamento dei servizi sociali a favore della domiciliarità"',
            'Progetto PNRR 1.2 – "Percorsi di autonomia per persone con disabilità"',
            'Progetto PNRR 1.3.1 – "Housing Temporaneo"',
            'Progetto PNRR 1.3.2 – "Stazione di Posta"',
            'Azione 1 – Procedure e metodi di lavoro: costituire tavoli con componente politica e tecnica, qualificare procedure di lavoro sociale (equipe multidisciplinare, valutazione multidimensionale, progetto di vita, coprogettazioni)',
            'Azione 2 – Risposte alla cittadinanza: Vita Autonoma, Abitazione per persone in grave marginalità, Lavoro (formazione e tirocinio), Genitorialità (prevenzione e sviluppo capacità genitoriali)',
          ],
          risorse: [
            'PNRR',
            'Fondo Nazionale Politiche Sociali',
            'Fondo Povertà',
            'Fondi derivanti da singole progettualità',
          ],
          partners: [
            'Ufficio di Piano con funzioni di coordinamento e raccordo',
            'Servizi sociali dei comuni',
            'Enti di Terzo Settore',
            'Enti/istituti vari',
            'Consulenti e collaboratori individuati ad hoc per il PNRR',
          ],
          cooperazioneAltriAmbiti: 'Ambito di Trezzo sull\'Adda (medesime funzioni)',
          continuita2021: false,
          nuovoServizio: false,
          statoServizio: 'rivisto',
          coinvolgimentoASST: true,
          areePolicy: [
            'K) Interventi di sistema per il potenziamento dell\'Ufficio di Piano e della gestione associata',
          ],
          indicatoriOutput: [
            'Numero di incontri effettuati con il territorio (servizi sociali, amministratori, responsabili)',
            'Numero dei tavoli costituiti per il PNRR',
            'Numero degli incontri dei tavoli del PNRR',
            'Numero di protocolli, linee guida, strumentazione condivisa con la rete dei servizi',
            'Numero di progetti realizzati e condivisi con il territorio',
            'Aumento del numero di attori che costituiscono la rete dei servizi e condividono le azioni di sistema',
            'Numero di accordi formali o di prassi costruite',
            'Numero di protocolli sottoscritti con attori territoriali per l\'integrazione tra servizi PNRR e altri progetti',
            'Il risultato si considera raggiunto con l\'80% degli operatori/amministratori',
          ],
          indicatoriOutcome: [
            'Aumentata consapevolezza e competenza degli attori del territorio nella costruzione di processi di empowerment e capacity building, anche in previsione del termine dei progetti PNRR',
            'Potenziamento della rete sul territorio e delle collaborazioni con il terzo settore per la gestione dei servizi realizzati con il PNRR',
            'Aumento dei cittadini che beneficeranno di nuovi servizi in risposta al loro bisogno specifico, in particolare la grave marginalità e l\'abitare per soggetti con disabilità in progetti di vita autonoma indipendente',
          ],
        },
      ],
    },

    // ─── 6.6 OBIETTIVI INTERAMBITO ───────────────────────────────────────────
    {
      id: 'obiettivi-interambito',
      titolo: 'Interambito',
      colore: '#8b5cf6',
      icona: '🤝',
      schede: [
        {
          id: 'rete-matrioska',
          titolo: 'Consolidamento della Rete Matrioska',
          obiettivo_cluster: 'Interambito',
          sottotitolo: 'Governance, rete di lavoro e qualificazione dei servizi',
          obiettivo:
            'Consolidare la governance esterna e interna della Rete Matrioska, potenziare le funzioni degli sportelli e promuovere iniziative di sensibilizzazione sul tema migratorio, in cooperazione con gli altri ambiti della Provincia di Monza e della Brianza.',
          target: 'Soggetti aderenti alla Rete Matrioska; enti, servizi territoriali e attori attivi sul tema dei fenomeni migratori; operatori e operatrici.',
          target_cluster: ['Adulti', 'Minori'],
          focus_disabilita: false,
          azioni: [
            'Strutturare collaborazioni continuative con enti e servizi territoriali, sistematizzando le modalità di segnalazione e di risposta, proponendo incontri informativi su tematiche inerenti al fenomeno migratorio',
            'Strutturare modalità di comunicazione, informazione e partecipazione rivolte alle amministrazioni locali',
            'Mantenere periodicamente le convocazioni del Tavolo Interistituzionale quale luogo di raccordo e confronto tra tecnici, politici e stakeholders sulle tematiche legate alle migrazioni',
            'Individuare nuove modalità di raccolta dati inerenti al lavoro operativo degli sportelli al fine di quantificare le nuove richieste specifiche provenienti dall\'utenza',
            'Sulla base dei dati raccolti, proporre nuove prassi organizzative degli sportelli che rispondano alla diversificazione dei bisogni portati',
            'Promuovere iniziative di sensibilizzazione sul tema migratorio con la collaborazione dei soggetti attivi sul territorio',
          ],
          risorse: [
            'Fondo di Ambito dedicato alla rete degli sportelli stranieri',
            'Uffici di Piano',
            'Figura di coordinamento della rete',
            'Operatori e operatrici degli sportelli',
            'Eventuale personale esterno a supporto del processo',
          ],
          partners: [
            'Glob Cooperativa Sociale (terzo settore aderente alla Rete Matrioska)',
            'CGIL di Monza e della Brianza',
            'CISL di Monza Brianza e Lecco',
            'ATS Brianza',
            'Prefettura di Monza',
            'IRCCS San Gerardo dei Tintori di Monza',
            'Servizi sociali comunali',
            'Centri Psico-Sociali',
            'CPIA',
            'Gruppo Tanti Mondi una comunità / Rete Trevi',
            'Progetti SAI Ordinari e Minori',
            'Casa Circondariale di Monza',
          ],
          cooperazioneAltriAmbiti: 'Carate Brianza, Desio, Monza, Seregno (Protocollo di Intesa della Rete Matrioska)',
          continuita2021: true,
          nuovoServizio: false,
          coinvolgimentoASST: true,
          areePolicy: [
            'A) Contrasto alla povertà e all\'emarginazione sociale e promozione dell\'inclusione attiva',
            'K) Interventi di sistema per il potenziamento dell\'Ufficio di Piano e il rafforzamento della gestione associata',
          ],
          indicatoriOutput: [
            'Aumento delle collaborazioni con enti e servizi esterni',
            'Prassi consolidata e tracciata di segnalazione e di richiesta consulenze da parte di enti e servizi del territorio',
            'Documento di raccolta ed elaborazione dati relativa al lavoro degli sportelli e ai bisogni emergenti',
          ],
          indicatoriOutcome: [
            'Ampliamento della partecipazione e del riconoscimento dei rappresentanti politici',
            'Sviluppo di nuove proposte di governance di sistema e di lavoro di rete con i servizi',
          ],
        },

        {
          id: 'contrasto-gap',
          titolo: 'Contrasto del Gioco d\'Azzardo Patologico (GAP)',
          obiettivo_cluster: 'Interambito',
          obiettivo:
            'Sensibilizzare al rischio GAP la popolazione in generale con attenzione a target più a rischio (anziani e giovani); potenziare le competenze dei moltiplicatori; aumentare la consapevolezza di amministratori e operatori; aumentare l\'health literacy sul GAP e la conoscenza dei servizi territoriali.',
          target: 'Tutta la cittadinanza e le istituzioni delle province di Monza e Lecco; centri anziani e associazioni di volontariato con prevalenza over 65; giovani tramite connessione con il mondo sportivo.',
          target_cluster: ['Anziani', 'Adulti', 'Minori'],
          focus_disabilita: false,
          azioni: [
            'Sviluppo "Tavolo No Slot" territoriale',
            'Identificazione e allestimento di luoghi-presidio per il contrasto al gioco d\'azzardo patologico (azioni "No Slot" costanti e strutturate)',
            'Costruzione di materiali informativi e campagne di sensibilizzazione ad hoc',
            'Distribuzione di materiali informativi in contesti quotidiani (eventi "No Slot")',
            'Incontri di sensibilizzazione e formazioni sul gioco d\'azzardo online rivolti ai moltiplicatori con il coinvolgimento dei servizi',
            'Incontri sul rischio GAP in contesti frequentati da soggetti a rischio',
            'Corso di formazione, aggiornamento sulle normative e gli strumenti di controllo e gestione dei dati statistici e programmatori per le istituzioni',
            'Connessione con gli altri setting del Piano Gap di ATS Brianza (scuola, lavoro)',
            'Aggiornamento e diffusione di una piattaforma digitale con materiali utili sul tema (padlet)',
          ],
          risorse: [
            'Piano Gap di ATS Brianza (circa € 100.000,00 per annualità)',
            'Valorizzazione del lavoro degli operatori pubblici e del volontariato',
          ],
          partners: [
            'CSV Monza Lecco Sondrio',
            'Spazio Giovani Impresa Sociale',
            'Coop. Atipica',
            'Arci Lecco Sondrio',
            'Impresa Sociale Girasole',
            'ATS Brianza',
            'Operatori pubblici e del Terzo Settore',
          ],
          cooperazioneAltriAmbiti: 'Tutti gli ambiti della Provincia di Monza e Lecco sono partner progettuali',
          continuita2021: true,
          nuovoServizio: false,
          statoServizio: 'potenziamento',
          coinvolgimentoASST: true,
          areePolicy: [
            'A) Contrasto alla povertà e all\'emarginazione sociale',
            'A) Promozione inclusione attiva',
          ],
          indicatoriOutput: [
            'Almeno 30 enti/soggetti territoriali di presidio individuati',
            'Almeno 10 campagne e materiali specifici di sensibilizzazione',
            'Almeno 10.000 materiali informativi annui distribuiti',
            'Almeno 10 incontri di formazione organizzati',
            'Almeno 10 eventi realizzati',
            'Almeno 80 moltiplicatori intercettati nella formazione',
            'Almeno 8 incontri di rete',
            'Almeno 20 eventi organizzati',
          ],
          indicatoriOutcome: [
            'Potenziamento delle conoscenze dei cittadini sul rischio patologico del gioco d\'azzardo',
            'Maggiore conoscenza dei servizi di cura di chi ha sviluppato un problema di dipendenza',
          ],
        },

        {
          id: 'rete-artemide',
          titolo: 'Rete Interistituzionale e Provinciale Artemide',
          obiettivo_cluster: 'Interambito',
          obiettivo:
            'Diffondere una cultura di contrasto alla violenza di genere; migliorare la qualità dei servizi a sostegno delle donne vittime di violenza; sostenere donne e figli; potenziare le reti con gli stakeholder; rafforzare gli interventi per l\'autonomia abitativa e lavorativa delle donne; garantire personale qualificato e immediata protezione.',
          target: 'Donne vittime di violenza e i loro figli; CAV/Case Rifugio; enti sottoscrittori del Protocollo della Rete Artemide; cittadinanza.',
          target_cluster: ['Adulti', 'Minori'],
          focus_disabilita: false,
          azioni: [
            'Messa a sistema degli eventi di sensibilizzazione aperti alla cittadinanza',
            'Partecipazione del coordinatore/responsabile della rete a eventi di sensibilizzazione organizzati dagli stakeholder',
            'Reperire idonei canali di finanziamento per la realizzazione di nuovi eventi di sensibilizzazione',
            'Effettuare periodiche analisi quali/quantitative avvalendosi del patrimonio dati ISTAT, dei Centri Anti Violenza e delle Case Rifugio',
            'Costituire e mantenere tavoli di lavoro tematici ad hoc: tavolo innovazione, tavolo formazione, tavolo protocolli e procedure, tavolo lavoro',
            'Coordinare le cabine di regia e il Tavolo di Governance',
            'Pianificare incontri con nuovi stakeholder interessati ad entrare nella Rete Artemide',
            'Definire con i nuovi partner i reciproci contributi alla Rete Artemide',
            'Realizzare incontri di informazione con i servizi sociali e sociosanitari per favorire l\'accesso delle donne a percorsi di autonomia',
            'Attuazione delle misure regionali sul tema dell\'autonomia delle donne',
            'Approvare ed attuare il piano biennale della formazione della Rete Artemide',
            'Facilitare la connessione tra i nodi della Rete Artemide (CAV, Case Rifugio, Forze dell\'Ordine, pronto soccorso, servizi sociali e sociosanitari)',
          ],
          risorse: [
            '€ 1.004.052,17 Risorse Regionali (biennio 2024-2025)',
            '€ 265.828,30 degli ambiti territoriali (biennio 2024-2025)',
            'Almeno un operatore/volontario per ciascun partner della rete',
            '1 responsabile della Rete Artemide',
            '1 coordinatrice della Rete Artemide',
            '1 figura amministrativa',
            '2 operatrici di rete',
            '6 facilitatori dei tavoli',
          ],
          partners: [
            'I 5 Ambiti territoriali della Provincia di Monza e della Brianza',
            'ATS Brianza, ASST Brianza, IRCCS San Gerardo dei Tintori di Monza',
            'Provincia di Monza e della Brianza, Consigliera di Parità, AFOL Monza e Brianza',
            'INPS Monza e Brianza',
            'Centro Orientamento Famiglia Monza, Croce Rossa Monza, Croce Rossa Villasanta',
            'Istituti Clinici Zucchi, Ordine dei medici chirurghi e odontoiatri, Policlinico di Monza',
            'Organizzazioni Sindacali (CGIL, CISL, UIL)',
            'Centri Antiviolenza Cadom, Mittatron, Telefonodonna, White Mathilda',
            'Forze dell\'Ordine: Carabinieri, Guardia di Finanza, Polizia di Stato, Prefettura, Procura, Questura',
            'Cooperativa Aeris e Cooperativa Spazio Giovani (enti sostenitori)',
          ],
          cooperazioneAltriAmbiti: 'Tutti e 5 gli ambiti della Provincia di Monza e della Brianza',
          continuita2021: true,
          nuovoServizio: false,
          coinvolgimentoASST: true,
          areePolicy: [
            'A) Contrasto alla povertà e all\'emarginazione sociale e promozione della inclusione attiva',
            'B) Politiche Abitative',
            'H) Interventi connessi alle politiche per il lavoro',
            'I) Interventi per la famiglia',
          ],
          indicatoriOutput: [
            'Numero nuovi aderenti alla Rete Artemide',
            'Numero iniziative di sensibilizzazione',
            'Numero di donne avviate al lavoro',
          ],
          indicatoriOutcome: [
            'Almeno 2 azioni di carattere preventivo e di sensibilizzazione',
            'Almeno 2 nuove adesioni alla Rete Artemide',
            'Sostegno ad almeno 10 donne vittime di violenza di genere nel percorso di autonomia abitativa e lavorativa',
            'Almeno 3 incontri annuali della cabina di regia',
            'Almeno 2 incontri annuali del Tavolo di Governance',
            'Almeno 2 incontri annuali dei tavoli di lavoro',
            '1 analisi quali/quantitativa annuale',
            'Approvazione del Piano biennale della formazione entro giugno 2025',
          ],
        },

        {
          id: 'giustizia-riparativa',
          titolo: 'Giustizia Riparativa',
          obiettivo_cluster: 'Interambito',
          obiettivo:
            'Favorire il reinserimento e l\'inclusione sociale di persone sottoposte a provvedimento dell\'autorità giudiziaria; ridurre la recidiva; promuovere percorsi di riconoscimento della vittima e responsabilizzazione dell\'autore di reato; offrire supporto alle vittime di reato; verificare la sostenibilità delle progettualità alla luce del D.L. 150/2022.',
          target: 'Persone autori di reato sottoposte a provvedimenti dell\'autorità giudiziaria; detenuti presso la Casa Circondariale di Monza; vittime generaliste di reato.',
          target_cluster: ['Adulti', 'Minori'],
          focus_disabilita: false,
          azioni: [
            'Attivazione di percorsi di inclusione sociale in favore di persone sottoposte a provvedimenti dell\'autorità giudiziaria',
            'Attivazione di percorsi di reinserimento lavorativo e/o costruzione di percorsi formativi personalizzati integrati con il progetto educativo individualizzato',
            'Percorsi di accoglienza abitativa temporanea ad alta intensità e mediazione sociale dei conflitti',
            'Realizzazione di programmi di giustizia riparativa e mediazione penale in collaborazione con servizi sociali del Comune di Monza, aree pedagogiche degli Istituti di pena e UEPE',
            'Promozione e attivazione dei percorsi a valenza riparativa, con particolare riguardo alle attività socialmente utili e ai lavori di pubblica utilità',
            'Realizzazione di attività di promozione del paradigma della mediazione e della riparazione quale nucleo significativo di un sistema di detenzione innovativo',
            'Incontri periodici con gli stakeholder per la tenuta complessiva delle progettualità e la valutazione della sostenibilità alla luce del D.L. 150/2022',
          ],
          risorse: [
            '€ 15.200,00 già assegnati per attività di giustizia riparativa (scadenza 12 giugno 2025, salvo proroghe)',
            'Istanza a Regione Lombardia per contributo pari ad € 135.730,00 (esito in attesa)',
            '1 educatore professionale a 30 ore/settimana',
            '1 responsabile elevata qualifica a 6 ore/settimana',
          ],
          partners: [
            'Afol Provincia di Monza e Brianza',
            'A&I Società Cooperativa Sociale Onlus',
            'Ex.It Consorzio di Cooperative Sociali',
            'Dike Cooperativa per la mediazione dei conflitti',
            'Consorzio Desio – Brianza ASC',
            'CFP Unione Artigiani',
            'Aeris Cooperativa Sociale',
            'Azienda Speciale Offerta Sociale',
            'Azienda Speciale di Formazione "Scuola Paolo Borsa"',
            'Consorzio Mestieri/Comunità Brianza',
            'Sindacati (CGIL, CISL)',
            'IRCCS San Gerardo',
            'CSV Monza-Lecco-Sondrio',
            'Fondazione Exodus',
            'Questura',
            'UEPE',
            'Scuole superiori del territorio',
            '5 Ambiti territoriali della Provincia di Monza e della Brianza',
            'Centro per l\'impiego di Monza',
          ],
          cooperazioneAltriAmbiti: 'I 5 ambiti territoriali della Provincia di Monza e della Brianza',
          continuita2021: true,
          nuovoServizio: false,
          coinvolgimentoASST: true,
          areePolicy: [
            'A) Contrasto alla povertà e all\'emarginazione sociale e promozione dell\'inclusione attiva',
          ],
          indicatoriOutput: [
            'Numero degli incontri della cabina di regia',
            'Numero degli incontri dell\'equipe operativa',
            'Numero degli incontri dei Poli territoriali',
            'Numero degli incontri con gli ambiti territoriali provinciali',
            'Numero degli incontri con tutti gli stakeholder',
          ],
          indicatoriOutcome: [
            '3 incontri all\'anno della cabina di regia',
            '1 incontro al mese dell\'equipe operativa',
            '2 incontri dei Poli territoriali all\'anno',
            '1 incontro annuale con gli ambiti territoriali',
            'Almeno 3 incontri con tutti gli stakeholder',
          ],
        },

        {
          id: 'inclusione-minori-penale',
          titolo: 'Inclusione Socio-Lavorativa di Minori Sottoposti a Provvedimenti dell\'Autorità Giudiziaria',
          obiettivo_cluster: 'Interambito',
          sottotitolo: 'Implementazione e consolidamento del sistema di accompagnamento',
          obiettivo:
            'Incontrare i bisogni dei ragazzi sottoposti a procedimenti penali attraverso percorsi educativi; sostenere la genitorialità; accompagnare i ragazzi con percorsi psicologici, ricreativi e di inserimento sociale con l\'obiettivo di ridurre al minimo il rischio di recidiva.',
          target: 'Giovani sottoposti a procedimento penale minorile e provvedimenti dell\'autorità giudiziaria; familiari di minori e giovani in carico ai servizi di penale minorile.',
          target_cluster: ['Minori'],
          focus_disabilita: false,
          azioni: [
            'Affiancamenti educativi individuali',
            'Sostegni psicologici individuali e di gruppo per minori e genitori',
            'Attività di gruppo: Gruppo legalità e pet education',
            'Attività di prevenzione nelle scuole',
            'Laboratori professionalizzanti finalizzati all\'inserimento lavorativo',
            'Attivare e implementare la rete esistente dei servizi del penale minorile territoriale (USSM, servizi sociali territoriali)',
            'Intensificare lo scambio e le collaborazioni con realtà del Terzo Settore e del volontariato',
            'Attivare interventi di affiancamento educativo e psicologico a supporto delle messe alla prova e dei percorsi penali',
          ],
          risorse: [
            '€ 250.000 per il biennio 2025-2026',
            'Tecnici Uffici di Piano e operatori dei servizi sociali dei comuni coinvolti',
            'Ente capofila ASC Consorzio Desio Brianza',
            'Operatori del penale minorile',
            'Operatori dei servizi di formazione, orientamento e integrazione lavorativa del territorio',
            'Formatori',
            'Operatori Enti del Terzo Settore',
            'Psicologi',
          ],
          partners: [
            'ASC Consorzio Desio Brianza (ente capofila)',
            'Ufficio di Servizi Sociali Minorenni (USSM)',
            'Centri di Pronto Intervento (CPI) territoriali',
            'Enti del Terzo Settore storicamente gestori delle azioni legate al target',
          ],
          cooperazioneAltriAmbiti: 'Il progetto ha valenza provinciale; gli ambiti sono fruitori del progetto',
          continuita2021: true,
          nuovoServizio: false,
          statoServizio: 'potenziamento',
          coinvolgimentoASST: true,
          areePolicy: [
            'G) Politiche giovanili e per i minori',
            'A) Contrasto alla povertà e all\'emarginazione sociale e promozione dell\'inclusione attiva',
            'I) Interventi per la famiglia',
          ],
          indicatoriOutput: [
            'Realizzazione incontri del tavolo e condivisione con la rete dei singoli territori',
            'Numero di destinatari che concludono percorsi di gruppo',
            'Numero operatori di rete coinvolti',
            'Numero progettualità attivate in collaborazione con i soggetti della rete territoriale',
          ],
          indicatoriOutcome: [
            'Avviare la costruzione sul territorio lombardo di metodologie condivise e proposte di nuove attività da proporre a Regione Lombardia',
            'Riduzione della percentuale di minori con recidiva da quando è partita la progettualità',
            'Incremento delle realtà territoriali coinvolte nella rete a supporto di progettualità a favore dei ragazzi con procedimento penale minorile in atto',
          ],
        },
      ],
    },

    // ─── 6.4 IMMIGRAZIONE ────────────────────────────────────────────────────
    {
      id: 'immigrazione',
      titolo: 'Immigrazione',
      colore: '#ec4899',
      icona: '🌍',
      schede: [
        {
          id: 'sensibilizzazione-msna',
          titolo: 'Sensibilizzazione e Informazione Migrazione, Affido e MSNA',
          obiettivo_cluster: 'Inclusione',
          sottotitolo: 'Minori Stranieri Non Accompagnati',
          obiettivo:
            'Costruire azioni di sensibilizzazione in merito al tema dell\'affido per minori stranieri non accompagnati (MSNA), valutando risorse e possibilità del territorio. Raccogliere dati sul bisogno, mappare le risorse degli enti territoriali e divulgare alla cittadinanza al fine di sensibilizzare il tessuto sociale.',
          target: 'Cittadini dell\'Ambito di Vimercate e Trezzo sull\'Adda.',
          target_cluster: ['Minori', 'Adulti'],
          focus_disabilita: false,
          azioni: [
            'Raccolta dei dati in merito al bisogno di percorsi di affido per minori stranieri non accompagnati (MSNA)',
            'Mappatura risorse ed enti del territorio che trattano il tema, rafforzando il lavoro di rete con gli attori presenti',
            'Messa in rete delle realtà del territorio che si occupano di affido; valorizzazione degli interventi già in atto',
            'Raccolta delle esperienze di altri territori in ambito formativo, per ampliare le possibilità progettuali',
            'Potenziamento delle attività di informazione e sensibilizzazione alla cittadinanza sul tema migrazioni e affido MSNA: eventi sul territorio',
          ],
          risorse: [
            'Progetto SAI – Fondo Nazionale per le Politiche e i Servizi dell\'Asilo (FNPSA)',
          ],
          partners: [
            'Personale del progetto Sistema Accoglienza Integrazione (SAI)',
            'Personale del Servizio Affidi Mowgli',
            'Enti del Terzo Settore per divulgazione e sensibilizzazione',
          ],
          cooperazioneAltriAmbiti: 'Ambito di Trezzo sull\'Adda (medesime funzioni)',
          continuita2021: false,
          nuovoServizio: false,
          coinvolgimentoASST: false,
          areePolicy: [
            'A) Contrasto alla povertà e all\'emarginazione sociale e promozione dell\'inclusione attiva',
            'G) Politiche giovanili e per i minori',
          ],
          indicatoriOutput: [
            'Numero incontri di rete',
            'Numero di eventi realizzati sul territorio',
            'Numero di valutazioni di potenziali progetti di affido',
            'Potenziamento della rete che sul territorio opera in tema affido e migrazioni',
            'Aumentata consapevolezza e sensibilità della cittadinanza in tema affido e migrazioni',
            'Formazione reciproca tra il servizio affidi e il progetto SAI 1404',
          ],
        },

        {
          id: 'equipe-multidisciplinare-immigrazione',
          titolo: 'Costituzione Equipe Multidisciplinare per Vulnerabilità e Immigrazione',
          obiettivo_cluster: 'Inclusione',
          obiettivo:
            'Consolidare le interconnessioni tra le equipe dell\'Area Inclusione per rispondere in modalità integrata ai bisogni connessi al fenomeno della vulnerabilità abitativa di cittadini con background migratorio. Contribuire alla raccolta coordinata dei dati sul bisogno abitativo, alla ricerca di risposte territoriali integrate e all\'individuazione di modalità strutturate di comunicazione alla rete dei soggetti attivi nell\'area immigrazione.',
          target: 'Operatori delle equipe dell\'area inclusione; rete dei comuni e dei soggetti attivi sui territori nell\'area immigrazione; persone o nuclei familiari con background migratorio in situazione di vulnerabilità abitativa residenti nei comuni dell\'ambito.',
          target_cluster: ['Adulti', 'Minori'],
          focus_disabilita: false,
          azioni: [
            'Azione 1 – Raccogliere e condividere con l\'équipe abitare i bisogni abitativi di cittadini con background migratorio segnalati da comuni, enti terzi, Sportelli Stars o in uscita dai progetti SAI',
            'Azione 2 – Partecipare all\'equipe di valutazione multidisciplinare a geometria variabile, laddove coinvolga cittadini con background migratorio, apportando letture e competenze specifiche sul fenomeno migratorio',
            'Azione 3 – Sensibilizzare e informare i servizi del territorio sulle potenzialità dei progetti SAI quali risorse per percorsi di inserimento socio-economico-abitativo; partecipare almeno annualmente alle Commissioni Territoriali e organizzare incontri con il sottogruppo immigrazione',
          ],
          risorse: [
            'Fondo Nazionale per le Politiche e i Servizi dell\'Asilo (FNPSA)',
            'Fondo Povertà',
          ],
          partners: [
            'Personale dell\'Ufficio di Piano (coordinamento e raccordo)',
            'Personale dei progetti SAI Minori e SAI Ordinari',
            'Personale degli Sportelli Stars',
            'Servizi sociali comunali (segnalazioni e valutazione multidimensionale)',
          ],
          cooperazioneAltriAmbiti: 'Ambito di Trezzo sull\'Adda (medesime modalità operative)',
          continuita2021: false,
          nuovoServizio: false,
          coinvolgimentoASST: true,
          areePolicy: [
            'A) Contrasto alla povertà e all\'emarginazione sociale e promozione dell\'inclusione attiva',
            'B) Politiche abitative',
            'K) Interventi di sistema per il potenziamento dell\'Ufficio di Piano',
          ],
          indicatoriOutput: [
            'Prassi consolidata di segnalazione e valutazione delle situazioni di vulnerabilità abitativa',
            'Numero delle soluzioni attivate a fronte delle segnalazioni di vulnerabilità abitativa ricevute',
            'Numero di segnalazioni di vulnerabilità abitativa raccolte e condivise',
            'Numero di equipe di valutazione multidisciplinari svolte',
            'Numero e tipologia di interventi integrati attivati a favore delle persone segnalate',
            'Numero di partecipazioni alle Commissioni Territoriali',
            'Numero di incontri svolti con il sottogruppo immigrazione di valutazione del Piano di Zona',
            'Ampliamento della platea di partecipanti/enti/istituzioni raggiunte dalle iniziative di informazione e sensibilizzazione',
          ],
          indicatoriOutcome: [
            'Costruzione di un modello di lavoro di rete innovativo, efficiente ed efficace, partecipato internamente ed esternamente',
            'Maggiore consapevolezza da parte degli operatori delle specificità portate dalla cittadinanza con background migratorio in relazione alla vulnerabilità abitativa',
            'Maggiore conoscenza da parte degli operatori delle risorse integrate attivate sul territorio',
            'Definizione più articolata e completa del fabbisogno abitativo del territorio',
            'Consolidamento delle modalità di collaborazione tra le equipe dell\'area inclusione',
          ],
        },
      ],
    },
  ],
};

// ─── DATI MONZA ─────────────────────────────────────────────────────────────

export const pianoZonaMonza: PianoZona = {
  ambito: 'Monza',
  triennio: '2025–2027',
  descrizione: 'Piano di Zona dell\'Ambito Territoriale Sociale di Monza / Offertasociale',
  ultimoAggiornamento: '2024-12',
  gruppi: [
    {
      id: 'giovani-minori-famiglie',
      titolo: 'Giovani, Minori, Famiglie',
      colore: '#6366f1',
      icona: '👨‍👩‍👧‍👦',
      schede: [
        {
          id: 'patti-comunita',
          titolo: 'Promozione patti di comunità/progettualità di attivazione della comunità',
          obiettivo_cluster: 'Benessere sociale',
          obiettivo: 'Promuovere patti di comunità e progettualità di attivazione della comunità per contrastare povertà educativa e dispersione scolastica, rafforzando corresponsabilità educativa, rete territoriale e collaborazione interistituzionale.',
          target: 'Genitori, famiglie affidatarie, bambini/e, ragazzi/e, adulti e partner della rete territoriale.',
          target_cluster: ['Minori', 'Adulti'],
          focus_disabilita: false,
          obiettiviDaRaggiungere: [
            'Promuovere la responsabilizzazione della comunità nei processi educativi.',
            'Formare sui patti di comunità/progettualità di attivazione della comunità.',
            'Rilevare i Patti di comunità/progetti attivi tra i partner della rete.',
            'Creare un tavolo di lavoro stabile tra soggetti attuatori.',
            'Ampliare i soggetti coinvolti, aumentare i Patti/progettualità sottoscritti e trovare canali di finanziamento per la comunicazione.',
          ],
          azioni: [
            'Costituire e gestire il tavolo di lavoro dedicato alla promozione dei patti di comunità.',
            'Promuovere collaborazione, valorizzazione esperienze, analisi rischi/bisogni e individuazione soluzioni.',
            'Realizzare azioni di comunicazione e sensibilizzazione sulle esperienze territoriali.',
            'Rilevare Patti/progetti esistenti e promuovere sottoscrizione di nuovi Patti/progettualità.',
            'Attivare incontri con ASST/IRCCS S. Gerardo e Istituti comprensivi.',
          ],
          risorse: [
            'Formazione: risorse CSV.',
            'Comunicazione: risorse da reperire tramite canali di finanziamento dedicati.',
            'Quantificazione economica da definire in fase attuativa.',
            'Almeno un operatore/volontario per partner della rete.',
          ],
          partners: [
            'Istituti scolastici',
            'Uffici istruzione/partecipazione/servizi sociali dei Comuni',
            'Parrocchie e oratori',
            'ETS e reti informali',
            'Ufficio scolastico provinciale',
            'Associazioni',
            'ASST/IRCCS S. Gerardo e Istituti comprensivi',
            'Reti territoriali (Tiki Taka, Monza.con, Rete Artemide) e Tavoli tematici',
          ],
          bisogniPdf: ['Formazione', 'Conoscenza', 'Ricomposizione esperienze', 'Potenziamento corresponsabilità educativa'],
          inputPdf: ['Personale specializzato', 'Volontari', 'Esperienze attive', 'Risorse economiche'],
          modalitaOperativePdf: [
            'Incontri con ASST/IRCCS e Istituti comprensivi.',
            'Percorso formativo sui Patti educativi/progetti di comunità per il tavolo Comunità educante.',
            'Rilevazione Patti/progetti attivi, analisi punti di forza/debolezza e proposte risolutive.',
            'Ricerca canali di finanziamento per comunicazione e attivazione nuovi Patti/progettualità.',
          ],
          coinvolgimentoASST: true,
          nuovoServizio: true,
          indicatoriOutput: [
            'n. percorsi di formazione; n. partecipanti formazione; questionari restituiti',
            'n. eventi comunicativi; n. partecipanti eventi',
            'n. Patti/progetti rilevati; n. Patti/progetti nuovi sottoscritti',
            'n. incontri tavolo Patti; n. nuovi componenti tavolo; n. incontri tavolo Comunità educante',
            'n. canali finanziamento individuati; n. incontri con ASST/IRCCS/Istituti comprensivi',
          ],
          indicatoriOutcome: [
            'Aumento connessioni tra attori della comunità per attuare Patti/progetti di attivazione della comunità.',
            'Almeno 1 percorso formativo e almeno 2 eventi di comunicazione/sensibilizzazione.',
            'Almeno 2 Patti/progetti nuovi e rafforzamento della collaborazione territoriale.',
          ],
          areePolicy: [
            'A) Contrasto povertà/emarginazione e promozione inclusione attiva',
            'G) Politiche giovanili e per i minori',
          ],
          contenutoIntegralePdf: `Sezione PDF: TITOLO INTERVENTO "Promozione patti di comunità/progettualità di attivazione della comunità" (pagg. 1-5 del file estratto).
Contenuti inclusi: obiettivi, azioni programmate, target, risorse economiche/personale, integrazione policy, punti chiave, coinvolgimento ASST/IRCCS, continuità/novità, coprogrammazione/coprogettazione, rete territoriale, bisogni/input, modalità operative, indicatori di processo, outcome e impatto atteso.`
        },
        {
          id: 'spazi-giovani',
          titolo: 'Spazi fisici e digitali per i giovani (11-35)',
          obiettivo_cluster: 'Benessere sociale',
          obiettivo: 'Favorire partecipazione attiva e corresponsabile dei giovani alla vita della città, riqualificando spazi urbani e introducendo luoghi fisici/digitali di confronto, connessi anche ai processi di partecipazione politica giovanile.',
          target: 'Giovani (anche in forma associata), scuole, ETS, centri di aggregazione, enti e uffici tecnici per riqualificazione urbana.',
          target_cluster: ['Minori', 'Adulti'],
          focus_disabilita: false,
          obiettiviDaRaggiungere: [
            'Favorire partecipazione attiva/contributiva dei giovani.',
            'Promuovere senso di appartenenza alla città.',
            'Potenziare partecipazione politica dei giovani (Consigli comunali dei ragazzi e delle ragazze).',
          ],
          azioni: [
            'Coinvolgere uffici tecnici/riqualificazione urbana/verde pubblico.',
            'Coinvolgere scuole e associazioni sportive su progettualità partecipative.',
            'Aprire tavolo di confronto con giovani per mappare luoghi di aggregazione e spazi da attivare/riqualificare.',
            'Sostenere processi di attivazione civica e riqualificazione spazi/servizi.',
            'Coinvolgere rappresentanza giovanile nel Tavolo Comunità educante.',
            'Confronto con referenti politici per attivazione Consigli comunali dei ragazzi/e.',
          ],
          risorse: [
            'Risorse per tenuta processo e possibili interventi di riqualificazione urbana (da quantificare).',
            'Reperimento fondi anche con coinvolgimento del privato.',
            'Almeno un operatore/volontario per partner della rete.',
          ],
          partners: [
            'Giovani e associazioni giovanili',
            'Uffici tecnici/riqualificazione urbana/verde pubblico',
            'Istituti scolastici',
            'Associazioni sportive',
            'ETS',
            'Assemblea dei Sindaci/Consigli comunali',
          ],
          bisogniPdf: [
            'Conoscenza del bisogno dei giovani',
            'Valorizzazione competenze giovanili nei processi decisionali',
            'Valorizzazione patrimonio pubblico',
            'Costruzione città con/per i giovani',
            'Innovazione approccio politiche sociali con contributo nuove generazioni',
          ],
          inputPdf: ['Personale specializzato', 'Volontari', 'Istituti comprensivi', 'Uffici tecnici', 'Associazioni sportive', 'Giovani', 'Amministratori', 'Strumenti digitali'],
          modalitaOperativePdf: [
            'Mappatura esperienze di valorizzazione giovani e spazi pubblici.',
            'Avvio tavolo di confronto con giovani e individuazione luoghi di interesse/riqualificazione.',
            'Quantificazione spesa e uso strumenti digitali per ingaggio.',
            'Individuazione rappresentanza giovanile per Tavolo Comunità educante.',
            'Valutazione e costituzione Consigli comunali dei ragazzi/e.',
          ],
          coinvolgimentoASST: true,
          nuovoServizio: true,
          indicatoriOutput: [
            'n. esperienze in atto; n. giovani al tavolo; n. incontri tavolo di confronto',
            'n. referenti uffici/scuole/associazioni coinvolti; n. luoghi da riqualificare',
            'n. giovani al Tavolo Comunità educante; n. strumenti digitali usati',
            'n. incontri con amministratori; data costituzione Consigli comunali ragazzi/e',
          ],
          indicatoriOutcome: [
            'Nuovi spazi aperti ai giovani.',
            'Presenza stabile rappresentanza giovanile nel Tavolo Comunità educante.',
            'Programmazione politica più partecipata sul target giovani.',
          ],
          areePolicy: [
            'A) Inclusione attiva',
            'F) Digitalizzazione servizi',
            'G) Politiche giovanili e minori',
          ],
          contenutoIntegralePdf: `Sezione PDF: TITOLO INTERVENTO "Spazi fisici e digitali per i giovani (11-35)" (pagg. 6-10 del file estratto).
Contenuti inclusi: obiettivi, azioni, target, risorse, policy, punti chiave, aspetti digitali, modalità operative, indicatori di processo, output e impatto atteso.`
        },
        {
          id: 'spazi-bambini-famiglie',
          titolo: 'Spazi per bambini e famiglie (0-10)',
          obiettivo_cluster: 'Benessere sociale',
          obiettivo: 'Migliorare e rendere più accessibili gli spazi formali/informali per bambini e famiglie, valorizzando quartieri, risorse comunitarie ed economia solidale, con approccio partecipativo e intergenerazionale.',
          target: 'Bambini, genitori/tutori/famiglie, scuole, urbanistica, ETS e comunità territoriale.',
          target_cluster: ['Minori'],
          focus_disabilita: false,
          obiettiviDaRaggiungere: [
            'Conoscere spazi e bisogni di bambini/famiglie.',
            'Costruire rapporto fiduciario con famiglie.',
            'Favorire protagonismo di bambini/famiglie nell\'abbellimento degli spazi.',
            'Incrementare disponibilità/accessibilità e innovare uso degli spazi.',
            'Promuovere circolarità risorse e fattibilità azioni con competenze comunitarie.',
          ],
          azioni: [
            'Mappare spazi formali/informali e risorse attivabili (azione trasversale).',
            'Aprire confronto con bambini/famiglie per definire luoghi di interesse, bisogni e proposte.',
            'Coinvolgere scuole e comitati genitori su progettualità partecipative e spazi inutilizzati.',
            'Progettare spazi pubblici child/family-friendly.',
            'Reperire risorse e canali finanziari (anche aziende sensibili) per adeguamento e attività.',
            'Promuovere incontri di sensibilizzazione con enti territoriali e comunicazione dedicata.',
          ],
          risorse: [
            'Risorse economiche per riqualificazione/ristrutturazione, attività e comunicazione (da quantificare).',
            'Reperimento fondi anche con coinvolgimento privato e aziendale.',
            'Almeno un operatore/volontario per partner della rete.',
          ],
          partners: [
            'Bambini, famiglie, genitori/tutori',
            'Istituti comprensivi e comitati genitori',
            'Uffici tecnici/urbanistica/amministratori',
            'ETS',
            'Aziende sensibili e reti territoriali',
          ],
          bisogniPdf: [
            'Conoscenza risorse/bisogni',
            'Legami collaborativi con famiglie',
            'Città a misura bambini/famiglie',
            'Valorizzazione risorse persone/territorio ed economia solidale',
            'Spazi più rispondenti ai bisogni e maggiore centralità bambini/famiglie nelle politiche sociali',
          ],
          inputPdf: ['Personale specializzato', 'Bambini e famiglie', 'Volontari', 'Uffici tecnici/urbanistici', 'Istituti comprensivi', 'Amministratori', 'Aziende'],
          modalitaOperativePdf: [
            'Rilevazione risorse e spazi con stakeholder.',
            'Coinvolgimento bambini/famiglie nella co-definizione di spazi e bisogni.',
            'Incontri con scuole e uffici tecnici per fattibilità e attuazione.',
            'Ricerca congiunta risorse economiche e sponsorizzazioni.',
            'Comunicazione/sensibilizzazione su luoghi e opportunità.',
          ],
          coinvolgimentoASST: true,
          nuovoServizio: true,
          indicatoriOutput: [
            'n. spazi finalizzati; n. incontri con bambini/famiglie; n. risorse economia circolare',
            'n. scuole coinvolte; n. incontri uffici tecnici; n. spazi da riqualificare',
            'n. canali finanziamento; n. aziende coinvolte; n. attività promozione spazi',
            'n. incontri enti per sensibilizzazione',
          ],
          indicatoriOutcome: [
            'Potenziamento spazi/luoghi a misura di bambini e famiglie.',
            'Miglioramento della qualità della vita complessiva nel territorio.',
          ],
          areePolicy: [
            'A) Inclusione attiva',
            'G) Politiche giovanili e minori',
            'I) Interventi per la famiglia',
          ],
          contenutoIntegralePdf: `Sezione PDF: TITOLO INTERVENTO "Spazi per bambini e famiglie (0-10)" (pagg. 11-16 del file estratto).
Contenuti inclusi: obiettivi, azioni, target, risorse, policy, bisogni/input, modalità operative, indicatori, output e impatto atteso.`
        },
        {
          id: 'contrasto-poverta-educativa',
          titolo: 'Contrasto alla povertà educativa ed alla dispersione scolastica',
          obiettivo_cluster: 'Inclusione',
          obiettivo: 'Ricostruire in modo integrato la filiera territoriale contro povertà educativa e dispersione scolastica, attraverso un laboratorio congiunto, un piano condiviso e patti formalizzati tra enti pubblici e privati.',
          target: 'Scuole, assessorati competenti, servizi socio-sanitari, giovani/associazioni giovanili, famiglie/associazioni familiari.',
          target_cluster: ['Minori'],
          focus_disabilita: false,
          obiettiviDaRaggiungere: [
            'Ricomporre i soggetti che operano su povertà educativa/dispersione scolastica.',
            'Potenziare collaborazione tra servizi pubblici, privato sociale e scuola.',
            'Attivare laboratorio congiunto per obiettivi comuni e strategie collegiali.',
            'Contrastare frammentazione progettuale e valorizzare interventi già attivi.',
            'Stendere un piano condiviso e patti operativi con copertura finanziaria.',
          ],
          azioni: [
            'Individuare stakeholder e facilitatore del tavolo.',
            'Convocare il tavolo per mappatura servizi/interventi, analisi fenomeno e ricomposizione risorse (azione trasversale).',
            'Costituire Comunità di pratiche e definire tematiche condivise.',
            'Stendere il PIANO di contrasto e ricercare finanziamenti per attuarlo.',
            'Sottoscrivere PATTO/PATTI pubblico-privati a contrasto povertà educativa/dispersione scolastica.',
          ],
          risorse: [
            'Risorse per facilitatore tavolo (da quantificare/reperire).',
            'Ulteriori risorse per attuazione azioni del PIANO (da quantificare e finanziare).',
            'Almeno un operatore/volontario per partner della rete.',
          ],
          partners: [
            'Scuole',
            'Assessorati istruzione/politiche educative/giovanili',
            'Servizi socio-sanitari (ATS Brianza, Consultori, NPIA)',
            'Giovani e associazioni giovanili',
            'Famiglie e associazioni familiari',
            'Reti territoriali e Tavoli tematici di Ambito',
          ],
          bisogniPdf: [
            'Ricomporre risorse',
            'Fare rete tra stakeholder',
            'Benessere bambini/famiglie',
            'Diffusione informazioni su progetti attivi',
            'Continuità servizi/progetti in atto',
            'Piano condiviso contro povertà educativa e dispersione scolastica',
          ],
          inputPdf: ['Soggetti della rete', 'Esperienze in corso', 'Fonti teoriche/metodologiche'],
          modalitaOperativePdf: [
            'Reperire finanziamento facilitatore, individuarlo e convocare tavolo.',
            'Mappare servizi/interventi e analizzare fenomeno territoriale.',
            'Formalizzare Comunità di pratiche e approvare PIANO (entro 31/12/2026).',
            'Reperire finanziamenti per il PIANO e sottoscrivere almeno un patto.',
          ],
          coinvolgimentoASST: true,
          nuovoServizio: true,
          indicatoriOutput: [
            'n. canali finanziamento (facilitatore e piano)',
            'n. stakeholder coinvolti',
            'n. incontri tavolo',
            'data approvazione PIANO',
            'n. patti sottoscritti',
          ],
          indicatoriOutcome: [
            'Ricomposizione sociale e riduzione povertà educativa/dispersione scolastica.',
            'Strutturazione stabile della cooperazione territoriale sul tema.',
          ],
          areePolicy: [
            'A) Inclusione attiva',
            'G) Politiche giovanili e minori',
          ],
          contenutoIntegralePdf: `Sezione PDF: TITOLO INTERVENTO "Contrasto alla povertà educativa ed alla dispersione scolastica" (pagg. 17-21 del file estratto).
Contenuti inclusi: obiettivi, azioni, target, risorse, policy, governance tavolo, modalità operative, indicatori/output e impatto atteso.`
        },
        {
          id: 'contrasto-neet',
          titolo: 'Contrasto dei NEET',
          obiettivo_cluster: 'Benessere sociale',
          obiettivo: 'Ridurre l\'incidenza dei NEET con interventi personalizzati, integrazione scuola-servizi-rete territoriale e definizione di strategie operative condivise di ingaggio e accompagnamento.',
          target: 'NEET, famiglie, scuole, ATS/ASST/IRCCS, AFOL, associazioni e soggetti della rete territoriale.',
          target_cluster: ['Minori', 'Adulti'],
          focus_disabilita: false,
          obiettiviDaRaggiungere: [
            'Contribuire alla riduzione dell\'incidenza NEET.',
            'Potenziare interventi di riduzione NEET.',
            'Potenziare comunicazione tra scuola e servizi.',
          ],
          azioni: [
            'Costituire tavolo tematico NEET con scuole, AFOL, consultori/servizi adolescenti, associazioni e assessorati.',
            'Approfondire il fenomeno con fonti di riferimento e analisi condivise.',
            'Individuare i NEET e definire modalità di ingaggio di giovani e famiglie.',
            'Definire piani individualizzati con servizi pubblici/privato sociale/volontariato.',
            'Attivare interventi personalizzati, ascolto famiglie (anche online), gruppi di ascolto e sensibilizzazione multilivello.',
            'Reperire risorse economiche per eventuali azioni/servizi aggiuntivi.',
          ],
          risorse: [
            'Nessun investimento economico previsto in prima battuta.',
            'Eventuali costi futuri per potenziamento servizi/azioni da quantificare.',
            'Almeno un operatore/volontario per partner della rete.',
          ],
          partners: [
            'Scuole',
            'ATS/ASST/IRCCS',
            'AFOL',
            'Associazioni giovanili e familiari',
            'Servizi adolescenti/consultori',
            'Reti territoriali e Tavoli di Ambito',
          ],
          bisogniPdf: [
            'Offrire opportunità lavorative/formative contro forme multiple di disagio',
            'Conoscere attività già in corso sul contrasto NEET',
            'Agganciare i NEET',
            'Fare rete tra stakeholder',
          ],
          inputPdf: ['Soggetti della rete', 'Esperienze in corso', 'Fonti teoriche/metodologiche sul tema'],
          modalitaOperativePdf: [
            'Individuazione componenti tavolo e sua convocazione/tenuta.',
            'Raccolta fonti teoriche/metodologiche e analisi collegiali.',
            'Rilevazione attività in atto e strategie ulteriori di divulgazione.',
            'Definizione strategie innovative di contrasto NEET e copertura finanziaria.',
          ],
          coinvolgimentoASST: true,
          nuovoServizio: true,
          statoServizio: 'potenziamento',
          indicatoriOutput: [
            'n. componenti tavolo; n. incontri tavolo',
            'n. strategie divulgazione attività',
            'n. attività nuove/innovative attivate',
            'n. canali finanziamento individuati',
          ],
          indicatoriOutcome: [
            'Diminuzione dei giovani che non studiano e non lavorano.',
            'Migliore coordinamento scuola-servizi-rete nella presa in carico NEET.',
          ],
          areePolicy: [
            'A) Inclusione attiva',
            'G) Politiche giovanili e minori',
            'I) Interventi per la famiglia',
          ],
          contenutoIntegralePdf: `Sezione PDF: TITOLO INTERVENTO "Contrasto dei NEET" (pagg. 22-25 del file estratto).
Contenuti inclusi: obiettivi, azioni, target, risorse, governance, bisogni/input, modalità operative, indicatori/output e impatto atteso.`
        },
      ],
    },
    {
      id: 'salute',
      titolo: 'Salute',
      colore: '#0ea5e9',
      icona: '🩺',
      schede: [
        {
          id: 'cultura-salute',
          titolo: 'Cultura della salute',
          obiettivo_cluster: 'Inclusione',
          obiettivo: 'Diffondere la consapevolezza sui temi della salute tra tutti i cittadini dell\'Ambito di Monza con approccio preventivo, di prossimità e di ricomposizione sociale, valorizzando e integrando le iniziative già attive.',
          target: 'Minori, adolescenti e famiglie; adulti e anziani; persone con disabilità; associazioni e organizzazioni; cittadinanza nel suo complesso.',
          target_cluster: ['Minori', 'Adulti', 'Anziani'],
          focus_disabilita: false,
          obiettiviDaRaggiungere: [
            'Diffondere la cultura della salute in modo capillare sul territorio.',
            'Promuovere un approccio preventivo e di ricomposizione sociale.',
            'Rafforzare il supporto alle figure di prossimità (tutor della salute, custodi sociali, operatori domiciliari).',
            'Ricomporre e connettere le azioni di sensibilizzazione e le progettualità in atto.',
            'Disseminare il modello agli altri Ambiti territoriali di ATS Brianza.',
          ],
          azioni: [
            'Rilevare e ricomporre le azioni di sensibilizzazione già attive (azione trasversale).',
            'Promuovere il dialogo tra operatori e la connessione con il sistema dei servizi.',
            'Formare e attivare figure prossime al cittadino in luoghi di prossimità.',
            'Reperire canali di finanziamento per percorsi formativi specializzati.',
            'Realizzare incontri con gli altri Ambiti ATS Brianza per presentazione e confronto.',
          ],
          risorse: [
            'Nessuna risorsa specifica per la diffusione, salvo formazione specialistica.',
            'Quantificazione economica da definire in fase attuativa.',
            'Almeno un operatore/volontario per ciascun partner della rete.',
          ],
          partners: [
            'ATS Brianza e ASST/IRCCS',
            'Reti territoriali (Tiki Taka, Monza.con)',
            'Tavoli tematici (es. Tavolo Associazioni Oncologiche, Tavolo GAP)',
            'Associazioni e organizzazioni sindacali',
            'CSV, CRI, cooperative sociali del territorio',
          ],
          bisogniPdf: [
            'Tutela della salute psico-fisico-sociale del maggior numero di cittadini',
            'Ricomposizione delle azioni di sensibilizzazione',
            'Promozione dell\'invecchiamento attivo e del benessere',
            'Interconnessione delle progettualità esistenti',
            'Strutturazione stabile di azioni di promozione della salute',
          ],
          inputPdf: [
            'Iniziative di promozione della salute già attive',
            'Operatori specializzati',
            'Associazioni di volontariato e figure di prossimità',
          ],
          modalitaOperativePdf: [
            'Individuare figure e luoghi di prossimità.',
            'Rilevare iniziative già in atto e promuovere nuove connessioni operative.',
            'Realizzare momenti formativi per nuovi luoghi/operatori.',
            'Confrontarsi con altri Ambiti ATS per disseminazione del modello.',
          ],
          coinvolgimentoASST: true,
          nuovoServizio: true,
          areePolicy: [
            'E) Anziani',
            'G) Politiche giovanili e per i minori',
            'I) Interventi per la famiglia',
            'J) Interventi a favore di persone con disabilità',
          ],
          indicatoriOutput: [
            'Almeno 20 figure di prossimità presenti sul territorio',
            'Almeno 5 iniziative di sensibilizzazione ricomposte',
            'Almeno 10 nuovi luoghi di prossimità da formare',
            'Almeno 2 interventi formativi realizzati',
            'Almeno 1 canale di finanziamento individuato',
            'Almeno 3 Ambiti territoriali coinvolti e 2 incontri realizzati',
          ],
          indicatoriOutcome: [
            'Aumento dei luoghi di promozione della salute.',
            'Miglioramento della salute e prevenzione di condizioni di malattia psico-fisico-sociale.',
          ],
          contenutoIntegralePdf: `Sezione PDF: TITOLO INTERVENTO "Cultura della salute".
Contenuti inclusi: obiettivi, azioni, target, risorse, policy integrate, punti chiave, coinvolgimento ASST/IRCCS, co-programmazione/co-progettazione, bisogni/input, modalità operative, indicatori di processo, output e impatto atteso.`,
        },
        {
          id: 'accessibilita-servizi-salute',
          titolo: 'Accessibilità',
          obiettivo_cluster: 'Inclusione',
          obiettivo: 'Favorire l\'accessibilità informativa e fisica ai servizi, soprattutto per le persone fragili, semplificando i percorsi di accesso e valorizzando Case della Comunità, strumenti digitali e reti di prossimità.',
          target: 'Anziani e persone con disabilità, caregiver, comunità, tutor della salute, operatori di sportelli diffusi e servizi, uffici tecnici pubblici.',
          target_cluster: ['Anziani',],
          focus_disabilita: true,
          obiettiviDaRaggiungere: [
            'Migliorare accesso informativo e fisico ai servizi.',
            'Semplificare le modalità di accesso alle RSA.',
            'Potenziare i luoghi e gli attori di prossimità (Case della Comunità, tutor, sportelli diffusi).',
            'Valorizzare il digitale per orientamento e accesso ai servizi.',
            'Promuovere abbattimento delle barriere architettoniche.',
          ],
          azioni: [
            'Promuovere tutor della salute e altri soggetti prossimi al cittadino.',
            'Ricomporre/ottimizzare strumenti digitali per accesso e informazione.',
            'Condividere informazioni con sportelli diffusi e contesti professionali di rete.',
            'Valorizzare esperienze digitali domiciliari (anche PNRR Anziani).',
            'Attivare incontri con ATS/ASST/IRCCS per facilitare accesso alle RSA.',
            'Sensibilizzare uffici tecnici su barriere architettoniche e accessibilità urbana.',
          ],
          risorse: [
            'Risorse dedicate alla formazione di tutor/operatori della salute, da quantificare.',
            'Canali di finanziamento da individuare.',
            'Almeno un operatore/volontario per ciascun partner della rete.',
          ],
          partners: [
            'Case della Comunità',
            'ATS Brianza, ASST/IRCCS',
            'Servizi sociali comunali e sportelli diffusi',
            'Associazioni, organizzazioni sindacali e reti territoriali',
            'Uffici tecnici degli enti locali',
          ],
          bisogniPdf: [
            'Prossimità al cittadino e intercettazione del bisogno non espresso',
            'Informazione/orientamento ai servizi',
            'Accessibilità delle informazioni e uso tecnologia',
            'Semplificazione accesso RSA',
            'Abbattimento barriere architettoniche',
          ],
          inputPdf: [
            'Case della Comunità presenti e in apertura',
            'Servizi di segretariato sociale e socio-sanitario',
            'Punti di accesso al welfare diffusi',
            'Sperimentazione PUA sociale e socio-sanitario',
            'Sistemi tecnologici domiciliari e piattaforme caregiver',
          ],
          modalitaOperativePdf: [
            'Incontri periodici del Tavolo Salute per divulgazione interna/esterna.',
            'Coinvolgimento referenti Case della Comunità e operatori di prossimità.',
            'Mappatura e diffusione strumenti digitali già attivi.',
            'Incontri con uffici tecnici per abbattimento barriere e con ATS/ASST/IRCCS per accesso RSA.',
          ],
          coinvolgimentoASST: true,
          nuovoServizio: true,
          areePolicy: [
            'A) Contrasto alla povertà e all\'emarginazione sociale e promozione dell\'inclusione attiva',
            'E) Anziani',
            'F) Digitalizzazione dei servizi',
            'G) Politiche giovanili e per i minori',
            'I) Interventi per la famiglia',
            'J) Interventi a favore di persone con disabilità',
          ],
          indicatoriOutput: [
            'Almeno 1 incontro del tavolo per divulgazione su Case della Comunità',
            'Almeno 4 soggetti prossimi al cittadino coinvolti',
            'Almeno 5 nuovi punti di accesso al welfare attivati',
            'Almeno 1 strumento digitale attivo per facilitare l\'accesso ai servizi',
            'Almeno 2 incontri con ATS/ASST/IRCCS su accesso RSA',
            'Almeno 2 incontri con uffici tecnici su barriere architettoniche',
          ],
          indicatoriOutcome: [
            'Potenziamento dei luoghi prossimi al cittadino.',
            'Miglioramento della qualità della salute psico-fisica-sociale dei cittadini.',
          ],
          contenutoIntegralePdf: `Sezione PDF: TITOLO INTERVENTO "Accessibilità".
Contenuti inclusi: obiettivi, azioni, target, risorse, integrazione policy, coinvolgimento ASST/IRCCS, continuità/novità, bisogni/input, aspetti di digitalizzazione, modalità operative, indicatori/output e impatto atteso.`,
        },
        {
          id: 'contrasto-isolamento-solitudine',
          titolo: 'Contrasto all\'isolamento/solitudine',
          obiettivo_cluster: 'Benessere sociale',
          obiettivo: 'Conoscere, consolidare e potenziare in modo strutturato le iniziative territoriali contro isolamento e solitudine, con azioni trasversali rivolte all\'intera comunità.',
          target: 'Adolescenti, giovani e famiglie; anziani e caregiver; associazioni e organizzazioni; comunità nel suo complesso.',
          target_cluster: ['Minori', 'Adulti', 'Anziani'],
          focus_disabilita: false,
          obiettiviDaRaggiungere: [
            'Conoscere i progetti già attivi contro isolamento e solitudine.',
            'Dare continuità agli interventi esistenti.',
            'Potenziare le azioni trasversali rivolte alla comunità.',
            'Trovare canali di finanziamento per nuove iniziative.',
          ],
          azioni: [
            'Mappare e aggiornare i progetti territoriali già attivi (azione trasversale).',
            'Ricercare bandi e canali di finanziamento dedicati.',
            'Organizzare e realizzare iniziative/progetti contro l\'isolamento su target diversi.',
            'Raccordare l\'azione con la mappatura/ricomposizione dell\'offerta territoriale.',
          ],
          risorse: [
            'Risorse economiche non ancora quantificabili, da definire successivamente.',
            'Almeno un operatore/volontario per ciascun partner della rete.',
          ],
          partners: [
            'ATS Brianza, ASST/IRCCS',
            'Reti territoriali (Tiki Taka, Monza.con)',
            'Tavoli tematici territoriali',
            'Associazioni, sindacati, CSV, volontariato e cooperative sociali',
          ],
          bisogniPdf: [
            'Conoscenza dell\'esistente (progetti e mappature già realizzate)',
            'Sensibilizzazione sugli effetti dell\'isolamento sulla salute psico-fisico-sociale',
            'Potenziamento di progetti e iniziative a contrasto della solitudine',
          ],
          inputPdf: [
            'Personale specializzato',
            'Progetti già attivi contro l\'isolamento',
            'Esperienze pregresse sul territorio',
          ],
          modalitaOperativePdf: [
            'Rilevazione condivisa delle attività in atto con tutti gli stakeholder del Piano di Zona.',
            'Definizione di nuove azioni e ricerca finanziamenti idonei.',
            'Attuazione delle attività a finanziamento riconosciuto.',
          ],
          coinvolgimentoASST: true,
          nuovoServizio: true,
          areePolicy: [
            'A) Contrasto alla povertà e all\'emarginazione sociale e promozione dell\'inclusione attiva',
            'D) Domiciliarità',
            'E) Anziani',
            'G) Politiche giovanili e per i minori',
            'I) Interventi per la famiglia',
            'J) Interventi a favore di persone con disabilità',
          ],
          indicatoriOutput: [
            'Almeno 5 eventi di sensibilizzazione già attivi',
            'Almeno 20 persone presenti agli eventi di sensibilizzazione',
            'Almeno 1 canale di finanziamento individuato',
            'Almeno 2 iniziative/progetti realizzati contro la solitudine',
          ],
          indicatoriOutcome: [
            'Incremento delle iniziative/progetti contro isolamento e solitudine.',
            'Miglioramento del benessere psico-fisico e sociale delle persone.',
          ],
          contenutoIntegralePdf: `Sezione PDF: TITOLO INTERVENTO "Contrasto all'isolamento/solitudine".
Contenuti inclusi: obiettivi, azioni, target, risorse, policy, coinvolgimento ASST/IRCCS, co-programmazione/co-progettazione, bisogni/input, modalità operative, indicatori, output e impatto.`,
        },
        {
          id: 'supporto-salute-mentale',
          titolo: 'Azioni di supporto alla tutela della salute mentale',
          obiettivo_cluster: 'Inclusione',
          obiettivo: 'Rilevare precocemente il disagio psichico e rafforzare le risposte territoriali integrate, contrastando isolamento e solitudine delle persone con fragilità psichiche e sostenendo anche i bisogni emergenti della popolazione anziana.',
          target: 'Persone con disagio psichico, anziani e caregiver, adolescenti/giovani/famiglie, scuole, associazioni e comunità territoriale.',
          target_cluster: ['Adulti', 'Anziani', 'Minori'],
          focus_disabilita: false,
          obiettiviDaRaggiungere: [
            'Intercettare precocemente segnali di disagio psichico nei luoghi di prossimità.',
            'Contrastare l\'isolamento di persone con disagio psichico.',
            'Valorizzare connessione tra servizi socio-sanitari, sanitari e comunitari.',
            'Approfondire problematiche emergenti (autismo grave, disabilità psichica acquisita, anziani con disturbi psichici).',
            'Attivare nuove azioni progettuali e laboratoriali con copertura finanziaria.',
          ],
          azioni: [
            'Coinvolgere i servizi di salute mentale e valorizzare i luoghi di sistema già attivi.',
            'Individuare luoghi e operatori/volontari prossimi al cittadino (azione trasversale).',
            'Formare i soggetti di prossimità su riconoscimento segnali e comunicazione efficace.',
            'Rilevare persone sole con disagio psichico attraverso la rete territoriale.',
            'Attivare laboratori e costruire proposte progettuali in risposta ai bisogni emergenti.',
            'Reperire canali di finanziamento sensibilizzando anche enti finanziatori.',
          ],
          risorse: [
            'Risorse da reperire per formazione, contrasto isolamento e nuove azioni progettuali.',
            'Quantificazione economica da definire successivamente.',
            'Almeno un operatore/volontario per ciascun partner della rete.',
          ],
          partners: [
            'Servizi di salute mentale (ASST/IRCCS e rete socio-sanitaria)',
            'Reti territoriali e tavoli progettuali',
            'Associazioni, organizzazioni sindacali, CSV, volontariato, cooperative sociali',
            'Scuole e stakeholder del Piano di Zona',
          ],
          bisogniPdf: [
            'Far emergere situazioni di disagio psichico non note ai servizi',
            'Incrementare occasioni di socialità e relazione',
            'Rafforzare la connessione con servizi di salute mentale',
            'Garantire risposte adeguate anche per bisogni complessi ed emergenti',
          ],
          inputPdf: [
            'Personale specializzato',
            'Servizi di cura della salute mentale',
            'Operatori/volontari prossimi al cittadino',
          ],
          modalitaOperativePdf: [
            'Convocazione referenti salute mentale al Tavolo Salute per alleanze operative.',
            'Coinvolgimento stakeholder per rilevazione esperienze di prossimità e azioni anti-isolamento.',
            'Definizione contenuti, beneficiari e formatori dei percorsi formativi.',
            'Pianificazione laboratori e costruzione piano di nuove azioni con attuazione progressiva.',
          ],
          coinvolgimentoASST: true,
          nuovoServizio: true,
          areePolicy: [
            'D) Domiciliarità',
            'E) Anziani',
            'G) Politiche giovanili e per i minori',
            'J) Interventi a favore di persone con disabilità',
          ],
          indicatoriOutput: [
            'Almeno 2 referenti dei servizi salute mentale coinvolti',
            'Almeno 3 incontri con i referenti della salute mentale',
            'Almeno 10 esperienze di prossimità attive',
            'Almeno 20 persone con disagio psichico individuate',
            'Almeno 20 partecipanti alla formazione e 6 incontri formativi effettuati',
            'Almeno 3 azioni attivate contro isolamento e bisogni psichici emergenti',
          ],
          indicatoriOutcome: [
            'Migliore rilevazione precoce del disagio psichico.',
            'Rafforzamento delle risposte territoriali integrate a tutela della salute mentale.',
          ],
          contenutoIntegralePdf: `Sezione PDF: TITOLO INTERVENTO "Azioni di supporto alla tutela della salute mentale".
Contenuti inclusi: obiettivi, azioni programmate, target, risorse, policy, coinvolgimento ASST/IRCCS, continuità/novità, bisogni/input, modalità operative, indicatori di processo, output e impatto atteso.`,
        },
      ],
    },
    {
      id: 'abitare',
      titolo: 'Abitare',
      colore: '#10b981',
      icona: '🏘️',
      schede: [
        {
          id: 'agenzia-casa-fattibilita',
          titolo: 'Agenzia per la casa dell\'Ambito di Monza: fattibilità',
          obiettivo_cluster: 'Benessere sociale',
          obiettivo: 'Valutare e promuovere la costituzione di un\'Agenzia per la casa di Ambito, valorizzando il Tavolo ABITARE e costruendo alleanze istituzionali e territoriali per sostenere le persone in fascia grigia nell\'accesso alla casa.',
          target: 'Uffici alloggi dei Comuni dell\'Ambito, proprietari (anche organizzati), cittadini in fascia grigia, agenzie immobiliari, persone con bisogni di affitto temporaneo per lavoro.',
          target_cluster: ['Adulti'],
          focus_disabilita: false,
          obiettiviDaRaggiungere: [
            'Conoscere buone prassi di Agenzie per la casa.',
            'Interloquire con il Comune di Monza sull\'Agenzia in apertura e sulle sue peculiarità.',
            'Costruire alleanze tra Tavolo ABITARE, Uffici alloggi e altri attori territoriali.',
            'Favorire la trasformazione dell\'Agenzia comunale in Agenzia di Ambito.',
          ],
          azioni: [
            'Individuazione di almeno 2-3 buone prassi e organizzazione di eventi laboratoriali di confronto.',
            'Incontri tra Tavolo ABITARE e Agenzia per la casa del Comune di Monza con coinvolgimento referenti di Brugherio e Villasanta.',
            'Costruzione di alleanze con Uffici alloggi e aggiornamento periodico dell\'Assemblea dei Sindaci.',
            'Predisposizione di un piano di collaborazione Tavolo ABITARE - Agenzia per la casa del Comune di Monza.',
          ],
          risorse: [
            'Nessuna risorsa economica preventivata in questa fase.',
            'Almeno un operatore/volontario per ciascun partner della rete.',
          ],
          partners: [
            'Comuni dell\'Ambito (Monza, Brugherio, Villasanta)',
            'Tavolo ABITARE',
            'Uffici alloggi e settori comunali (casa, patrimonio, contabile, tecnici)',
            'Associazioni, cooperative sociali, consorzi, sindacati, Caritas',
          ],
          bisogniPdf: [
            'Uniformare l\'offerta delle politiche abitative',
            'Costruire alleanze con enti che si occupano di abitare',
            'Rafforzare la legittimazione del Tavolo ABITARE',
            'Promuovere competenze sulle politiche abitative',
            'Conoscere modalità innovative di intervento',
          ],
          inputPdf: [
            'Operatori/volontari del tavolo',
            'Progettualità già in atto',
            'Settori comunali competenti',
            'Altri tavoli di partecipazione del Piano di Zona',
          ],
          modalitaOperativePdf: [
            'Analisi di 2-3 buone prassi e incontri laboratoriali con esperienze innovative.',
            'Programmazione incontri Tavolo ABITARE - Agenzia casa del Comune di Monza.',
            'Definizione di modalità di collaborazione reciproca e presentazione all\'Assemblea dei Sindaci.',
          ],
          coinvolgimentoASST: false,
          nuovoServizio: true,
          areePolicy: [
            'B) Politiche abitative',
          ],
          indicatoriOutput: [
            'Almeno 2 esperienze innovative individuate',
            'Almeno 2 incontri laboratoriali effettuati',
            'Almeno 10 partecipanti a ciascun incontro laboratoriale',
            'Almeno 2 incontri tra Agenzia comunale e Tavolo ABITARE',
            'Data di presentazione del piano di collaborazione all\'Assemblea dei Sindaci',
          ],
          indicatoriOutcome: [
            'Attivazione di una governance unitaria di Ambito sull\'abitare.',
            'Migliore accesso alla casa per le persone in fascia grigia e contrasto a povertà/emarginazione abitativa.',
          ],
          contenutoIntegralePdf: `Sezione PDF: TITOLO INTERVENTO "Agenzia per la casa dell'Ambito di Monza: fattibilità".
Contenuti inclusi: obiettivi, azioni, target, risorse, policy, coinvolgimento rete territoriale, bisogni/input, modalità operative, indicatori di processo, output e impatto atteso.`,
        },
        {
          id: 'sportello-casa-riconnettere-esistente',
          titolo: 'Sportello per la Casa. Riconnettere l\'esistente',
          obiettivo_cluster: 'Benessere sociale',
          obiettivo: 'Riconnettere risorse e sportelli territoriali sull\'abitare in un sistema coeso, accessibile e orientato alle persone con vulnerabilità, migliorando divulgazione delle informazioni e integrazione con i servizi socio-sanitari.',
          target: 'Operatori dei servizi, persone con vulnerabilità abitative, ETS impegnati sul tema casa, organizzazioni sindacali.',
          target_cluster: ['Adulti'],
          focus_disabilita: false,
          obiettiviDaRaggiungere: [
            'Strutturare un sistema unitario di supporto all\'abitare.',
            'Conoscere e valorizzare misure/risorse pubbliche, del privato sociale e del privato.',
            'Ridurre l\'onere burocratico per l\'accesso alle misure.',
            'Rendere accessibili le informazioni anche tramite strumenti digitali.',
            'Coinvolgere i servizi socio-sanitari nelle azioni del tavolo.',
          ],
          azioni: [
            'Rilevazione risorse territoriali e costruzione elenco risorse (azione trasversale).',
            'Rilevazione sportelli di informazione/orientamento accessibili alle famiglie (azione trasversale).',
            'Costituzione tavolo di confronto tra operatori degli sportelli.',
            'Predisposizione piano di divulgazione delle informazioni.',
            'Ricerca finanziamenti per tenuta rete sportelli e acquisto strumento digitale.',
            'Sensibilizzazione dei servizi socio-sanitari per partecipazione attiva.',
          ],
          risorse: [
            'Reperimento risorse per copertura dell\'operatore a tenuta del tavolo.',
            'Ammontare da definire successivamente.',
            'Almeno un operatore/volontario per ciascun partner della rete.',
          ],
          partners: [
            'Tavolo ABITARE',
            'ATS/ASST/IRCCS (coinvolgimento auspicato e da consolidare)',
            'Sportelli di prossimità e servizi territoriali',
            'Reti territoriali, associazioni, cooperative, consorzi, sindacati, Caritas',
          ],
          bisogniPdf: [
            'Conoscere le risorse e sostenere le famiglie nell\'accesso',
            'Ricomporre la frammentazione informativa e organizzativa',
            'Rendere note le risorse per politiche abitative',
            'Trovare finanziamenti per la tenuta della rete sportelli',
            'Coinvolgere servizi socio-sanitari',
          ],
          inputPdf: [
            'Molteplici sportelli di prossimità già attivi',
            'Operatori specializzati e volontari',
          ],
          modalitaOperativePdf: [
            'Rilevazione risorse e sportelli attivi con elenco strutturato.',
            'Individuazione referenti ATS/ASST/IRCCS e incontri di sensibilizzazione.',
            'Costituzione tavolo sportelli per confronto, circolarità informazioni e divulgazione.',
            'Predisposizione piano accessibile di divulgazione e ricerca canali finanziamento.',
          ],
          coinvolgimentoASST: true,
          nuovoServizio: true,
          areePolicy: [
            'A) Contrasto alla povertà e all\'emarginazione sociale e promozione dell\'inclusione attiva',
            'B) Politiche abitative',
          ],
          indicatoriOutput: [
            'Rilevazione di almeno 10 sportelli attivi',
            'Almeno 2 incontri con ATS/ASST/IRCCS e Tavolo ABITARE',
            'Almeno 1 canale di finanziamento individuato',
            'Almeno 2 incontri annui del tavolo sportelli',
            'Almeno 6 partecipanti al tavolo di confronto',
            'Predisposizione piano di divulgazione entro fine 2026',
          ],
          indicatoriOutcome: [
            'Aumento dei canali di divulgazione delle informazioni ai cittadini sul tema abitare.',
            'Migliore orientamento e accesso alle risorse abitative territoriali.',
          ],
          contenutoIntegralePdf: `Sezione PDF: TITOLO INTERVENTO "Sportello per la Casa. Riconnettere l'esistente".
Contenuti inclusi: obiettivi, azioni programmate, target, risorse, integrazione policy, coinvolgimento ASST/IRCCS, bisogni/input, aspetti di digitalizzazione, modalità operative, indicatori, output e impatto atteso.`,
        },
        {
          id: 'beni-servizi-condivisi-cohousing',
          titolo: 'Promozione di beni/servizi condivisi e di co-housing',
          obiettivo_cluster: 'Benessere sociale',
          obiettivo: 'Promuovere beni/servizi condivisi e co-housing come soluzioni abitative sostenibili ad alto valore sociale, diffondendo conoscenze, sensibilizzando la cittadinanza e attivando microsperimentazioni territoriali.',
          target: 'Abitanti in cerca di soluzioni ad alto valore sociale, cittadini interessati alla condivisione, proprietari e gestori di alloggi, operatori pubblici/privato sociale/volontariato.',
          target_cluster: ['Adulti', 'Anziani'],
          focus_disabilita: false,
          obiettiviDaRaggiungere: [
            'Conoscere esperienze e buone prassi territoriali ed extra-territoriali.',
            'Monitorare sperimentazioni in corso o in avvio nell\'Ambito di Monza.',
            'Attivare azioni di comunicazione e sensibilizzazione della cittadinanza.',
            'Potenziare esperienze esistenti e avviare microsperimentazioni di co-housing.',
            'Reperire risorse economiche dedicate.',
          ],
          azioni: [
            'Analisi di buone prassi e monitoraggio esperienze locali (es. Oroburo, Corte di S. Albino, Cederna, ecc.).',
            'Comunicazione/sensibilizzazione sul tema condivisione e co-housing.',
            'Individuazione contesti abitativi per nuove esperienze condivise.',
            'Elaborazione microsperimentazione di co-housing di Ambito e ricerca fondi.',
          ],
          risorse: [
            'Risorse da reperire per sensibilizzazione/comunicazione e microsperimentazioni.',
            'Ammontare da definire successivamente.',
            'Almeno un operatore/volontario per ciascun partner della rete.',
          ],
          partners: [
            'Tavolo ABITARE',
            'Operatori pubblici, privato sociale e volontariato',
            'Associazioni, cooperative, consorzi, sindacati, Caritas',
            'Proprietari e soggetti gestori di alloggi',
          ],
          bisogniPdf: [
            'Promuovere reciprocità e attivazione delle persone nella vita quotidiana',
            'Accrescere competenze di operatori/volontari sul tema',
            'Diffondere i benefici sociali di beni/servizi condivisi e co-housing',
            'Potenziare esperienze in corso e reperire risorse',
          ],
          inputPdf: [
            'Operatori specializzati/volontari',
            'Esperienze sperimentali in corso o in attivazione',
          ],
          modalitaOperativePdf: [
            'Individuazione/analisi esperienze pilota fuori Ambito.',
            'Monitoraggio progettualità sperimentali locali con referenti progetto.',
            'Definizione strumenti comunicativi e attivazione contesti per nuove esperienze condivise.',
            'Progettazione microsperimentazione co-housing e ricerca canali di finanziamento.',
          ],
          coinvolgimentoASST: false,
          nuovoServizio: true,
          areePolicy: [
            'A) Contrasto alla povertà e all\'emarginazione sociale e promozione dell\'inclusione attiva',
            'B) Politiche abitative',
          ],
          indicatoriOutput: [
            'Almeno 3 esperienze pilota analizzate',
            'Almeno 5 esperienze mappate sul territorio d\'Ambito',
            'Almeno 10 eventi di sensibilizzazione/comunicazione',
            'Almeno 2 esperienze di beni/servizi condivisi attivate',
            'Almeno una fonte di finanziamento per microsperimentazione co-housing',
            'Almeno una fonte di finanziamento per eventi comunicativi',
          ],
          indicatoriOutcome: [
            'Aumento delle esperienze di vicinanza solidale tra famiglie.',
            'Riduzione costi/sprechi e miglioramento qualità della vita attraverso economie di scala e solidarietà.',
          ],
          contenutoIntegralePdf: `Sezione PDF: TITOLO INTERVENTO "Promozione di beni/servizi condivisi e di co-housing".
Contenuti inclusi: obiettivi, azioni, target, risorse, policy integrate, governance, bisogni/input, modalità operative, indicatori di processo, output e impatto atteso.`,
        },
        {
          id: 'consolidamento-dialogo-sociale-abitare',
          titolo: 'Consolidamento del dialogo sociale',
          obiettivo_cluster: 'Benessere sociale',
          obiettivo: 'Istituzionalizzare il Tavolo ABITARE e consolidare la concertazione delle politiche abitative tra Comuni, stakeholder e parti sociali, fino alla redazione e approvazione del Piano Triennale dell\'Offerta Abitativa di Ambito.',
          target: 'Assessorati e uffici tecnici/politici dei Comuni, rappresentanze, parti sociali, enti del Terzo Settore, soggetti privati.',
          target_cluster: ['Adulti'],
          focus_disabilita: false,
          obiettiviDaRaggiungere: [
            'Istituzionalizzare il Tavolo ABITARE come sede stabile di governance.',
            'Migliorare concertazione tra assessorati competenti e stakeholder.',
            'Costruire contributi condivisi per il Piano Triennale dell\'Offerta Abitativa.',
            'Approvare gli obiettivi strategici e il Piano Triennale in Assemblea dei Sindaci.',
          ],
          azioni: [
            'Incontri periodici del Tavolo ABITARE per tenuta obiettivi strategici.',
            'Coinvolgimento assessorati edilizia/urbanistica, anagrafe, patrimonio e politiche abitative dei Comuni.',
            'Coinvolgimento stakeholder territoriali e raccolta contributi su temi abitativi.',
            'Redazione bozza Piano Triennale (contestualizzazione, analisi piano vigente, punti nodali, confronto).',
            'Presentazione e approvazione finale del Piano in Assemblea dei Sindaci.',
          ],
          risorse: [
            'Nessuna risorsa economica preventivata in questa fase.',
            'Almeno un operatore/volontario per ciascun partner della rete.',
          ],
          partners: [
            'Tavolo ABITARE',
            'Assessorati e uffici comunali dell\'Ambito',
            'Rappresentanze, parti sociali, ETS e soggetti privati',
            'Reti territoriali, associazioni, cooperative, consorzi, sindacati, Caritas',
          ],
          bisogniPdf: [
            'Ricomporre risorse tecnico-politiche e reti territoriali',
            'Orientare politiche abitative più efficaci',
            'Predisporre un Piano Triennale condiviso dell\'offerta abitativa',
          ],
          inputPdf: [
            'Stakeholder competenti (Comuni, rappresentanze, parti sociali, ETS, privato)',
            'Tavolo ABITARE di Ambito',
            'Assessorato politiche abitative del Comune di Monza',
          ],
          modalitaOperativePdf: [
            'Illustrazione e approvazione obiettivi strategici del Tavolo ABITARE in Assemblea Sindaci.',
            'Pianificazione incontri periodici (bimestrali/trimestrali) e monitoraggio obiettivi.',
            'Incontri con assessorati e stakeholder per coinvolgimento e conoscenza reciproca.',
            'Redazione, presentazione e approvazione del Piano Triennale dell\'Offerta Abitativa.',
          ],
          coinvolgimentoASST: false,
          nuovoServizio: true,
          areePolicy: [
            'A) Contrasto alla povertà e all\'emarginazione sociale e promozione dell\'inclusione attiva',
            'B) Politiche abitative',
          ],
          indicatoriOutput: [
            'Almeno 1 incontro ogni 3 mesi del Tavolo ABITARE',
            'Almeno 3 incontri complessivi',
            'Almeno 5 altri stakeholder individuati',
            'Almeno 2 incontri con stakeholder individuati',
            'Approvazione Piano Triennale dell\'Offerta Abitativa entro 31/12/2025',
          ],
          indicatoriOutcome: [
            'Messa in rete stabile degli attori delle politiche abitative.',
            'Miglioramento governance e qualità delle politiche abitative di Ambito.',
          ],
          contenutoIntegralePdf: `Sezione PDF: TITOLO INTERVENTO "Consolidamento del dialogo sociale".
Contenuti inclusi: obiettivi, azioni, target, risorse, policy integrate, coinvolgimento rete, bisogni/input, modalità operative, indicatori di processo, output e impatto atteso.`,
        },
      ],
    },
    {
      id: 'agio-e-benessere',
      titolo: 'Agio e Benessere',
      colore: '#22c55e',
      icona: '🌱',
      schede: [
        {
          id: 'spazi-intergenerazionali-interculturali',
          titolo: 'Spazi intergenerazionali e interculturali ad alta integrazione',
          obiettivo_cluster: 'Benessere sociale',
          obiettivo: 'Favorire lo sviluppo e il potenziamento di luoghi intergenerazionali e interculturali integrati, promuovendo invecchiamento attivo, protagonismo giovanile, inclusione delle persone con disabilità e percorsi di educazione digitale intergenerazionale.',
          target: 'Giovani, adulti, migranti, anziani e comunità nel suo complesso.',
          target_cluster: ['Minori', 'Adulti', 'Anziani'],
          focus_disabilita: false,
          obiettiviDaRaggiungere: [
            'Conoscere e integrare gli spazi intergenerazionali/interculturali già attivi.',
            'Coinvolgere i referenti delle esperienze esistenti nel Tavolo Agio e Benessere.',
            'Creare e potenziare luoghi intergenerazionali/interculturali sul territorio.',
            'Realizzare corsi di formazione digitale intergenerazionale.',
          ],
          azioni: [
            'Rilevazione degli spazi e luoghi intergenerazionali/interculturali attivi.',
            'Incontro con referenti delle esperienze in corso e coinvolgimento nel percorso.',
            'Predisposizione progetto di creazione/potenziamento spazi e corsi digitali intergenerazionali.',
            'Reperimento canali di finanziamento e attivazione nuove esperienze.',
          ],
          risorse: [
            'Risorse da reperire tramite finanziamenti dedicati.',
            'Quantificazione economica da definire in seguito.',
            'Almeno un operatore/volontario per ciascun partner della rete.',
          ],
          partners: [
            'Reti territoriali (Tiki Taka, Gaci, Monza.con)',
            'Tavoli di lavoro territoriali',
            'Associazioni, organizzazioni di volontariato e sindacali',
            'Cooperative sociali, consorzi, AFOL MB, Caritas e Generazione Senior',
          ],
          bisogniPdf: [
            'Valorizzare occasioni di incontro intergenerazionale e multiculturale',
            'Potenziare luoghi di confronto e crescita reciproca',
            'Valorizzare competenze reciproche come opportunità di apprendimento',
          ],
          inputPdf: [
            'Persone specializzate',
            'Associazionismo',
            'Giovani, anziani e persone in interscambio',
            'Esperienze intergenerazionali/interculturali già in atto',
          ],
          modalitaOperativePdf: [
            'Mappatura spazi/luoghi attivi.',
            'Incontri con referenti esperienze in corso.',
            'Progetto di potenziamento con tavolo dedicato e canali di finanziamento.',
            'Attivazione di nuove esperienze intergenerazionali/interculturali.',
          ],
          coinvolgimentoASST: true,
          nuovoServizio: true,
          areePolicy: [
            'A) Contrasto alla povertà e all\'emarginazione sociale e promozione dell\'inclusione attiva',
            'E) Anziani',
            'G) Politiche giovanili e per i minori',
            'J) Interventi a favore di persone con disabilità',
          ],
          indicatoriOutput: [
            'Almeno 10 esperienze attive mappate',
            'Almeno 1 incontro con referenti esperienze attive',
            '5 componenti del tavolo di progetto',
            'Data approvazione progetto di potenziamento',
            'Almeno 1 canale di finanziamento individuato',
            'Almeno 2 esperienze intergenerazionali/interculturali attivate',
          ],
          indicatoriOutcome: [
            'Intensificazione delle relazioni intergenerazionali e interculturali.',
          ],
          contenutoIntegralePdf: `Sezione PDF: TITOLO INTERVENTO "Spazi intergenerazionali e interculturali ad alta integrazione".
Contenuti inclusi: obiettivi, azioni programmate, target, risorse economiche/personale, integrazione con aree di policy, coinvolgimento ASST/IRCCS, rete territoriale, bisogni/input, aspetti digitali, modalità operative, indicatori di processo, output e impatto atteso.`,
        },
        {
          id: 'valorizzazione-operatori-prossimita',
          titolo: 'Valorizzazione degli operatori/volontari prossimi ai cittadini',
          obiettivo_cluster: 'Benessere sociale',
          obiettivo: 'Valorizzare e mettere in rete le esperienze di prossimità per intercettare precocemente i bisogni, rafforzando le competenze di operatori/volontari e attivando nuovi contesti di vicinanza al cittadino.',
          target: 'Organizzazioni e associazioni, servizi territoriali, comunità nel complesso.',
          target_cluster: ['Adulti'],
          focus_disabilita: false,
          obiettiviDaRaggiungere: [
            'Intercettare il bisogno in ottica preventiva.',
            'Conoscere e raccordare le esperienze di prossimità presenti sul territorio.',
            'Qualificare operatori/volontari con percorsi formativi dedicati.',
            'Attivare nuovi spazi/opportunità di prossimità.',
          ],
          azioni: [
            'Mappatura esperienze di prossimità al cittadino (azione trasversale).',
            'Raccordo e messa in rete delle esperienze attive.',
            'Formazione di operatori/volontari per potenziamento e nuove attivazioni (azione trasversale).',
            'Ricerca canali di finanziamento idonei (azione trasversale).',
          ],
          risorse: [
            'Nessun costo monetario previsto, salvo formazione.',
            'Almeno un operatore/volontario per ciascun partner della rete.',
          ],
          partners: [
            'Reti territoriali (Tiki Taka, Gaci, Monza.con)',
            'Associazioni, volontariato, cooperative, consorzi',
            'Organizzazioni sindacali, AFOL MB, Caritas, Generazione Senior',
          ],
          bisogniPdf: [
            'Valorizzare esperienze attive strategiche per avvicinare cittadino e servizi',
            'Fare rete tra esperienze di prossimità',
            'Qualificare operatori/volontari',
            'Garantire risorse e maggiore accessibilità alle informazioni',
          ],
          inputPdf: [
            'Personale specializzato',
            'Volontari',
          ],
          modalitaOperativePdf: [
            'Conoscenza esperienze già attive e collegamento con mappatura AFA/EFS Urban Health.',
            'Definizione piano formativo e nuovi contesti di prossimità.',
            'Reperimento finanziamenti e attivazione nuovi spazi.',
          ],
          coinvolgimentoASST: true,
          nuovoServizio: true,
          areePolicy: [
            'A) Contrasto alla povertà e all\'emarginazione sociale e promozione dell\'inclusione attiva',
            'E) Anziani',
          ],
          indicatoriOutput: [
            'Rilevazione di almeno 5 esperienze attive sul territorio',
            'Almeno 3 incontri operatori/volontari',
            'Almeno 1 percorso di formazione',
            'Almeno 10 partecipanti alla formazione',
            'Almeno 2 nuovi contesti di prossimità attivati',
          ],
          indicatoriOutcome: [
            'Intensificazione relazioni/interconnessioni tra attori sociali.',
            'Potenziamento vicinanza al cittadino e intercettazione precoce dei bisogni.',
          ],
          contenutoIntegralePdf: `Sezione PDF: TITOLO INTERVENTO "Valorizzazione degli operatori/volontari prossimi ai cittadini".
Contenuti inclusi: obiettivi, azioni, target, risorse, policy, coinvolgimento ASST/IRCCS, bisogni/input, piano operativo, indicatori, output e impatto atteso.`,
        },
        {
          id: 'piattaforma-digitale-servizi',
          titolo: 'Piattaforma digitale per la mappatura e la ricomposizione dell\'offerta di servizi territoriali',
          obiettivo_cluster: 'Sistema',
          sottotitolo: 'Obiettivo trasversale',
          obiettivo: 'Costituire uno spazio digitale accessibile a operatori e cittadini per conoscere, localizzare e ricomporre l\'offerta territoriale dei servizi, facilitandone l\'accesso e rafforzando la governance di Ambito.',
          target: 'Cittadini, operatori dei servizi, volontari.',
          target_cluster: ['Adulti'],
          focus_disabilita: false,
          obiettiviDaRaggiungere: [
            'Favorire la ricomposizione dell\'offerta territoriale.',
            'Facilitare l\'accesso ai servizi.',
            'Dotare il sistema di uno strumento digitale informativo-organizzativo condiviso.',
          ],
          azioni: [
            'Costituzione tavolo ristretto di progettazione della piattaforma.',
            'Rilevazione esperienze digitali in essere e informazioni da mappare (con altri tavoli PdZ).',
            'Definizione modalità/tempi di raccolta dati e individuazione strumento digitale idoneo.',
            'Quantificazione spesa e reperimento risorse.',
            'Attivazione software e realizzazione eventi informativi/formativi per operatori e cittadini.',
          ],
          risorse: [
            'Risorse Fondo Povertà per attività di conoscenza delle risorse territoriali.',
            'Finanziamenti da reperire per predisposizione piattaforma digitale.',
            'Quantificazione economica da definire in seguito.',
          ],
          partners: [
            'Tavoli di partecipazione al Piano di Zona',
            'Reti territoriali',
            'Operatori/volontari che utilizzano strumenti informatici',
            'SC Comunicazione e Innovazione',
            'ATS/ASST/IRCCS e rete stakeholder territoriale',
          ],
          bisogniPdf: [
            'Far conoscere servizi e attori della rete a operatori e cittadini',
            'Ricomporre informazioni riducendo la frammentazione',
          ],
          inputPdf: [
            'Tavoli di partecipazione PdZ',
            'Reti territoriali',
            'Operatori/volontari',
            'Struttura SC Comunicazione e Innovazione',
          ],
          modalitaOperativePdf: [
            'Costituzione tavolo ristretto e definizione processo completo di mappatura.',
            'Quantificazione spesa e reperimento risorse.',
            'Individuazione gestore/sistema informatico.',
            'Eventi informativi/formativi per accesso alla piattaforma.',
          ],
          coinvolgimentoASST: true,
          nuovoServizio: true,
          areePolicy: [
            'F) Digitalizzazione dei servizi',
            'K) Interventi di sistema per il potenziamento dell\'Ufficio di Piano e il rafforzamento della gestione associata',
          ],
          indicatoriOutput: [
            'Almeno 5 componenti al tavolo ristretto',
            'Almeno 20% servizi/attività censite',
            'Almeno 3 esperienze digitali in atto conosciute',
            'Almeno 50 accessi (operatori/volontari)',
          ],
          indicatoriOutcome: [
            'Accesso diffuso alle risorse territoriali e maggiore garanzia del diritto all\'assistenza sociale.',
          ],
          contenutoIntegralePdf: `Sezione PDF: TITOLO INTERVENTO "Piattaforma digitale per la mappatura e la ricomposizione dell'offerta di servizi territoriali".
Contenuti inclusi: obiettivi trasversali, azioni, target, risorse, policy digitalizzazione/sistema, coinvolgimento ASST/IRCCS, bisogni/input, piano operativo dettagliato, indicatori, output e impatto atteso.`,
        },
        {
          id: 'ricomposizione-sociale-gruppi',
          titolo: 'Iniziative di ricomposizione sociale attraverso lo strumento dei gruppi',
          obiettivo_cluster: 'Benessere sociale',
          obiettivo: 'Sviluppare e ampliare la partecipazione dei cittadini come risorsa di prossimità, ricomponendo gruppi formali/informali e potenziando iniziative comunitarie di supporto reciproco lungo il ciclo di vita.',
          target: 'Associazioni ed ETS, comunità nel complesso, servizi territoriali.',
          target_cluster: ['Adulti'],
          focus_disabilita: false,
          obiettiviDaRaggiungere: [
            'Conoscere realtà e gruppi attivi in ambito socio-assistenziale.',
            'Ricomporre risorse e promuovere corresponsabilità comunitaria.',
            'Promuovere iniziative di prossimità e capacitazione dei cittadini.',
            'Sostenere conciliazione familiare/vita-lavoro e carichi di cura.',
          ],
          azioni: [
            'Mappatura annuale dei gruppi attivi (azione trasversale).',
            'Integrazione con programmi ATS di promozione salute (peer education, gruppi cammino, ecc.).',
            'Incontri tra gruppi formali/informali per ricomposizione risorse (azione trasversale).',
            'Organizzazione iniziative sperimentali di prossimità e supporto alle fasi di vita/famiglia.',
            'Promozione accesso ai gruppi per utenza servizi sociali/socio-sanitari/sanitari/educativi e cittadinanza.',
            'Attivazione servizi/iniziative a favore di conciliazione familiare e vita-lavoro.',
          ],
          risorse: [
            'Risorse Fondo Povertà per conoscenza risorse territoriali e attivazione operatori sociali di comunità.',
            'Personale degli enti partner.',
          ],
          partners: [
            'Reti territoriali (Tiki Taka, Gaci, Monza.con)',
            'Tavoli di lavoro, associazioni, ETS, volontariato, cooperative, consorzi',
            'Organizzazioni sindacali, AFOL MB, Caritas, Generazione Senior',
            'ATS/ASST/IRCCS',
          ],
          bisogniPdf: [
            'Potenziare conoscenza e valorizzazione risorse comunitarie',
            'Rendere la comunità corresponsabile nel supporto reciproco',
            'Conciliare tempi di vita e sostenere fragilità',
            'Raggiungere persone che non accedono abitualmente ai servizi',
            'Valorizzare volontariato e associazionismo',
          ],
          inputPdf: [
            'Personale specializzato',
            'Associazioni e volontari',
            'Esperienze di prossimità già attive',
            'Operatori sociali di comunità',
            'Centri per la famiglia',
            'Azioni di sistema su servizi ponte (pre/post scuola, sospensioni scolastiche)',
          ],
          modalitaOperativePdf: [
            'Mappatura gruppi e aggiornamento annuale.',
            'Inserimento mappatura nella piattaforma digitale di Ambito (ove attiva).',
            'Incontri tra gruppi e costruzione piano di potenziamento con stakeholder.',
            'Comunicazione/promozione opportunità di incontro reciproco a cittadini e servizi.',
          ],
          coinvolgimentoASST: true,
          nuovoServizio: true,
          areePolicy: [
            'A) Contrasto alla povertà e all\'emarginazione sociale e promozione dell\'inclusione attiva',
            'D) Domiciliarità',
            'F) Digitalizzazione dei servizi',
            'G) Politiche giovanili e per i minori',
            'I) Interventi per la famiglia',
            'J) Interventi a favore della disabilità',
          ],
          indicatoriOutput: [
            '10% in più dei gruppi/risorse conosciute a fine mappatura',
            'Almeno 2 incontri tra gruppi all\'anno',
            'Almeno 2 nuove iniziative di prossimità',
            'Almeno 5 eventi annui di divulgazione',
            'Data approvazione piano di potenziamento iniziative di prossimità',
          ],
          indicatoriOutcome: [
            'Potenziamento della capacità contributiva della comunità in risposta alle necessità della vita quotidiana delle persone.',
          ],
          contenutoIntegralePdf: `Sezione PDF: TITOLO INTERVENTO "Iniziative di ricomposizione sociale attraverso lo strumento dei gruppi".
Contenuti inclusi: obiettivi, azioni (incluse iniziative gruppi/servizi ponte/conciliazione), target, risorse, policy, coinvolgimento ASST/IRCCS, bisogni/input, piano operativo, indicatori di processo, output e impatto atteso.`,
        },
      ],
    },
    {
      id: 'lavoro',
      titolo: 'Lavoro',
      colore: '#f59e0b',
      icona: '💼',
      schede: [
        {
          id: 'comunicazione-integrata-opportunita-lavoro',
          titolo: 'Comunicazione integrata delle opportunità in ambito lavorativo',
          obiettivo_cluster: 'Benessere sociale',
          obiettivo: 'Ricomporre i canali comunicativi sulle opportunità lavorative, favorendo integrazione delle informazioni, maggiore accessibilità ai servizi e diffusione efficace tra domanda e offerta di lavoro.',
          target: 'Persone in entrata nel mercato lavorativo, lavoratori, aziende locali, Centro per l\'Impiego, agenzie interinali, enti di formazione, scuole.',
          target_cluster: ['Adulti'],
          focus_disabilita: false,
          obiettiviDaRaggiungere: [
            'Censire i canali comunicativi esistenti.',
            'Individuare modalità di collaborazione tra enti interessati al tema lavoro.',
            'Integrare le informazioni e definire modalità comunicative efficaci.',
            'Attivare percorsi di formazione per operatori/volontari prossimi al cittadino.',
          ],
          azioni: [
            'Costituzione gruppo di lavoro dedicato alla comunicazione integrata.',
            'Condivisione canali comunicativi e realtà prossime ai cittadini per informazione/orientamento.',
            'Creazione pagina web di ricomposizione informazioni e pubblicazione sui canali stakeholder/social.',
            'Formazione delle realtà prossime al cittadino per la diffusione delle informazioni.',
            'Ricerca e orientamento canali di finanziamento per percorsi formativi.',
          ],
          risorse: [
            'Nessuna risorsa economica prevista, salvo formazione specialistica.',
            'Quantificazione economica da definire in seguito.',
            'Almeno un operatore/volontario per ciascun partner della rete.',
          ],
          partners: [
            'Reti territoriali (Tiki Taka, Monza Ospitalità, Rete Artemide)',
            'Tavoli tematici (es. Tavolo contrasto GAP)',
            'Associazioni, sindacati, consorzi, enti accreditati al lavoro',
            'Azienda Speciale Scuola Paolo Borsa, AFOL MB, Provincia MB, Fondo Diamo Lavoro',
          ],
          bisogniPdf: [
            'Collaborazione tra enti che si occupano di lavoro e fragilità',
            'Ricomposizione canali comunicativi e opportunità lavorative',
            'Diffusione efficace informazioni per incontro domanda/offerta',
          ],
          inputPdf: [
            'Sistemi comunicativi molteplici ma frammentati',
            'Collaborazioni parziali tra enti',
            'Molteplici opportunità lavorative',
            'Personale specializzato, associazionismo, risorse economiche stakeholder',
          ],
          modalitaOperativePdf: [
            'Costituzione gruppo lavoro “Comunicazione integrata opportunità lavoro”.',
            'Condivisione canali esistenti e punti di accesso prossimi al cittadino.',
            'Sviluppo pagina web ricompositiva e formazione degli attori territoriali.',
          ],
          coinvolgimentoASST: false,
          nuovoServizio: false,
          areePolicy: [
            'H) Interventi connessi alle politiche per il lavoro',
            'A) Contrasto alla povertà e all\'emarginazione sociale e promozione dell\'inclusione attiva',
          ],
          indicatoriOutput: [
            'Almeno 3 sistemi di comunicazione attivi coinvolti',
            'Almeno 5 punti di accesso prossimi al cittadino coinvolti',
            'Almeno 30% opportunità di lavoro pubblicizzate sulla pagina web',
            'Data di pubblicazione pagina web',
            'Almeno 1 canale di finanziamento per formazione operatori/volontari',
            'Almeno 1000 accessi annui a pagina web/social stakeholder',
          ],
          indicatoriOutcome: [
            'Aumento della conoscenza delle opportunità di accesso al lavoro.',
            'Contributo alla riduzione della disoccupazione.',
          ],
          contenutoIntegralePdf: `Sezione PDF: TITOLO INTERVENTO "Comunicazione integrata delle opportunità in ambito lavorativo".
Contenuti inclusi: obiettivi, azioni, target, risorse, policy integrate, rete territoriale, bisogni/input, aspetti di digitalizzazione, modalità operative, indicatori di processo, output e impatto atteso.`,
        },
        {
          id: 'ingaggio-aziende-pdz',
          titolo: 'Iniziative finalizzate all\'ingaggio delle aziende nel Piano di Zona',
          obiettivo_cluster: 'Benessere sociale',
          sottotitolo: 'Obiettivo trasversale',
          obiettivo: 'Coinvolgere attivamente le aziende nello sviluppo sociale locale, superando un approccio riparatorio e promuovendo un welfare capacitante, abilitante e corresponsabile nelle politiche del lavoro.',
          target: 'Sottoscrittori del Piano di Zona interessati al dialogo con aziende, aziende attive sul territorio, associazioni datoriali.',
          target_cluster: ['Adulti'],
          focus_disabilita: false,
          obiettiviDaRaggiungere: [
            'Conoscere esperienze già in atto di ingaggio aziende.',
            'Sviluppare una strategia di ingaggio aziendale condivisa.',
            'Costruire un tavolo aziende e iniziative sperimentali con ruolo contributivo delle imprese.',
          ],
          azioni: [
            'Attivazione sottogruppo di lavoro nel tavolo governance lavoro.',
            'Mappatura aziende e definizione modalità/tempi di rilevazione.',
            'Definizione piano strategico di ingaggio aziende e condivisione con Tavolo Lavoro.',
            'Avvio tavolo aziende per definire azioni congiunte.',
          ],
          risorse: [
            'Non si prevedono risorse in denaro dedicate.',
            'Almeno un operatore/volontario per ciascun partner della rete.',
          ],
          partners: [
            'Reti territoriali (Tiki Taka, Monza Ospitalità, Rete Artemide)',
            'Tavoli di lavoro, associazioni, sindacati, consorzi, enti accreditati al lavoro',
            'AFOL MB, Provincia MB, Azienda Speciale Scuola Paolo Borsa',
          ],
          bisogniPdf: [
            'Sensibilizzare su collaborazione aziende-politiche sociali',
            'Collaborare efficacemente con le aziende',
            'Effettuare lettura sistemica di bisogni e contesto',
            'Aumentare inserimenti lavorativi',
          ],
          inputPdf: [
            'Presenza di molteplici aziende sul territorio',
            'Tavolo di governance PdZ sul lavoro con stakeholder competenti',
          ],
          modalitaOperativePdf: [
            'Costituzione sottogruppo tematico con presidio obiettivi.',
            'Mappatura aziende e piano strategico di ingaggio con tempistiche.',
            'Ingaggio aziende e avvio tavolo dedicato per azioni congiunte.',
          ],
          coinvolgimentoASST: false,
          nuovoServizio: false,
          areePolicy: [
            'A) Contrasto alla povertà e all\'emarginazione sociale e promozione dell\'inclusione attiva',
            'G) Politiche giovanili e per i minori',
            'H) Interventi connessi alle politiche per il lavoro',
            'I) Interventi per la famiglia',
            'J) Interventi a favore di persone con disabilità',
          ],
          indicatoriOutput: [
            'Almeno 1 esperienza analizzata',
            'Almeno 10 aziende locali contattate',
            'Data approvazione piano strategico di ingaggio aziende',
            'Data avvio tavolo aziende',
          ],
          indicatoriOutcome: [
            'Valorizzazione della mission sociale delle aziende.',
            'Contributo delle imprese alle risposte ai bisogni sociali territoriali.',
          ],
          contenutoIntegralePdf: `Sezione PDF: TITOLO INTERVENTO "Iniziative finalizzate all'ingaggio delle aziende nel Piano di Zona".
Contenuti inclusi: obiettivi trasversali, azioni, target, risorse, policy, rete territoriale, bisogni/input, modalità operative, indicatori e impatto atteso.`,
        },
        {
          id: 'inserimenti-lavorativi-fragili',
          titolo: 'Inserimenti lavorativi',
          obiettivo_cluster: 'Benessere sociale',
          obiettivo: 'Potenziare inserimenti lavorativi e tirocini, anche per soggetti in condizione di fragilità e nuove fragilità, rafforzando collaborazione con aziende, servizi socio-assistenziali ed enti della rete.',
          target: 'Persone fragili, aziende, Ordine Consulenti del Lavoro, CPI, enti datoriali, SIL, Scuola Paolo Borsa, enti accreditati al lavoro.',
          target_cluster: ['Adulti'],
          focus_disabilita: true,
          obiettiviDaRaggiungere: [
            'Sostenere inserimenti lavorativi di soggetti fragili.',
            'Sostenere aziende nell\'attivazione di tirocini e collocamenti.',
            'Diffondere conoscenza art.18 L.68/1999.',
            'Potenziare risorse e supporti socio-assistenziali/educativi.',
            'Individuare e ingaggiare fascia grigia con percorsi personalizzati.',
          ],
          azioni: [
            'Approfondimento mondo aziende e confronto con aziende pilota.',
            'Sensibilizzazione imprese per attivazione tirocini e collocamenti.',
            'Mappatura servizi socio-assistenziali a supporto persone fragili (azione trasversale).',
            'Promozione uso servizi di supporto per facilitare inserimenti.',
            'Ricerca/potenziamento risorse economiche dedicate e messa in comune risorse rete.',
            'Attività laboratoriale su bisogni della fascia grigia con stakeholder.',
          ],
          risorse: [
            'Fondi comunali SIL',
            'Fondi Casa/Lavoro',
            'Programma GOL',
            'PNRR',
            'Piano provinciale disabili',
            'Progetto SINTESI 4.0',
            'Fondi Povertà per formazione/attività laboratoriale',
          ],
          partners: [
            'ASST/IRCCS (EVM, supporto/monitoraggio/valutazione)',
            'Reti territoriali, tavoli lavoro, associazioni, sindacati, enti accreditati',
            'AFOL MB, Provincia MB, Azienda Speciale Scuola Paolo Borsa, consorzi',
            'Aziende del territorio',
          ],
          bisogniPdf: [
            'Potenziare opportunità di lavoro per soggetti fragili e neofragili',
            'Sviluppare praticantato in azienda propedeutico all\'inserimento',
            'Individuare nuove modalità di sostegno con aziende',
            'Rafforzare incrocio domanda/offerta e risposta fascia grigia',
          ],
          inputPdf: [
            'Esperienza pregressa con soggetti fragili',
            'Personale specialistico multi-professionale',
            'Soggetti rete con competenze specifiche',
            'Aziende con responsabilità sociale',
            'Risorse economiche e servizi socio-assistenziali/educativi',
          ],
          modalitaOperativePdf: [
            'Incontri tavolo-target per approfondire mondo aziende e margini di collaborazione.',
            'Individuazione aziende pilota e obiettivo comune istituzioni-imprese.',
            'Percorso laboratoriale su fascia grigia e modalità di ingaggio.',
            'Quantificazione risorse, ricerca nuovi canali, percorsi personalizzati e attivazione tirocini/inserimenti.',
            'Sensibilizzazione diffusa di altre aziende.',
          ],
          coinvolgimentoASST: true,
          nuovoServizio: false,
          statoServizio: 'potenziamento',
          areePolicy: [
            'A) Contrasto alla povertà e all\'emarginazione sociale e promozione dell\'inclusione attiva',
            'G) Politiche giovanili e per i minori',
            'H) Interventi connessi alle politiche per il lavoro',
            'I) Interventi per la famiglia',
            'J) Interventi a favore di persone con disabilità',
          ],
          indicatoriOutput: [
            'Almeno 20 soggetti fragili collocati in un anno',
            'Almeno 30 aziende contattate',
            'Almeno 5 aziende con collocamenti in obbligo ex art.18',
            'Almeno 2 incontri annui tavolo con aziende',
            'Almeno 100.000,00 euro di risorse a potenziamento nel triennio',
            'Almeno 5 iniziative di sensibilizzazione aziende',
            'Almeno 10 partecipanti attività laboratoriale',
          ],
          indicatoriOutcome: [
            'Aumento inserimenti lavorativi delle persone fragili e miglioramento benessere sociale.',
            'Maggiore corresponsabilità sociale delle aziende.',
          ],
          contenutoIntegralePdf: `Sezione PDF: TITOLO INTERVENTO "Inserimenti lavorativi".
Contenuti inclusi: obiettivi, azioni (incluso art.18, mappatura servizi, fascia grigia), target, risorse, policy integrate, coinvolgimento ASST/IRCCS, bisogni/input, modalità operative, indicatori di processo, output e impatto atteso.`,
        },
        {
          id: 'benessere-lavoratori',
          titolo: 'Promozione del benessere dei lavoratori',
          obiettivo_cluster: 'Benessere sociale',
          obiettivo: 'Promuovere il benessere dei lavoratori attraverso focus group sul disagio lavoro-correlato, gruppi AMA, supporti territoriali e potenziamento dell\'educazione finanziaria tramite formazione e nuovi luoghi di prossimità.',
          target: 'Aziende/organizzazioni sindacali/enti datoriali, lavoratori, Associazione AMA, ambulatorio stress lavoro-correlato, distretto socio-sanitario, centro per la famiglia.',
          target_cluster: ['Adulti'],
          focus_disabilita: false,
          obiettiviDaRaggiungere: [
            'Conoscere bisogni e problematiche connesse al benessere lavorativo.',
            'Attivare gruppi AMA e supporti territoriali dedicati.',
            'Mappare e potenziare sportelli/esperienze di educazione finanziaria.',
            'Formare operatori prossimi al cittadino e attivare nuovi luoghi di prossimità.',
          ],
          azioni: [
            'Avvio tavolo/focus group sul benessere lavoro-correlato.',
            'Coinvolgimento AMA, ambulatorio stress ASST, distretto socio-sanitario e centro famiglia.',
            'Mappatura/potenziamento sportelli educazione finanziaria (azione trasversale).',
            'Formazione operatori prossimi al cittadino su educazione finanziaria (azione trasversale).',
            'Ricerca finanziamenti e attivazione nuovi sportelli/luoghi di prossimità.',
          ],
          risorse: [
            'Costi non ancora quantificabili.',
            'Canali di finanziamento da reperire per sportelli e formazione.',
            'Almeno un operatore/volontario per ciascun partner della rete.',
          ],
          partners: [
            'ASST Brianza (ambulatorio stress lavoro-correlato)',
            'Distretto socio-sanitario e Centro per la famiglia',
            'Associazioni territoriali AMA',
            'Reti territoriali, sindacati, enti accreditati, AFOL MB, Provincia MB',
            'Monza.con e servizi sociali comunali',
          ],
          bisogniPdf: [
            'Mettere al centro benessere dei lavoratori nelle politiche sociali',
            'Supportare lavoratori in disagio lavorativo',
            'Incentivare gruppi AMA come strategia di supporto',
            'Potenziare competenze operatori su gestione risorse finanziarie',
            'Potenziare risorse territoriali sul tema',
          ],
          inputPdf: [
            'Ambulatorio pubblico stress lavoro-correlato',
            'Personale specializzato',
            'Distretto socio-sanitario e Centro per la famiglia',
            'Associazioni AMA territoriali',
            'Servizi/interventi attivi su educazione finanziaria',
          ],
          modalitaOperativePdf: [
            'Avvio focus group per analisi bisogni e problematiche.',
            'Definizione collaborazione con Associazione AMA e strategie di ingaggio lavoratori.',
            'Mappatura sportelli/progetti educazione finanziaria e formazione operatori.',
            'Potenziamento diffuso educazione finanziaria e nuovi luoghi di prossimità.',
          ],
          coinvolgimentoASST: true,
          nuovoServizio: false,
          areePolicy: [
            'A) Contrasto alla povertà e all\'emarginazione sociale e promozione dell\'inclusione attiva',
            'G) Politiche giovanili e per i minori',
            'H) Interventi connessi alle politiche per il lavoro',
            'I) Interventi per la famiglia',
            'J) Interventi a favore di persone con disabilità',
          ],
          indicatoriOutput: [
            'Almeno 10 partecipanti al focus group',
            'Almeno 3 incontri di focus group',
            'Almeno 2 incontri con Associazione AMA',
            'Almeno 2 gruppi AMA annui attivati nel laboratorio',
            'Almeno 10 lavoratori coinvolti nei gruppi AMA',
            'Almeno 2 percorsi annui di formazione su gestione finanziaria familiare',
            'Almeno 10 operatori beneficiari della formazione in un anno',
            'Almeno 4 nuovi sportelli/luoghi di prossimità su gestione finanziaria familiare',
          ],
          indicatoriOutcome: [
            'Rafforzamento della rete sul benessere lavorativo.',
            'Riduzione dell\'abbandono lavorativo.',
          ],
          contenutoIntegralePdf: `Sezione PDF: TITOLO INTERVENTO "Promozione del benessere dei lavoratori".
Contenuti inclusi: obiettivi, azioni (focus group, AMA, educazione finanziaria), target, risorse, policy, coinvolgimento ASST, bisogni/input, modalità operative, indicatori di processo, output e impatto atteso.`,
        },
      ],
    }
  ],
};

// ─── DATI CARATE ─────────────────────────────────────────────────────────────

export const pianoZonaCarate: PianoZona = {
  ambito: 'Carate',
  triennio: '2025–2027',
  descrizione: 'Piano di Zona dell\'Ambito Territoriale Sociale di Carate / Offertasociale',
  ultimoAggiornamento: '2024-12',
  gruppi: [],
};

// ─── DATI SEREGNO ─────────────────────────────────────────────────────────────

export const pianoZonaSeregno: PianoZona = {
  ambito: 'Seregno',
  triennio: '2025–2027',
  descrizione: 'Piano di Zona dell\'Ambito Territoriale Sociale di Seregno / Offertasociale',
  ultimoAggiornamento: '2024-12',
  gruppi: [
    {
      id: 'contrasto-alla-poverta',
      titolo: 'Contrasto alla povertà',
      colore: '#ef4444',
      icona: '🤝',
      schede: [
        {
          id: 'integrare-reti-inclusione-sociale',
          titolo: 'Integrare le reti di inclusione sociale a favore delle persone in condizioni di vulnerabilità',
          obiettivo_cluster: 'Inclusione',
          obiettivo: 'Migliorare la qualità della vita delle persone in condizione di povertà o marginalità (anche estrema), sviluppando una rete pubblica-privata integrata capace di garantire presa in carico multidimensionale, orientamento ai servizi, accompagnamento giuridico e percorsi personalizzati di inclusione sociale.',
          target: 'Persone e nuclei familiari in condizione o a rischio di marginalità e isolamento sociale; persone senza fissa dimora; persone di altre nazionalità.',
          target_cluster: ['Adulti'],
          focus_disabilita: false,
          obiettiviDaRaggiungere: [
            'Accompagnare, nel rispetto delle normative vigenti, le persone senza dimora nell’istruttoria per l’ottenimento della residenza.',
            'Orientare e accompagnare le persone nell’accesso ai servizi sociali, sanitari, legali e di inclusione lavorativa.',
            'Sviluppare una regia pubblica e una rete stabile tra enti pubblici e privati per un’assistenza completa e coordinata.',
            'Sviluppare luoghi sul modello della “Stazione di Posta” e Centri Servizi leggeri per contrasto povertà/marginalità.',
            'Garantire presa in carico integrata con valutazioni multidimensionali e percorsi personalizzati.',
            'Ristabilire legami sociali e favorire integrazione comunitaria, migliorando la qualità della vita delle persone in difficoltà.',
          ],
          azioni: [
            'Presa in carico integrata: valutazione multidimensionale dei bisogni (sociali, sanitari, economici, psicologici), piano personalizzato e monitoraggio periodico dei risultati.',
            'Orientamento ai servizi e accompagnamento giuridico/legale: mappatura servizi socio-assistenziali/sanitari/legali, attivazione sportelli informativi, accompagnamento personalizzato, rete con servizi legali per assistenza gratuita.',
            'Integrazione con soggetti pubblici e privati: tavoli di coordinamento periodici, protocolli d’intesa formali, scambio dati e buone prassi per presa in carico più efficace.',
            'Costituzione di Centri Servizi: individuazione sedi accessibili, definizione offerta su bisogni territoriali, gestione con ETS qualificati.',
            'Accompagnamento nell’istruttoria per la residenza: formazione operatori, semplificazione procedure, supporto individuale, raccordo stretto con uffici anagrafe.',
            'Potenziamento dell’accesso ai servizi anche tramite supporto alle competenze informatiche per prevenire esclusione sociale.',
            'Organizzazione del servizio di Pronto Intervento Sociale comprendente anche MSNA.',
          ],
          risorse: [
            'Spesa media annua: 200.000,00 €.',
            'Fonti: Fondo AdI, Fondo Povertà, FNPS, PNRR, risorse proprie dei Comuni, quote di cofinanziamento ETS da coprogettazioni, donazioni volontarie agli ETS.',
            'Personale dedicato: assistenti sociali, servizi amministrativi/tecnici pubblici (Ufficio di Piano, Servizi Sociali comunali, enti di diritto pubblico), operatori socio-educativi e dei servizi formativi/lavoro in forza agli ETS.',
          ],
          partners: [
            'Ufficio di Piano e Servizi Sociali comunali',
            'Uffici Anagrafe dei Comuni',
            'ASST Brianza',
            'Enti del Terzo Settore',
            'Associazioni territoriali, Parrocchie e Caritas',
            'Servizi sanitari e socio-sanitari',
            'CPI e altri soggetti pubblici/privati del territorio',
          ],
          bisogniPdf: [
            'Dignità e riconoscimento sociale delle persone in povertà.',
            'Autonomia decisionale e progettuale.',
            'Relazioni sociali e contrasto dell’isolamento.',
            'Sicurezza economica, sociale e psicologica.',
            'Realizzazione personale e sviluppo competenze.',
            'Uscita dalla povertà con risposte multidimensionali e integrate.',
          ],
          inputPdf: [
            'Dati socio-demografici (età, genere, istruzione, stato civile, composizione nucleo).',
            'Condizioni economiche (reddito disponibile, spese affitto, debiti, accesso al credito).',
            'Condizioni abitative (tipologia alloggio, servizi di base, sovraffollamento).',
            'Condizioni di salute (stato generale, accesso alle cure, eventuali disabilità).',
            'Condizioni lavorative (occupazione, tipologia contratto, disoccupazione di lunga durata).',
            'Condizioni sociali (isolamento, partecipazione comunitaria, accesso ai servizi).',
          ],
          modalitaOperativePdf: [
            'Fase di ascolto e co-progettazione con utenti e attori territoriali per definire bisogni/intervento.',
            'Fase di implementazione con attivazione servizi, erogazione prestazioni e monitoraggio andamento.',
            'Fase di valutazione con analisi indicatori di processo e risultato.',
            'Potenziamento dell’accompagnamento alla residenza fittizia e dell’orientamento ai servizi.',
            'Integrazione stabile con rete pubblica/privata e sviluppo di Centri Servizi leggeri.',
            'Attivazione in coprogettazione di Stazione di Posta e Pronto Intervento Sociale.',
          ],
          cooperazioneAltriAmbiti: 'Sì: confronto continuo e protocolli per uniformare gli interventi.',
          continuita2021: true,
          nuovoServizio: false,
          statoServizio: 'potenziamento',
          coinvolgimentoASST: true,
          areePolicy: [
            'A) Contrasto alla povertà e all’emarginazione sociale e promozione dell’inclusione attiva',
            'B) Politiche abitative',
            'E) Anziani',
            'H) Interventi connessi alle politiche per il lavoro',
            'I) Interventi per la famiglia',
          ],
          indicatoriOutput: [
            'Numero di persone raggiunte dall’intervento.',
            'Tipologia di bisogni soddisfatti.',
            'Tempo medio di risposta alle richieste.',
            'Grado di soddisfazione degli utenti.',
            'Numero di collaborazioni/partnership attivate.',
            'Numero di attività formative organizzate.',
            'Utilizzo delle tecnologie digitali nell’intervento.',
            'Realizzazione in coprogettazione di una Stazione di Posta: accoglienza diurna continuativa per almeno 5 persone residenti e attività diurne per almeno 10 persone residenti entro 30/03/2026.',
            'Riduzione del numero di persone senza fissa dimora nel triennio (-30% rispetto all’esistente; baseline al 31/08/2024: 180 persone con residenza fittizia nei 10 Comuni di Ambito).',
            'Definizione protocollo tecnico-amministrativo unico servizio sociale/anagrafe di Ambito per ottenimento e gestione residenze fittizie entro 31/03/2026.',
            'Realizzazione in coprogettazione di un servizio di Pronto Intervento Sociale, comprensivo anche dei minori, entro 30/06/2026.',
            'Definizione protocollo operativo workflow per costruzione progetto sociale finalizzato all’accesso ai dispositivi del Fondo Povertà entro 30/06/2025, con implementazione completa entro 30/06/2026.',
          ],
          indicatoriOutcome: [
            'Miglioramento della condizione economica (reddito disponibile, riduzione debito, accesso servizi essenziali).',
            'Miglioramento della condizione abitativa (accesso ad alloggio adeguato, riduzione sovraffollamento).',
            'Miglioramento della salute fisica e mentale (accesso alle cure, riduzione sintomi depressivi/ansiosi).',
            'Aumento dell’autonomia personale (nuove competenze, partecipazione sociale e lavorativa).',
            'Riduzione dell’isolamento sociale e aumento delle relazioni/partecipazione comunitaria.',
            'Miglioramento delle relazioni familiari e riduzione dei comportamenti a rischio.',
            'Riduzione delle disuguaglianze e aumento della coesione sociale.',
            'Miglioramento della percezione di sicurezza nella comunità.',
          ],
          contenutoIntegralePdf: `Sezione PDF: Macroarea "Contrasto alla povertà e all'emarginazione sociale" – obiettivo "Integrare le reti di inclusione sociale a favore delle persone in condizioni di vulnerabilità" (pagg. 164-178 del documento).
Contenuti inclusi: LEPS di riferimento, obiettivi, azioni programmate, target, risorse economiche e di personale, integrazione policy, punti chiave dell’intervento, coinvolgimento ASST (analisi/programmazione/realizzazione), cooperazione con altri Ambiti, continuità con programmazione precedente, coprogrammazione/coprogettazione con ETS, bisogni e input, modelli innovativi, indicatori di processo/output e indicatori di outcome/impatto sociale.`,
        },
      ],
    },
    {
      id: 'abitare',
      titolo: 'Abitare',
      colore: '#10b981',
      icona: '🏘️',
      schede: [
        {
          id: 'diritto-abitazione-accoglienza-abitativa',
          titolo: 'Sostenere il diritto all’abitazione e all’accoglienza abitativa',
          obiettivo_cluster: 'Benessere sociale',
          obiettivo: 'Sviluppare e consolidare una filiera abitativa integrata nell’Ambito di Seregno, rafforzando il ruolo dell’Agenzia dell’Abitare come servizio di orientamento, prevenzione del disagio abitativo, connessione delle risorse per l’emergenza e promozione di soluzioni abitative sostenibili e inclusive, anche per nuove forme di povertà e marginalità sociale.',
          target: 'Persone in condizioni di rischio o marginalità sociale per difficoltà di individuare soluzioni abitative.',
          target_cluster: ['Adulti'],
          focus_disabilita: false,
          obiettiviDaRaggiungere: [
            'Ampliare l’articolazione della domanda/offerta intercettata dal sistema abitativo pubblico, aprendo a nuovi target e tipologie di proprietari.',
            'Sviluppare l’Agenzia dell’Abitare come nuovo servizio di supporto all’orientamento all’abitare nell’Ambito di Seregno.',
            'Promuovere una nuova cultura dell’abitare e strumenti di prevenzione del disagio abitativo, con governance pubblico-privata integrata e connessione stabile con i servizi sociali.',
          ],
          azioni: [
            'Accoglienza e ascolto: creare un ambiente accogliente in cui le persone possano esprimere difficoltà e ricevere supporto.',
            'Orientamento e accompagnamento: guidare le persone verso opportunità/servizi adeguati e sostenerle nel percorso di uscita dalla difficoltà abitativa.',
            'Supporto nella ricerca alloggi e nella gestione delle pratiche burocratiche correlate.',
            'Sviluppo dell’Agenzia dell’Abitare come strumento di prevenzione della grave esclusione abitativa e di mantenimento dell’alloggio/mobilità verso patrimoni più adeguati.',
            'Attivazione di dispositivi di mediazione tra conduttori e locatari e trasformazione dei canoni, anche con sperimentazioni con attori della finanza etica e sociale.',
            'Attivazione di risorse di affiancamento e tutoring nei casi specifici, valorizzando la connessione capillare con le reti locali.',
            'Sviluppo di una cabina unica per la gestione delle emergenze abitative, combinando opportunità pubbliche e del privato sociale con attivazione rapida delle soluzioni.',
            'Costituzione di un Osservatorio sul bisogno abitativo locale con relazioni periodiche e raccolta dati condivisa con i Comuni/Ufficio di Piano.',
            'Supporto allo sviluppo dell’offerta abitativa e di progetti di housing sociale, inclusa definizione di target, modello abitativo e gestione sociale.',
            'Consolidamento della prassi di lavoro in logica di filiera dei servizi abitativi innovativi.',
            'Costruzione database aggiornato alloggi disponibili, promozione servizi Agenzia (web/social/materiali), consulenza/orientamento, supporto stipula contratti (soprattutto canone concordato), collaborazione con Comuni/cooperative/associazioni.',
          ],
          risorse: [
            'Risorse economiche preventivate: 90.000,00 € annui.',
            'Personale dedicato: esperti di politiche abitative con competenze in antropologia, economia civile e no profit, urbanistica sociale, con supporto di consulenze legali specifiche.',
          ],
          partners: [
            'Comuni dell’Ambito Territoriale di Seregno',
            'Ufficio di Piano',
            'Provincia e Regione (in raccordo sulle politiche abitative)',
            'Associazioni di categoria dei proprietari e degli inquilini',
            'Organizzazioni sindacali',
            'Enti del Terzo Settore',
            'Cooperative e associazioni territoriali',
            'Alcune aziende profit coinvolte su specifiche azioni/protocolli',
          ],
          bisogniPdf: [
            'Individuazione di soluzioni abitative efficaci e sostenibili per emergenze abitative e inclusione sociale.',
            'Diritto all’abitare e all’accoglienza abitativa come leva di contrasto alla marginalità.',
            'Miglioramento del sistema di housing sociale e sviluppo dell’Agenzia dell’Abitare di Ambito.',
            'Monitoraggio strutturato di domanda/offerta/accessibilità/qualità/soddisfazione per orientare le decisioni abitative.',
            'Bisogno consolidato nel territorio.',
          ],
          inputPdf: [
            'Indicatori di domanda: numero nuclei familiari, composizione demografica, preferenze abitative, capacità di spesa.',
            'Indicatori di offerta: disponibilità e qualità degli alloggi sul mercato.',
            'Indicatori di accessibilità: facilità di accesso a un alloggio adeguato (fattori economici, sociali, geografici).',
            'Indicatori di qualità dell’abitare: comfort, sicurezza, accessibilità, sostenibilità ambientale.',
            'Indicatori di soddisfazione utenti sui servizi abitativi erogati.',
            'Dati comunali sul fenomeno abitativo, dati raccolti dall’Agenzia (colloqui), fonti statistiche regionali/nazionali.',
          ],
          modalitaOperativePdf: [
            'Approccio integrato e multidisciplinare centrato sulla persona, con personalizzazione degli interventi e promozione dell’autonomia.',
            'Modello housing/housing first con supporti personalizzati e presa in carico continuativa.',
            'Realizzazione interventi tramite identificazione bisogni multidimensionali, ricerca/selezione alloggi, criteri trasparenti di assegnazione, supporto sociale-sanitario-psicologico, monitoraggio continuo.',
            'Organizzazione operativa dell’Agenzia: database alloggi, promozione servizi, orientamento/consulenza, supporto contrattuale, lavoro di rete con altri enti.',
            'Coordinamento delle Agenzie dell’Abitare a livello provinciale e monitoraggio Accordi Locali.',
          ],
          cooperazioneAltriAmbiti: 'Sì: in particolare per il coordinamento delle Agenzie e degli strumenti funzionali allo sviluppo delle politiche abitative.',
          continuita2021: true,
          nuovoServizio: true,
          statoServizio: 'potenziamento',
          coinvolgimentoASST: false,
          areePolicy: [
            'A) Contrasto alla povertà e all’emarginazione sociale e promozione dell’inclusione attiva',
            'B) Politiche abitative',
            'H) Interventi connessi alle politiche per il lavoro',
            'I) Interventi per la famiglia',
            'J) Interventi a favore di persone con disabilità',
            'F) Digitalizzazione dei servizi',
          ],
          indicatoriOutput: [
            'Numero persone servite dai servizi abitativi/Agenzia.',
            'Tempo medio di attesa per un alloggio.',
            'Numero alloggi assegnati.',
            'Numero contratti stipulati (inclusi canoni concordati).',
            'Numero protocolli/accordi di collaborazione stipulati con enti/istituzioni.',
            'Confronto canoni medi praticati rispetto ai canoni previsti dai progetti.',
            'Durata media delle permanenze negli alloggi.',
            'Costo medio per unità abitativa rispetto al previsto progettuale.',
            'Organizzazione di almeno un tavolo annuo con organizzazioni di categoria e OO.SS per monitoraggio Accordo Locale e possibile ricontrattazione al ribasso dei canoni concordati.',
          ],
          indicatoriOutcome: [
            'Riduzione del ricorso a interventi e soluzioni provvisorie/emergenziali e contenimento dei tempi di risposta all’emergenza abitativa.',
            'Contenimento della spesa pubblica sull’emergenza abitativa e riallocazione verso prevenzione del disagio abitativo.',
            'Maggiore coinvolgimento degli attori territoriali a supporto dei progetti abitativi.',
            'Incremento dei contratti a canone concordato nel triennio (+20%; baseline al 31/10/2024: circa 1.500 contratti).',
            'Aumento dei nuclei che si rivolgono all’Agenzia trovando soluzione abitativa (+15% anno 2025, +18% anno 2026, +20% nell’anno successivo; media annua nuclei in cerca di alloggio: circa 200).',
          ],
          contenutoIntegralePdf: `Sezione PDF: Macro Area "Politiche abitative" – obiettivo "Sostenere il diritto all’abitazione e all’accoglienza abitativa" (pagg. 179-190 del documento allegato).
Contenuti inclusi: LEPS connessi, obiettivi, azioni programmate, target, risorse economiche e di personale, trasversalità policy, punti chiave, coinvolgimento ASST, cooperazione con altri Ambiti, continuità/novità del servizio, coprogrammazione/coprogettazione, bisogni/input, modelli innovativi (Agenzia dell’Abitare e Housing/Housing First), modalità organizzative/operative, indicatori di output e outcome/impatto sociale.`,
        },
      ],
    },
    {
      id: 'domiciliarita',
      titolo: 'Domiciliarità',
      colore: '#0ea5e9',
      icona: '🏠',
      schede: [
        {
          id: 'rafforzare-reti-sostegno-caregiver-domicilio',
          titolo: 'Rafforzare e integrare tra loro le reti a sostegno dei caregiver e delle persone assistite al proprio domicilio',
          obiettivo_cluster: 'Inclusione',
          obiettivo: 'Sviluppare e stabilizzare, anche in continuità con la progettualità PNRR M5C2 1.1.3, un modello di domiciliarità integrato sociosanitario nell’Ambito di Seregno per potenziare l’assistenza domiciliare, favorire la presa in carico precoce della fragilità, sostenere i caregiver e ridurre il ricorso all’istituzionalizzazione.',
          target: 'Anziani fragili e persone con disabilità.',
          target_cluster: ['Anziani'],
          focus_disabilita: true,
          obiettiviDaRaggiungere: [
            'Potenziare l’assistenza domiciliare in termini quantitativi e qualitativi, rendendola rispondente ai bisogni specifici e ai LEPS, anche con sviluppo in gestione associata di servizi di custodia sociale.',
            'Individuare precocemente il bisogno e la fragilità iniziale, garantendo presa in carico sociosanitaria per migliorare la qualità della vita e ridurre istituzionalizzazione non necessaria.',
            'Sviluppare continuità assistenziale con un modello organizzativo/gestionale omogeneo di Ambito in raccordo con ASST Brianza.',
            'Sostenere le figure di riferimento della persona fragile, superando una logica solo assistenziale.',
            'Uniformare a livello di Ambito i criteri di presa in carico dei soggetti fragili.',
          ],
          azioni: [
            'Definire un protocollo operativo con ASST Brianza per la gestione integrata delle dimissioni protette in coerenza con le Linee Guida DAP ATS Brianza e gestione associata del servizio.',
            'Sviluppare l’Équipe di Valutazione multiprofessionale tra operatori sanitari e sociali per valutazione aree di bisogno e attivazione servizi necessari al rientro al domicilio.',
            'Definire e attivare il PAI agevolando l’accesso ai dispositivi finanziabili con il Fondo Povertà.',
            'Attivare esperienze di “Badante di Condominio” nell’Ambito Territoriale.',
            'Potenziare i servizi connessi all’assistenza domiciliare con pieno coinvolgimento degli attori nel progetto di vita e supervisione del case manager.',
          ],
          risorse: [
            'Fonti economiche: PNRR 1.1.3, FNPS, risorse comunali.',
            'Operatori del partenariato della coprogettazione PNRR 1.1.3.',
            'Assistenti Sociali area fragilità.',
            'Operatori ASST.',
            'Operatori economici accreditati.',
          ],
          partners: [
            'Ufficio di Piano e Comuni dell’Ambito',
            'ASST Brianza',
            'Operatori del partenariato coprogettazione PNRR 1.1.3',
            'ETS accreditati',
            'Operatori economici accreditati',
            'Caregiver e beneficiari coinvolti nella definizione del progetto di vita',
          ],
          bisogniPdf: [
            'Mancanza di adeguata assistenza domiciliare rispetto ai bisogni.',
            'Necessità di garantire continuità assistenziale e integrazione sociosanitaria.',
            'Bisogno già rilevato e consolidato nel territorio.',
          ],
          inputPdf: [
            'Schede sanitarie e sociali per valutazione multidimensionale congiunta.',
            'Analisi dei bisogni medico-infermieristici e assistenziali.',
            'Dati di presa in carico SAD per monitoraggio annuale.',
            'Strumenti digitali (Cartella Sociale Informatizzata e piattaforme a supporto famiglie).',
          ],
          modalitaOperativePdf: [
            'Coprogettazione e accreditamento come strumenti cardine di governance.',
            'Equipe di valutazione multidimensionale con coinvolgimento famiglie e beneficiari.',
            'Definizione condivisa del PAI e realizzazione del progetto sotto supervisione case manager.',
            'Erogazione degli interventi da parte degli ETS anche tramite dispositivi sostenuti dal Fondo Povertà.',
            'Organizzazione di modalità operative integrate in ottica di gestione associata.',
          ],
          cooperazioneAltriAmbiti: 'Sì: in avvio dall’équipe di valutazione multidimensionale congiunta e integrazione tra componenti sanitarie e sociali.',
          continuita2021: true,
          nuovoServizio: false,
          statoServizio: 'potenziamento',
          coinvolgimentoASST: true,
          areePolicy: [
            'D) Domiciliarità',
            'E) Anziani',
            'I) Interventi per la famiglia',
            'J) Interventi a favore di persone con disabilità',
            'F) Digitalizzazione dei servizi',
            'K) Interventi di sistema per UdP e gestione associata',
          ],
          indicatoriOutput: [
            'Definizione protocollo operativo con ASST Brianza per gestione integrata delle dimissioni protette (DAP ATS Brianza) entro 31/12/2025.',
            'Realizzazione in gestione associata del servizio dimissioni protette in coerenza con Linee Guida DAP ATS Brianza entro 31/03/2026.',
            'Sviluppo in gestione associata di un servizio di custodia sociale che coinvolga almeno altri due Comuni dell’Ambito (oltre agli attuali due) entro 31/03/2026.',
            'Attivazione di almeno un’esperienza di Badante di Condominio nell’Ambito entro 31/03/2026.',
            'Definizione workflow/protocollo per progetto sociale finalizzato all’accesso ai dispositivi Fondo Povertà entro 30/06/2025 e implementazione completa entro 31/12/2026.',
            'Numero prese in carico SAD 2025/2026/2027 maggiore o uguale al numero prese in carico SAD 2024.',
          ],
          indicatoriOutcome: [
            'Miglioramento della continuità assistenziale sociosanitaria nel territorio.',
            'Riduzione del ricorso all’istituzionalizzazione degli anziani non autosufficienti e delle persone fragili.',
            'Rafforzamento dell’autonomia e della domiciliarità tramite presa in carico personalizzata e flessibile.',
            'Potenziamento del supporto ai caregiver e ampliamento dei supporti forniti all’utenza.',
          ],
          contenutoIntegralePdf: `Sezione PDF: Macroarea "Domiciliarità" – obiettivo "Rafforzare e integrare tra loro le reti a sostegno dei caregiver e delle persone assistite al proprio domicilio" (pagg. 191-194 del documento allegato).
Contenuti inclusi: LEPS di riferimento, obiettivi, azioni programmate, target, risorse economiche/personale, policy integrate, punti chiave, coinvolgimento ASST (analisi/programmazione e realizzazione), cooperazione con altri Ambiti, continuità/Sistema, coprogettazione con ETS, bisogni/input, aspetti di digitalizzazione, modalità operative, risultati attesi e indicatori di output/outcome.`,
        },
      ],
    },
    {
      id: 'anziani',
      titolo: 'Anziani',
      colore: '#f59e0b',
      icona: '👵',
      schede: [
        {
          id: 'sinergia-interventi-sociosanitari-anziani-non-autosufficienti',
          titolo: 'Promuovere la sinergia degli interventi socio-sanitari specifici per le persone anziane non autosufficienti, anche in ottica preventiva',
          obiettivo_cluster: 'Inclusione',
          obiettivo: 'Sostenere azioni di protezione e supporto alla domiciliarità delle persone fragili e dei caregiver, creando una rete di collaborazione tra Servizi Sociali, ETS e Case di Comunità, con presa in carico sociosanitaria precoce e integrata.',
          target: 'Anziani e persone con disabilità.',
          target_cluster: ['Anziani'],
          focus_disabilita: true,
          obiettiviDaRaggiungere: [
            'Promuovere l’assistenza delle persone fragili con perdita progressiva di autonomia, tramite intercettazione precoce del bisogno e fragilità iniziale.',
            'Sostenere, in ottica integrata, i PUA delle Case di Comunità.',
            'Potenziare e qualificare il servizio garantito ai nuclei da assistenti familiari, valorizzando reti informali di prossimità e Terzo Settore.',
            'Ridurre il numero dei ricoveri reiterati presso i presidi ospedalieri.',
            'Promuovere un modello organizzativo omogeneo di Ambito per gestione integrata/coordinata degli interventi e uniformare criteri di accesso/valutazione (con riferimento al Fondo Non Autosufficienza).',
            'Sostenere la qualità della vita dei “giovani anziani” e promuovere invecchiamento attivo in attuazione della D.G.R. 2168/15.04.2024 e D.D.U.O. 8782/2024.',
          ],
          azioni: [
            'Incrementare personale di Ambito con risorse ministeriali destinandolo allo sviluppo di PUA (Punti Unici di Accesso) e UVM (Unità di Valutazione Multidimensionale) presso le Case di Comunità.',
            'Supportare le famiglie nella gestione dell’improvvisa perdita di autonomia con attivazione di Sportelli per Assistenti Familiari e corsi di formazione per Assistenti Familiari nell’Ambito.',
            'Offrire servizi a sostegno dell’autonomia tramite specifico accreditamento di enti idonei alla gestione delle risorse B1 e B2 del Fondo Non Autosufficienza per assistenza diretta con interventi sociali integrativi per adulti/anziani in raccordo con ASST Brianza.',
            'Erogare incontri divulgativi e di sensibilizzazione sul decadimento cognitivo.',
            'Proporre attività di gruppo per anziani finalizzate a socializzazione e mantenimento autonomie, anche con spazi di prossimità.',
            'Mantenere la Rete “Fianco a Fianco” connessa alla Volontaria Giurisdizione.',
            'Sviluppare il servizio di Protezione Giuridica in gestione associata nell’Ambito.',
          ],
          risorse: [
            'Fonti economiche: PNRR 1.1.3, FNPS, risorse comunali.',
            'Operatori del partenariato della coprogettazione.',
            'Assistenti Sociali area fragilità, Ufficio di Piano e ASST.',
            'Altri operatori ASST.',
            'Operatori accreditati.',
          ],
          partners: [
            'Ufficio di Piano',
            'Comuni dell’Ambito',
            'ASST Brianza',
            'Case di Comunità',
            'Enti del Terzo Settore',
            'Operatori economici accreditati',
            'Famiglie e caregiver',
          ],
          bisogniPdf: [
            'Necessità di integrare tra loro servizi sanitari, sociali, formali e informali del territorio.',
            'Rinforzo del sostegno ai caregiver e alla domiciliarità.',
            'Miglioramento dell’accessibilità ai servizi sanitari e socio-assistenziali.',
            'Mancanza di occasioni di incontro/socializzazione per anziani fragili e familiari/caregiver.',
            'Insufficiente assistenza domiciliare rispetto al bisogno di continuità assistenziale e integrazione sociosanitaria.',
          ],
          inputPdf: [
            'Presa in carico personalizzata integrata.',
            'Strumenti di long term care, accesso ai servizi e potenziamento servizi sociali.',
            'Azioni LR 15/2015.',
            'Cartella Sociale Informatizzata e piattaforme informatiche per supporto alle famiglie.',
          ],
          modalitaOperativePdf: [
            'Coprogettazione e accreditamento come strumenti organizzativi cardine.',
            'Incontri rivolti ai caregiver co-organizzati e co-condotti da UdP, ETS e ASST.',
            'Sviluppo di equipe di valutazione multidimensionale con coinvolgimento famiglie/beneficiari per definizione congiunta del PAI.',
            'Realizzazione degli interventi sotto supervisione del case manager.',
            'Implementazione di interventi erogati anche tramite voucher con risorse FNA.',
            'Messa in sinergia delle risorse di servizi e territorio per gestione della non autosufficienza.',
          ],
          cooperazioneAltriAmbiti: 'No.',
          continuita2021: true,
          nuovoServizio: false,
          statoServizio: 'potenziamento',
          coinvolgimentoASST: true,
          areePolicy: [
            'D) Domiciliarità',
            'E) Anziani',
            'I) Interventi per la famiglia',
            'J) Interventi a favore di persone con disabilità',
            'F) Digitalizzazione servizi',
            'K) Interventi di sistema per UdP e gestione associata',
          ],
          indicatoriOutput: [
            'Realizzazione, in almeno 3 Comuni, di interventi strutturati e coordinati di Ambito per promuovere invecchiamento attivo (D.G.R. 2168/2024 e D.D.U.O. 8782/2024) entro 31/12/2026.',
            'Incremento/destinazione di personale di Ambito per sviluppo PUA e UVM presso Case di Comunità su protocollo specifico con ASST Brianza entro 31/12/2025.',
            'Attivazione di almeno 2 Sportelli per Assistenti Familiari e almeno 3 corsi di formazione per Assistenti Familiari nell’Ambito.',
            'Realizzazione di specifico accreditamento enti idonei alla gestione risorse B1/B2 Fondo Non Autosufficienza per assistenza diretta con interventi sociali integrativi, in raccordo con ASST Brianza, entro 30/06/2025.',
          ],
          indicatoriOutcome: [
            'Potenziamento dei servizi connessi all’assistenza, al mantenimento e allo sviluppo delle autonomie.',
            'Maggiore sinergia tra risorse territoriali per la gestione della non autosufficienza.',
            'Riduzione isolamento e burn-out dei caregiver con miglior sostegno familiare.',
            'Miglioramento continuità assistenziale e integrazione sociosanitaria.',
          ],
          contenutoIntegralePdf: `Sezione PDF (copertina esclusa): Macro Area "Anziani" – obiettivo "Promuovere la sinergia degli interventi socio-sanitari specifici per le persone anziane non autosufficienti, anche in ottica preventiva" (pagg. 195-198 della numerazione interna).
Contenuti inclusi: LEPS di riferimento, obiettivi e azioni programmate, target, risorse economiche/personale, integrazione policy, punti chiave, coinvolgimento ASST, continuità/potenziamento, coprogrammazione/coprogettazione, bisogni/input, aspetti di digitalizzazione, modalità organizzative/operative, indicatori di output e outcome/impatto.`,
        },
      ],
    },
    {
      id: 'digitalizzazione',
      titolo: 'Digitalizzazione',
      colore: '#06b6d4',
      icona: '💻',
      schede: [
        {
          id: 'transizione-digitale-servizi-cittadinanza-procedure',
          titolo: 'Transizione digitale dei servizi rivolti alla cittadinanza e delle procedure interne',
          obiettivo_cluster: 'Sistema',
          obiettivo: 'Implementare la digitalizzazione dei servizi e dell’accesso, rafforzando il collegamento tra nodi della rete, contrastando povertà/esclusione digitale e potenziando la Cartella Sociale Informatizzata lungo tutto il processo (accesso, orientamento, valutazione, progetto individualizzato, erogazione, valutazione finale, conclusione).',
          target: 'Trasversale (cittadinanza, operatori dei servizi sociali comunali e di Ambito).',
          target_cluster: ['Adulti'],
          focus_disabilita: false,
          obiettiviDaRaggiungere: [
            'Digitalizzazione dell’accesso e dei servizi.',
            'Integrazione e rafforzamento del collegamento tra servizi.',
            'Potenziamento della capacità di programmazione.',
            'Realizzazione di percorsi di alfabetizzazione digitale per contrastare povertà/esclusione digitale (povertà educativa, isolamento anziani).',
            'Potenziamento competenze digitali per favorire ingresso/rientro nel mercato del lavoro.',
            'Investimento nella formazione del personale dei servizi.',
            'Rafforzamento uso Cartella Sociale Informatizzata nei servizi comunali e in gestione associata.',
          ],
          azioni: [
            'Cambio della cartella sociale in uso.',
            'Adozione e implementazione del nuovo prodotto informatico di cartella sociale.',
            'Adeguamento a modifiche normative del settore.',
            'Formazione del personale dei servizi sociali.',
            'Definizione assetto cartella.',
            'Migrazione dei dati già presenti nella precedente cartella.',
            'Sostegno all’utilizzo operativo della nuova cartella.',
            'Gestione progressiva delle istanze tramite Sportello Telematico di Ambito.',
          ],
          risorse: [
            '2% annuo Fondo Povertà, FNPS e/o risorse dei Comuni.',
            'Personale dedicato: operatori UdP e Comuni.',
          ],
          partners: [
            'Ufficio di Piano',
            'Comuni dell’Ambito',
            'Altri Ambiti (cooperazione)',
            'ASST Brianza (interoperabilità auspicata e azioni congiunte in realizzazione)',
          ],
          bisogniPdf: [
            'Migliore organizzazione del lavoro.',
            'Migliore passaggio delle informazioni tra servizi.',
            'Possibilità di monitorare meglio accesso e attività dei servizi.',
            'Contrasto al digital divide della cittadinanza fragile.',
          ],
          inputPdf: [
            'Cartella Sociale Informatizzata (vecchia e nuova) con migrazione dati.',
            'Formazione del personale dei servizi sociali.',
            'Servizio SI (Supporto Informatico) e Punti Digitali.',
            'Sportello Telematico di Ambito per istanze in gestione associata.',
          ],
          modalitaOperativePdf: [
            'Digitalizzazione e accesso digitale ai servizi in gestione associata.',
            'Scelte decisionali supportate da dati empirici raccolti in modo omogeneo e sistematico.',
            'Implementazione progressiva della cartella sociale su tutti i servizi associati e comunali.',
            'Mantenimento di presìdi di supporto digitale per cittadini fragili.',
          ],
          cooperazioneAltriAmbiti: 'Sì, con altri Ambiti.',
          continuita2021: false,
          nuovoServizio: false,
          coinvolgimentoASST: true,
          areePolicy: [
            'F) Digitalizzazione dei servizi',
            'K) Interventi di sistema per il potenziamento dell’Ufficio di Piano e gestione associata',
            'A) Contrasto povertà/esclusione digitale',
            'H) Interventi connessi alle politiche per il lavoro',
          ],
          indicatoriOutput: [
            'Implementazione della nuova Cartella Sociale Informatizzata in tutti i servizi associati entro 31/12/2025.',
            'Implementazione cartelle sociali dei servizi sociali (con referente per Comune) in tutti i Comuni dell’Ambito entro 31/12/2026, funzionale alla successiva Valutazione di Impatto sociale.',
            'Mantenimento fino al 31/12/2027 del servizio SI (Supporto Informatico) e dei Punti Digitali per abbattimento digital divide della cittadinanza fragile.',
            'Gestione di tutte le istanze di servizi/interventi in gestione associata tramite Sportello Telematico di Ambito entro 31/12/2026.',
          ],
          indicatoriOutcome: [
            'Digitalizzazione effettiva e accesso digitale più inclusivo ai servizi.',
            'Maggiore efficacia/efficienza degli interventi sociali.',
            'Migliore capacità programmatoria e decisionale basata su dati omogenei e sistematici.',
            'Riduzione del digital divide per cittadinanza fragile e operatori.',
          ],
          contenutoIntegralePdf: `Sezione PDF: Macroarea "Digitalizzazione dei servizi" – obiettivo "Transizione digitale dei servizi rivolti alla cittadinanza e delle procedure interne" (pagg. 199-201 del documento allegato).
Contenuti inclusi: LEPS di riferimento, obiettivi, azioni programmate, target, risorse economiche/personale, integrazione policy, punti chiave, coinvolgimento ASST, cooperazione con altri Ambiti, continuità/novità, bisogni/input, aspetti digitalizzazione, modalità organizzative/operative, risultati attesi e indicatori output/outcome.`,
        },
      ],
    },
    {
      id: 'lavoro',
      titolo: 'Lavoro',
      colore: '#f59e0b',
      icona: '💼',
      schede: [
        {
          id: 'potenziare-inclusione-lavorativa-fragilita-disabilita',
          titolo: 'Potenziare l’inclusione lavorativa delle persone in condizioni di fragilità e con disabilità',
          obiettivo_cluster: 'Inclusione',
          obiettivo: 'Sviluppare nel triennio un sistema territoriale integrato per l’inclusione socio-lavorativa delle persone più fragili (incluse persone con disabilità, NEET e persone vulnerabili), rafforzando la rete tra servizi sociali, AFOL MB, Provincia, ETS, scuole, aziende e attori sociosanitari.',
          target: 'Persone in stato di disoccupazione, NEET, persone vulnerabili e con disabilità.',
          target_cluster: ['Adulti'],
          focus_disabilita: true,
          obiettiviDaRaggiungere: [
            'Consolidare la Convenzione con Provincia MB/AFOL MB per i servizi di rete per il lavoro, con focus sulle persone più fragili in connessione con i servizi sociali dei 10 Comuni.',
            'Sviluppare e connettere una rete territoriale di servizi funzionali all’inclusione lavorativa (inclusi Centri servizi per la vulnerabilità).',
            'Reingegnerizzare i processi di inclusione lavorativa con coordinamento di Ambito degli enti accreditati (in particolare per disabilità) e messa in connessione delle risorse di Comuni/famiglie.',
            'Promuovere orientamento personalizzato, formazione mirata, supporto alla ricerca attiva del lavoro e sviluppo competenze trasversali.',
            'Contrastare disoccupazione di lunga durata, povertà educativa e inattività, favorendo autonomia economica e attivazione delle persone.',
          ],
          azioni: [
            'Sviluppo della Convenzione con Provincia MB per i servizi di rete per il lavoro tramite AFOL MB.',
            'Connessione degli interventi territoriali per l’inclusione lavorativa con il Servizio Vulnerabilità d’Ambito e con risorse Fondo Povertà.',
            'Nuova ingegnerizzazione del processo con coordinamento d’Ambito degli enti accreditati per inclusione lavorativa disabilità.',
            'Orientamento di base e di secondo livello, consulenza psicologica, accompagnamento alla carriera e tutoraggio tirocini/percorsi formativi.',
            'Supporto all’inserimento lavorativo di persone con disabilità/svantaggio in raccordo con servizi sociali, famiglie e datori di lavoro.',
            'Attivazione di percorsi di inclusione sociale e lavorativa (servizi di prima accoglienza, sostegno, attivazione e inclusione sociale).',
            'Mantenimento sportelli territoriali della rete per il lavoro e potenziamento offerta servizi/utenza raggiunta.',
            'Realizzazione della Valutazione di Impatto Sociale per riprogettazione dei servizi strategici di inclusione socio-lavorativa.',
          ],
          risorse: [
            'Risorse economiche: 500.000,00 € nel triennio.',
            'Risorse umane: personale di enti pubblici e del Terzo Settore con funzioni di orientamento, consulenza, formazione, tutoraggio, supporto all’inserimento lavorativo e gestione amministrativa.',
            'Risorse collegate: Convenzione Provincia MB/AFOL MB e Fondo Povertà.',
          ],
          partners: [
            'Provincia di Monza e Brianza',
            'AFOL MB',
            'Comuni dell’Ambito e Ufficio di Piano',
            'ASST Brianza (su disabilità e disagio psicologico/psichico)',
            'Enti del Terzo Settore',
            'Aziende del territorio',
            'Istituti scolastici e formativi',
            'Organizzazioni sindacali',
          ],
          bisogniPdf: [
            'Inclusione sociale e lavorativa delle persone fragili.',
            'Riduzione del tasso di disoccupazione delle persone fragili (con attenzione anche ai giovani).',
            'Riduzione della quota di persone fragili con bassa o nulla qualifica.',
            'Riduzione del numero di persone con disabilità in cerca di inclusione lavorativa.',
            'Riduzione della durata media della disoccupazione e aumento competenze digitali per l’inclusione lavorativa.',
            'Riduzione della percentuale di persone che non hanno mai lavorato.',
            'Bisogno consolidato.',
          ],
          inputPdf: [
            'Rete servizi lavoro AFOL MB e servizi sociali territoriali.',
            'Sportelli territoriali per il lavoro.',
            'Enti accreditati per inclusione lavorativa e risorse comunali/famiglie.',
            'Centri servizi per vulnerabilità.',
            'Percorsi formativi, tirocini e strumenti di orientamento/intermediazione.',
          ],
          modalitaOperativePdf: [
            'Approccio integrato e multidisciplinare con superamento frammentazione dei servizi.',
            'Presa in carico globale e personalizzata con collaborazione tra professionisti (sociali, sanitari, lavoro, psicologici).',
            'Fasi operative: analisi bisogno/mappatura territorio, progettazione servizi, partenariati, mobilitazione risorse, attivazione e promozione servizi.',
            'Struttura servizi: prima accoglienza, sostegno, attivazione e inclusione sociale.',
            'Utilizzo strumenti digitali per migliorare efficienza, comunicazione inter-servizi e accesso utenti (con sostegni dedicati).',
          ],
          cooperazioneAltriAmbiti: 'Sì: progettualità interambito su persone in uscita da detenzione (Progetto Sintesi), minori sottoposti a procedimenti penali (Progetto Totem), donne vittime di violenza (Rete Artemide), cittadini stranieri (Rete Matrioska).',
          continuita2021: true,
          nuovoServizio: true,
          statoServizio: 'potenziamento',
          coinvolgimentoASST: true,
          areePolicy: [
            'H) Interventi connessi alle politiche per il lavoro',
            'A) Contrasto alla povertà e all’emarginazione sociale e promozione inclusione attiva',
            'I) Interventi per la famiglia',
            'J) Interventi a favore di persone con disabilità',
            'F) Digitalizzazione dei servizi',
            'B) Politiche abitative',
          ],
          indicatoriOutput: [
            'Messa a terra dei servizi di rete per il lavoro previsti dalla Convenzione con Provincia MB/AFOL, con protocolli per persone fragili in connessione con servizi sociali, entro 30/04/2025.',
            'Aumento dei tirocini formativi per persone con disabilità e disagio psichico: +100% rispetto al dato attuale (15 tirocini annui) entro 31/12/2027.',
            'Mantenimento degli sportelli territoriali della rete per il lavoro.',
            'Incremento del numero di persone fragili raggiunte dai servizi.',
            'Incremento della tipologia di servizi erogati.',
            'Incremento del budget di funzionamento dei servizi attraverso risorse esterne ai bilanci comunali.',
            'Verifica della soddisfazione utenti (sondaggi/interviste).',
            'Realizzazione della Valutazione di Impatto Sociale per la riprogettazione dei servizi.',
          ],
          indicatoriOutcome: [
            'Aumento inclusione sociale e lavorativa delle persone fragili.',
            'Riduzione del rischio di povertà ed esclusione sociale.',
            'Aumento competenze personali/professionali e capacità di gestione autonoma della vita.',
            'Miglioramento del benessere soggettivo percepito.',
            'Aumento partecipazione civica e attivazione di nuove reti di collaborazione territoriali.',
            'Creazione di opportunità di sviluppo economico locale.',
          ],
          contenutoIntegralePdf: `Sezione PDF: Macro Area "Interventi connessi alle politiche per il lavoro" – obiettivo "Potenziare l'inclusione lavorativa delle persone in condizioni di fragilità e con disabilità" (pagg. 211-219 del documento allegato).
Contenuti inclusi: LEPS di riferimento, obiettivi e azioni programmate, target, risorse economiche/personale, integrazione policy e punti chiave, coinvolgimento ASST, cooperazione con altri Ambiti, continuità/novità, coprogrammazione/coprogettazione, bisogni/input, aspetti di digitalizzazione, modalità organizzative/operative, risultati attesi e indicatori di output/outcome/impatto.`,
        },
      ],
    },
    {
      id: 'famiglia',
      titolo: 'Famiglia',
      colore: '#6366f1',
      icona: '👨‍👩‍👧‍👦',
      schede: [
        {
          id: 'integrare-risorse-interventi-famiglie',
          titolo: 'Implementare ed integrare risorse e interventi a favore delle famiglie',
          obiettivo_cluster: 'Benessere sociale',
          obiettivo: 'Creare un sistema di protezione e sostegno alle famiglie più efficace ed efficiente, a partire dallo sviluppo del Centro per la Famiglia e dal LEPS P.I.P.P.I., superando la frammentazione dei servizi e garantendo interventi personalizzati e integrati per minori e nuclei vulnerabili.',
          target: 'Famiglie in generale, con particolare riferimento a quelle con figli 0-18 anni residenti nei 10 Comuni dell’Ambito Territoriale e Distrettuale di Seregno.',
          target_cluster: ['Minori'],
          focus_disabilita: false,
          obiettiviDaRaggiungere: [
            'Superare la frammentazione dei servizi e migliorare la governance territoriale.',
            'Individuare e diffondere servizi/interventi rivolti alle famiglie e al sostegno della genitorialità.',
            'Attivare percorsi personalizzati di accompagnamento familiare con équipe multidisciplinari.',
            'Rafforzare prevenzione e protezione dei minori (inclusa prevenzione maltrattamento).',
            'Garantire pronto intervento e risposta coordinata alle emergenze familiari, anche in raccordo con CAV e forze dell’ordine.',
            'Valorizzare risorse locali, volontariato e Terzo Settore nella rete di supporto.',
          ],
          azioni: [
            'Governance e coordinamento: rete territoriale tra servizi sociali, scuole, ASST, associazioni; protocolli operativi condivisi; sistema informativo integrato per monitoraggio.',
            'Accompagnamento personalizzato bambini/famiglie: valutazione multidisciplinare, piani individualizzati, supporto continuativo alla genitorialità.',
            'Prevenzione maltrattamento e promozione genitorialità positiva: campagne, formazione operatori, interventi precoci.',
            'Pronto intervento: squadra multidisciplinare pronta all’intervento in emergenza con supporto forze dell’ordine.',
            'Integrazione Terzo Settore: mappatura risorse locali, coinvolgimento associazioni/volontari, accordi per servizi specifici.',
            'Formazione, aggiornamento e sensibilizzazione: laboratori per genitori, orientamento scolastico e iniziative a supporto della genitorialità.',
            'Sistematizzazione del Centro per la Famiglia con ufficio dedicato (informazione, orientamento, accompagnamento) e tavolo di coordinamento multidisciplinare/intersettoriale.',
          ],
          risorse: [
            'Risorse economiche: 150.000,00 € annui.',
            'Rete professionale multidisciplinare: assistenti sociali, psicologi, educatori, mediatori familiari, operatori legali.',
            'Ulteriori professionalità: pedagogisti, ostetriche, insegnanti, artisti, operatori ludico-sportivi, volontari.',
            'Figure “coach” dedicate all’implementazione del programma PIPPI.',
          ],
          partners: [
            'Ufficio di Piano e Comuni dell’Ambito',
            'ASST Brianza',
            'Scuole e servizi per l’infanzia (Tavolo Coordinamento Pedagogico Territoriale ZeroSei)',
            'Case di Comunità di Giussano, Lentate sul Seveso, Seregno (Psicologia delle Cure Primarie e di Comunità)',
            'Enti del Terzo Settore',
            'CSV (Centro Servizi Volontariato)',
            'CAV Ambito di Seregno / Rete Artemide (Associazione White Mathilda)',
            'Tavolo Territoriale P.I.P.P.I.',
          ],
          bisogniPdf: [
            'Sostegno personalizzato alle famiglie in base ai contesti specifici.',
            'Prevenzione violenza domestica e tutela dei minori.',
            'Conciliazione vita-lavoro (in particolare per le donne).',
            'Allargamento della rete di collaborazione istituzioni–Terzo Settore.',
            'Innovazione dei modelli di governance con partecipazione attiva dei cittadini.',
            'Bisogno consolidato nel territorio.',
          ],
          inputPdf: [
            'Tassi di povertà, disoccupazione, istruzione e immigrazione.',
            'Disponibilità dei servizi territoriali per infanzia/adolescenza/famiglie.',
            'Numero accessi a sportelli informativi e orientamento famiglia.',
            'Tassi di isolamento sociale e livello conoscenza servizi da parte delle famiglie.',
            'Indicatori di conciliazione vita-lavoro e carichi genitoriali.',
          ],
          modalitaOperativePdf: [
            'Approccio sistemico e integrato con Tavolo tecnico interistituzionale permanente e coordinatore di progetto.',
            'Analisi bisogni con raccolta/lettura dati e orientamento delle azioni future.',
            'Percorsi personalizzati, servizi a bassa soglia (sportelli, gruppi auto-mutuo-aiuto), educativa domiciliare, mediazione familiare e pronta risposta.',
            'Monitoraggio continuo con indicatori di processo/output/risultato e rimodulazione interventi in base a criticità emergenti.',
            'Comunicazione multicanale (tradizionale/social) per informazione e coinvolgimento attivo di famiglie e giovani.',
          ],
          cooperazioneAltriAmbiti: 'Sì (coordinamento e scambio su interventi di conciliazione e servizi familiari in rete).',
          continuita2021: true,
          nuovoServizio: true,
          statoServizio: 'potenziamento',
          coinvolgimentoASST: true,
          areePolicy: [
            'I) Interventi per la famiglia',
            'G) Politiche giovanili e per i minori',
            'A) Contrasto alla povertà e all’emarginazione sociale e promozione inclusione attiva',
            'D) Domiciliarità',
            'F) Digitalizzazione dei servizi',
            'J) Interventi a favore di persone con disabilità',
          ],
          indicatoriOutput: [
            'Numero protocolli di collaborazione sottoscritti tra servizi sociali, scuole, ASST e Terzo Settore.',
            'Numero riunioni congiunte tra attori della rete.',
            'Percentuale casi con presa in carico integrata realizzata.',
            'Numero famiglie beneficiarie di piano d’azione individualizzato.',
            'Numero incontri tra operatori e famiglie.',
            'Percentuale famiglie soddisfatte del percorso di accompagnamento.',
            'Numero segnalazioni maltrattamento ricevute/gestite e numero interventi di sostegno alla genitorialità erogati.',
            'Numero interventi di emergenza effettuati e percentuale risposte adeguate in emergenza.',
            'Numero convenzioni con ETS, volontari coinvolti e attività svolte in collaborazione con il Terzo Settore.',
          ],
          indicatoriOutcome: [
            'Miglioramento benessere psicologico/emotivo del bambino e delle competenze relazionali/sociali.',
            'Miglioramento benessere familiare, comunicazione e competenze genitoriali, con riduzione conflitti.',
            'Riduzione maltrattamenti, abusi, abbandono scolastico e comportamenti a rischio tra i giovani.',
            'Aumento integrazione tra servizi e miglioramento efficacia degli interventi (tempi/soddisfazione utenti).',
            'Maggiore capacità del sistema di risposta ai bisogni emergenti e sviluppo nuovi interventi.',
            'Crescita della collaborazione con il Terzo Settore e miglioramento qualità complessiva dei servizi di rete.',
          ],
          contenutoIntegralePdf: `Sezione PDF: Macroarea "Interventi per la Famiglia" – obiettivo "Implementare ed integrare risorse e interventi a favore delle famiglie" (pagg. 220-235 del documento allegato).
Contenuti inclusi: LEPS di riferimento, obiettivi e azioni programmate (Centro per la Famiglia + PIPPI), target, risorse economiche/personale, integrazione policy e punti chiave, coinvolgimento ASST, continuità/potenziamento, coprogrammazione/coprogettazione con ETS, bisogni/input, aspetti di digitalizzazione, modalità organizzative/operative, indicatori output/outcome e impatto atteso.`,
        },
      ],
    },
    {
      id: 'sistema',
      titolo: 'Sistema',
      colore: '#06b6d4',
      icona: '⚙️',
      schede: [
        {
          id: 'coprogrammazione-coprogettazione-cogestione-servizi',
          titolo: 'Dalla coprogrammazione e coprogettazione alla cogestione dei servizi con il Terzo Settore',
          obiettivo_cluster: 'Sistema',
          obiettivo: 'Curare l’applicazione della gestione associata del Piano di Zona dell’Ambito di Seregno e dell’Accordo di Programma con ATS/ASST/Provincia, consolidando la cogestione dei servizi con il Terzo Settore, il potenziamento organizzativo dell’Ufficio di Piano e l’integrazione sociosanitaria, con raggiungimento dei LEPS di riferimento.',
          target: "Tutti gli operatori pubblici e del Terzo Settore coinvolti negli interventi dell'Ambito Territoriale.",
          target_cluster: ['Adulti'],
          focus_disabilita: false,
          obiettiviDaRaggiungere: [
            'Potenziamento e svolgimento della gestione associata dei servizi dei 10 Comuni dell’Ambito.',
            'Passaggio evolutivo dalla coprogrammazione/coprogettazione alla cogestione dei servizi con il Terzo Settore.',
            'Rafforzamento integrazione sociosanitaria con sviluppo PUA, UVM/EVM e azioni congiunte Ambito-ASST.',
            'Garantire il LEPS di supervisione del personale dei servizi sociali.',
            'Implementare strumenti stabili di rendicontazione e Valutazione di Impatto Sociale.',
          ],
          azioni: [
            'Potenziamento dei servizi di base dei Comuni e della componente tecnico-amministrativa in gestione associata.',
            'Percorsi di formazione congiunta per operatori comunali e Ufficio di Piano, valorizzando strategie e buone pratiche.',
            'Sviluppo studio di fattibilità per potenziamento dei servizi in gestione associata.',
            'Attivazione gruppi di lavoro guidati da esperti su: analisi/ridefinizione servizi associati, studio fattibilità impresa sociale, acquisizione metodologia VIS.',
            'Potenziamento integrazione sociosanitaria con incremento/destinazione personale di Ambito su PUA e UVM presso Case di Comunità.',
            'Introduzione rendicontazione annuale stato di avanzamento obiettivi Piano di Zona.',
            'Implementazione stabile della Valutazione di Impatto Sociale acquisita dagli operatori UOT.',
          ],
          risorse: [
            'Risorse da definire con sviluppo delle azioni in sinergia tra fondi nazionali, regionali e comunali.',
            'Personale: dirigenti/funzionari, assistenti sociali, pedagogisti, educatori, psicologi, figure amministrative.',
          ],
          partners: [
            'Comuni dell’Ambito Territoriale di Seregno',
            'Ufficio di Piano',
            'ATS Brianza',
            'ASST Brianza',
            'Provincia MB',
            'Enti del Terzo Settore',
            'UPEL',
            'IFEL',
          ],
          bisogniPdf: [
            'Evoluzione e sostenibilità degli interventi sociali in gestione associata.',
            'Rafforzamento governance interAmbiti e sociosanitaria.',
            'Nuovi assetti organizzativi e giuridico-amministrativi.',
            'Necessità di monitoraggio strutturato degli obiettivi e impatto degli interventi.',
            'Bisogno già affrontato, da consolidare con sviluppo di sistema.',
          ],
          inputPdf: [
            'Convenzione gestione associata del Piano di Zona e Accordo di Programma sociosanitario.',
            'LEPS di riferimento: servizio sociale professionale, supervisione personale, PUA/UVM con incremento operatori sociali, offerta integrata di interventi/servizi.',
            'Piano Operativo Analitico (POA) FNPS per supervisione assistenti sociali.',
            'Dati di avanzamento e rendicontazione annuale dello stato obiettivi PdZ.',
          ],
          modalitaOperativePdf: [
            'Coincidenza tra obiettivi e azioni programmate in ottica di gestione associata.',
            'Coprogrammazione/coprogettazione formalizzate e progressiva cogestione con Terzo Settore.',
            'Gruppi di lavoro tecnici per analisi servizi, ridefinizione organizzativa e metodologia VIS.',
            'Sinergia operativa su integrazione sociosanitaria (PUA, EVM/UVM, azioni congiunte ASST).',
            'Formazione congiunta operatori e valorizzazione buone pratiche tra enti.',
          ],
          cooperazioneAltriAmbiti: 'Sì, per la governance interAmbiti e sociosanitaria.',
          continuita2021: true,
          nuovoServizio: true,
          statoServizio: 'potenziamento',
          coinvolgimentoASST: true,
          areePolicy: [
            'K) Interventi di sistema per il potenziamento dell’Ufficio di Piano e il rafforzamento della gestione associata',
            'F) Digitalizzazione dei servizi',
            'A) Contrasto alla povertà e all’emarginazione sociale e promozione inclusione attiva',
            'D) Domiciliarità',
            'E) Anziani',
            'I) Interventi per la famiglia',
            'J) Interventi a favore di persone con disabilità',
          ],
          indicatoriOutput: [
            'Studio di fattibilità per il potenziamento dei servizi in gestione associata entro 30/06/2025.',
            'Potenziamento integrazione sociosanitaria con incremento/destinazione personale su PUA/UVM presso Case di Comunità: +1 assistente sociale entro 31/12/2025.',
            'Garanzia LEPS supervisione assistenti sociali nel rispetto POA FNPS: almeno 75% assistenti sociali dell’Ambito.',
            'Introduzione dello strumento di rendicontazione annuale dello stato obiettivi PdZ e relativa valutazione.',
            'Implementazione sistema stabile di Valutazione di Impatto Sociale acquisito dagli operatori UOT entro 31/12/2027.',
          ],
          indicatoriOutcome: [
            'Evoluzione e sostenibilità degli interventi sociali.',
            'Migliore qualità della governance e della gestione associata dei servizi.',
            'Maggiore integrazione sociosanitaria e capacità di risposta congiunta Ambito-ASST.',
            'Riduzione del rischio di burn-out professionale del servizio sociale attraverso supervisione strutturata.',
            'Migliore accountability e decisione pubblica data-driven tramite rendicontazione e VIS.',
          ],
          contenutoIntegralePdf: `Sezione PDF: MacroArea "Interventi di sistema per il potenziamento dell'Ufficio di Piano e il rafforzamento della gestione associata" – obiettivo "Dalla coprogrammazione e coprogettazione alla cogestione dei servizi con il Terzo Settore" (pagg. 241-244 del documento allegato).
Contenuti inclusi: LEPS di riferimento, obiettivi e azioni programmate, target, risorse economiche/personale, integrazione policy, coinvolgimento ASST, cooperazione interAmbiti, continuità/novità, coprogrammazione-coprogettazione con ETS, bisogni/input, aspetti digitalizzazione, modalità organizzative/operative, risultati attesi e indicatori output/outcome/impatto.`,
        },
      ],
    },
  ],
};

// ─── DATI DESIO ─────────────────────────────────────────────────────────────

export const pianoZonaDesio: PianoZona = {
  ambito: 'Desio',
  triennio: '2025–2027',
  descrizione: 'Piano di Zona dell\'Ambito Territoriale Sociale di Desio / Offertasociale',
  ultimoAggiornamento: '2024-12',
  gruppi: [
    {
      id: 'PNRR',
      titolo: 'PNRR',
      colore: '#0ea5e9',
      icona: '🏗️',
      schede: [
        {
          id: 'sviluppo-pnrr',
          titolo: 'Sostenibilità Progetti PNRR e nuove fonti',
          obiettivo_cluster: 'Sistema',
          obiettivo: 'Adottare modalità e dispositivi di Ambito in grado di garantire la sostenibilità dei Progetti PNRR al termine del finanziamento e l\'intercettazione di ulteriori fonti da bandi e avvisi.',
          target: 'Target definiti dalle linee PNRR e/o da altre fonti di finanziamento intercettate.',
          target_cluster: ['Adulti', 'Anziani'],
          focus_disabilita: true,
          azioni: [
            'Realizzare le linee PNRR 1.1.1 PIPPI, 1.1.2 Anziani, 1.2 Disabili, 1.1.3 Dimissioni Protette, 1.1.4 Supervisione operatori sociali, 1.3.1 Housing First secondo cronoprogramma.',
            'Promuovere confronto Comuni/Codebri per la sostenibilità post-finanziamento ministeriale.',
            'Adottare dispositivi di Ambito per intercettare ulteriori fondi da bandi e avvisi.',
            'Definire annualmente piano azioni e indicatori di processo.',
          ],
          risorse: [
            'Rateo linee PNRR 2025–2026 (riferimento tabella economica del piano).',
            'Ufficio di Piano, ASC Codebri, Comuni, ETS coinvolti.',
          ],
          partners: ['Ufficio di Piano', 'ASC Codebri', 'Comuni', 'Enti del Terzo Settore'],
          continuita2021: true,
          nuovoServizio: false,
          coinvolgimentoASST: true,
          areePolicy: [
            'Interventi di sistema per potenziamento Ufficio di Piano',
            'Rafforzamento gestione associata',
            'Digitalizzazione dei processi di Ambito',
          ],
        },
      ],
    },
    {
      id: 'abitare',
      titolo: 'Sistema Integrato Abitare',
      colore: '#14b8a6',
      icona: '🏠',
      schede: [
        {
          id: 'housing-temporaneo',
          titolo: 'Sistema integrato dell\'abitare',
          obiettivo_cluster: 'Benessere sociale',
          obiettivo: 'Sviluppare sul territorio dell\'Ambito un Sistema Integrato dell\'Abitare, a diversi livelli di governance, capace di offrire risposte differenziate e sostenibili per target diversi, in integrazione con servizi educativi e politiche del lavoro.',
          target: 'Persone e famiglie in vulnerabilità ed emergenza abitativa.',
          target_cluster: ['Adulti'],
          focus_disabilita: false,
          azioni: [
            'Aumentare l\'offerta abitativa, compatibile con sostenibilità e dati di contesto, anche con soluzioni innovative per l\'autonomia.',
            'Attivare capacity building per operatori sociali in ottica preventiva e di miglioramento dei metodi di lavoro.',
            'Attivare azioni di valutazione dell\'impatto dei progetti/servizi dell\'abitare sul territorio.',
            'Definire annualmente il piano programma delle azioni.',
          ],
          risorse: [
            'Fondi di Ambito (circa 700.000 €/anno) + fondi comunali e DGR.',
            'Agenzia SistemAbitare, Comuni, Ufficio di Piano, ETS.',
          ],
          partners: ['Agenzia SistemAbitare', 'Comuni', 'Ufficio di Piano', 'ETS'],
          continuita2021: true,
          nuovoServizio: false,
          coinvolgimentoASST: true,
          areePolicy: ['Politiche abitative', 'Politiche del lavoro', 'Inclusione sociale'],
        },
      ],
    },
    {
      id: 'giovani',
      titolo: 'Giovani',
      colore: '#f97316',
      icona: '🧑‍🎓',
      schede: [
        {
          id: 'giovani',
          titolo: 'Protagonismo e autonomia giovani',
          obiettivo_cluster: 'Benessere sociale',
          obiettivo: 'Promuovere e sostenere il protagonismo e l\'autonomia delle giovani generazioni, rafforzando le reti sociali e coinvolgendo i giovani nei processi di co-progettazione e co-programmazione.',
          target: 'Giovani dell\'Ambito (con focalizzazione su specifiche fasce d\'età e bisogni).',
          target_cluster: ['Minori', 'Adulti'],
          focus_disabilita: false,
          azioni: [
            'Dare continuità, ampliare e qualificare il Tavolo Giovani di Ambito.',
            'Individuare target specifici nella categoria giovani per interventi più mirati.',
            'Migliorare la comunicazione per aumentare complementarità tra azioni.',
            'Sviluppare mappa digitale di reti, progetti e opportunità per i giovani.',
            'Intercettare risorse di Ambito per eventi culturali/tematici e partecipazione giovanile.',
            'Promuovere messa a disposizione di luoghi comunali dedicati ai giovani.',
          ],
          risorse: ['Fondi di Ambito (circa 35.000 €/anno)', 'Reperimento fondi da bandi'],
          partners: ['Ufficio di Piano', 'Comuni', 'ETS', 'Istituti scolastici'],
          continuita2021: true,
          nuovoServizio: false,
          coinvolgimentoASST: true,
          areePolicy: ['Politiche giovanili', 'Politiche del lavoro', 'Prevenzione disagio'],
        },
      ],
    },
    {
      id: 'non-autosufficienza',
      titolo: 'Non autosufficienza',
      colore: '#ef4444',
      icona: '🫶',
      schede: [
        {
          id: 'welfare-prossimita',
          titolo: 'Accesso alla rete dei servizi per persone fragili',
          obiettivo_cluster: 'Inclusione',
          obiettivo: 'Sostenere a livello di Ambito le persone che affrontano maggiori ostacoli nell\'accesso ai servizi e alle opportunità di benessere, attraverso analisi dinamica dei bisogni e revisione/ampliamento della filiera dei servizi.',
          target: 'Persone fragili di ogni target.',
          target_cluster: ['Adulti', 'Anziani'],
          focus_disabilita: true,
          azioni: [
            'Gestione di Ambito delle misure Fondo Non Autosufficienza e Dopo di Noi.',
            'Dare continuità agli sportelli SI Supporto Informatico.',
            'Verificare adeguatezza di interventi domiciliari e servizi connessi (es. SAD).',
            'Modellizzare interventi di prossimità e sperimentare modelli innovativi.',
            'Programmare annualmente azioni e indicatori di processo.',
          ],
          risorse: [
            'Per sportello SI: circa 37.000 €/anno.',
            'Ufficio di Piano, Comuni, Ambiti di Carate/Seregno/Vimercate, ETS.',
          ],
          partners: ['Ufficio di Piano', 'Comuni', 'Ambiti territoriali', 'ETS'],
          continuita2021: true,
          nuovoServizio: false,
          coinvolgimentoASST: true,
          areePolicy: ['Fragilità', 'Domiciliarità', 'Integrazione sociosanitaria', 'Accesso digitale'],
        },
      ],
    },
    {
      id: 'inclusione',
      titolo: 'Inclusione',
      colore: '#84cc16',
      icona: '💼',
      schede: [
        {
          id: 'vulnerabilita-sociale',
          titolo: 'Programmazione integrata contro povertà e vulnerabilità',
          obiettivo_cluster: 'Inclusione',
          obiettivo: 'Sviluppare una programmazione integrata, trasversale e sostenibile per contrastare povertà e vulnerabilità economica/sociale, con risposte multidimensionali integrate con politiche abitative e del lavoro.',
          target: 'Persone in vulnerabilità economica o beneficiari ADI/SFL.',
          target_cluster: ['Adulti'],
          focus_disabilita: false,
          azioni: [
            'Sviluppo sperimentale del Dispositivo di Ambito in gestione associata dei servizi di inclusione e contrasto povertà.',
            'Promozione di interventi di educazione e supporto finanziario.',
            'Adozione di modalità di sistema integrate e sostenibili tra Ambito, ETS, Codebri ed enti sovraordinati.',
            'Continuità e qualificazione del Tavolo Emarginazione Povertà di Ambito.',
            'Definizione azioni nella pianificazione annuale.',
          ],
          risorse: [
            'Sviluppo Dispositivo di Ambito: circa 766.000 €/anno (QSFP).',
            'ASC Codebri, Comuni, Ufficio di Piano.',
          ],
          partners: ['ASC Codebri', 'Comuni', 'Ufficio di Piano', 'ETS'],
          continuita2021: true,
          nuovoServizio: false,
          coinvolgimentoASST: true,
          areePolicy: ['Contrasto povertà', 'Politiche abitative', 'Politiche del lavoro'],
        },
      ],
    },
    {
      id: 'digitalizzazione',
      titolo: 'Digitalizzazione',
      colore: '#06b6d4',
      icona: '⚙️',
      schede: [
        {
          id: 'rafforzamento-ufficio-piano',
          titolo: 'Qualificazione del sistema di Ambito',
          obiettivo_cluster: 'Sistema',
          obiettivo: 'Adottare approcci, strumenti e modalità di ingaggio/lavoro organiche e chiare a livello di Ambito, finalizzate alla qualificazione ed efficientamento del sistema.',
          target: 'Comuni dell\'Ambito, Ufficio di Piano, ETS, ASC Codebri.',
          target_cluster: ['Adulti'],
          focus_disabilita: false,
          azioni: [
            'Avviare revisione del Regolamento di Ambito.',
            'Dare continuità e qualificare il sistema di partecipazione locale.',
            'Sviluppare un approccio condiviso sui livelli di ingaggio Ambito/Azienda/ETS su bandi e coprogettazione.',
            'Individuare modello di Cartella Sociale Informatizzata e implementare banche dati.',
            'Aggiornare linee guida CT ADS.',
            'Rivedere bando e disciplinare accreditamento prestazioni sociali.',
            'Promuovere formalizzazione unità di offerta sperimentali e coordinamento Servizi al Lavoro.',
          ],
          risorse: [
            'Cartella Sociale Informatizzata: circa 35.000 €/anno.',
            'Qualificazione partecipazione locale: circa 20.000 €.',
          ],
          partners: ['Ufficio di Piano', 'Comuni', 'ETS', 'ASC Codebri'],
          continuita2021: true,
          nuovoServizio: false,
          coinvolgimentoASST: true,
          areePolicy: ['Interventi di sistema', 'Digitalizzazione dei servizi', 'Governance associata'],
        },
      ],
    },
    {
      id: 'giovani-minori-famiglie',
      titolo: 'Giovani, Minori, Famiglie',
      colore: '#6366f1',
      icona: '👨‍👩‍👧',
      schede: [
        {
          id: 'presa-in-carico-integrata',
          titolo: 'Sistema educativo innovativo',
          obiettivo_cluster: 'Sistema',
          obiettivo: 'Promuovere e realizzare un sistema educativo innovativo orientato alla valorizzazione delle competenze di famiglie e minorenni e alla promozione del benessere, con coinvolgimento della comunità educante e delle parti tecniche pubbliche.',
          target: 'Minori e famiglie dell\'Ambito.',
          target_cluster: ['Minori'],
          focus_disabilita: false,
          azioni: [
            'Coinvolgere portatori di interesse e integrare risorse per risposte più efficaci.',
            'Valorizzare competenze delle famiglie come attori della co-progettazione.',
            'Sperimentare pratiche educative innovative e ricomporre interventi/progetti (es. Centro Famiglia OIKOS).',
            'Attivare spazi di confronto pubblico-ETS per intercettare bandi e risorse contro povertà educativa.',
            'Sviluppare capacità di lettura/azione transculturale operatori sociali.',
            'Dare continuità e qualificare Tavolo Minori e Famiglia.',
          ],
          risorse: ['Da valutare in pianificazione annuale.', 'Comuni, ETS, Scuole, Ufficio di Piano.'],
          partners: ['Comuni', 'ETS', 'Scuole', 'Ufficio di Piano', 'ASST', 'ATS'],
          continuita2021: false,
          nuovoServizio: true,
          coinvolgimentoASST: true,
          areePolicy: ['Povertà educativa', 'Prevenzione dispersione scolastica', 'Famiglia e minori'],
        },
      ],
    },
    {
      id: 'non-autosufficienza',
      titolo: 'Non autosufficienza',
      colore: '#a855f7',
      icona: '👵',
      schede: [
        {
          id: 'invecchiamento-attivo',
          titolo: 'Reti territoriali per invecchiamento attivo',
          obiettivo_cluster: 'Inclusione',
          obiettivo: 'Promuovere interventi che favoriscano l\'invecchiamento attivo attraverso potenziamento reti sociali, integrazione iniziative territoriali e partecipazione degli anziani alla vita comunitaria.',
          target: 'Persone anziane autosufficienti o parzialmente autosufficienti.',
          target_cluster: ['Anziani'],
          focus_disabilita: false,
          azioni: [
            'Mappare risorse territoriali e iniziative esistenti (associazioni, centri, volontariato, spazi culturali).',
            'Mantenere Tavolo Anziani di Ambito come luogo di confronto e raccordo.',
            'Sperimentare attività inclusive: formazione digitale, attività fisiche adattate, laboratori artistici, socializzazione intergenerazionale.',
            'Attivare comunicazione/sensibilizzazione sulla cultura dell\'invecchiamento attivo.',
            'Definire annualmente azioni e indicatori di processo.',
          ],
          risorse: ['Quota FNPS 2025 e annualità successive.', 'Ufficio di Piano, ETS, Comuni, ASC Codebri.'],
          partners: ['Ufficio di Piano', 'ETS', 'Comuni', 'ASC Codebri', 'ASST'],
          continuita2021: true,
          nuovoServizio: false,
          coinvolgimentoASST: true,
          areePolicy: ['Long term care', 'Autonomia e domiciliarità', 'Digitalizzazione servizi', 'Contrasto isolamento'],
        },
      ],
    },
    {
      id: 'vita-indipendente',
      titolo: 'Vita indipendente',
      colore: '#ec4899',
      icona: '♿',
      schede: [
        {
          id: 'vita-autonoma-indipendente',
          titolo: 'Progetto di vita indipendente della persona con disabilità',
          obiettivo_cluster: 'Inclusione',
          obiettivo: 'Sviluppare azioni e progetti per la promozione della vita indipendente della persona con disabilità nelle diverse fasi di vita, rafforzando raccordo tra enti, comunità e rete territoriale.',
          target: 'Persone con disabilità su target di età differenziati e loro famiglie.',
          target_cluster: ['Adulti'],
          focus_disabilita: true,
          azioni: [
            'Promuovere raccordo dei punti di accesso e collegamento tra sportelli (es. Tiki Ascolta, Centri per la famiglia, CVI).',
            'Attivare azioni per adolescenti con disabilità nel passaggio scuola-lavoro (in integrazione con progetti GIANT).',
            'Sviluppare azioni occupazionali e di tempo libero per disabilità acquisita, includendo il tema mobilità.',
            'Sviluppare progetti per disabilità grave su tempo libero, sollievo e avvicinamento alla residenzialità.',
            'Accompagnare famiglie verso progetti di residenzialità anche con patrimonio privato.',
            'Avviare raccordo sanitario-sociale su bisogni cognitivi/comportamentali (DAMA, Case della comunità, servizi ASST).',
            'Dare continuità e qualificare il Tavolo Disabilità di Ambito.',
          ],
          risorse: ['Fondi Dopo di Noi', 'Fondi di Ambito e fondi da bandi', 'Ufficio di Piano, Comuni, ETS'],
          partners: ['Ufficio di Piano', 'Comuni', 'ETS', 'Rete Tiki Taka', 'ASST'],
          continuita2021: false,
          nuovoServizio: true,
          coinvolgimentoASST: true,
          areePolicy: ['Disabilità', 'Lavoro', 'Abitare', 'Digitalizzazione servizi', 'Inclusione di comunità'],
        },
      ],
    },
  ],
};

// ─── PLACEHOLDER ALTRI AMBITI ─────────────────────────────────────────────────

export const pianiDiZona: Record<string, PianoZona | null> = {
  Vimercate: pianoZonaVimercate,
  Monza: pianoZonaMonza,
  Carate: null,
  Desio: pianoZonaDesio,
  Seregno: pianoZonaSeregno,
};
